import { computed, effect, inject, Injectable, Injector, isDevMode, Signal, signal, WritableSignal } from '@angular/core';
import { Feature, Map, MapBrowserEvent, View } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { LineString, MultiLineString, Point } from 'ol/geom';
import * as olExtent from 'ol/extent';
import * as olEvents from 'ol/events';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Icon, RegularShape, Stroke, Style, Text, } from 'ol/style';
import { Circle as OlCircle } from 'ol/geom';
import { forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { map, take, takeWhile } from 'rxjs/operators';
import { TraceMetadataType, TraceRouteData, TraceRouteType, TraceTTSettingsType, TraceWayPointDrawIdType, TraceWayPointType } from '../models/trace-route-data';
import { MapService } from './map.service';
import { OlLayerService } from './ol/ol-layer.service';
import { unByKey } from 'ol/Observable';
import { ViewService } from './view.service';
import { PopupService } from './popup.service';
import { PopupData } from '../interfaces/popup-data';
import { TranslateService } from '@ngx-translate/core';
import { Layer } from 'ol/layer';
import { ToggleEventNameType, ToggleEventService } from './toggle-event.service';
import { Draw, Modify, Snap } from 'ol/interaction';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import hash from 'object-hash';
import { ModifyEvent } from 'ol/interaction/Modify';
import { DrawEvent } from 'ol/interaction/Draw';
import { METERS_PER_UNIT } from 'ol/proj/Units';
import { getPointResolution } from 'ol/proj';
import { easeIn } from 'ol/easing';
import { AlertService } from './alert.service';
import { Polyline } from 'ol/format';
import GeoJSON from 'ol/format/GeoJSON';
import { BrowserSnifferService } from './browser-sniffer.service';
import { Pixel } from 'ol/pixel';
import { ElevationService } from './elevation.service';
import { SegmentPoint } from '../models/segment-point';
import { ElevationPoiType } from '../models/elevation-poi';
import { ProfilePathService } from './profile-path.service';
import { TT_LIB_ENV_CONFIG } from '@tt-lib/tt-lib-env.config.token';
import { AppEnvironmentConfig } from '@tt-lib/interfaces/app-environment-config';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TraceXsmallDialogImportComponent } from '../components/trace/trace-xsmall-dialog-import/trace-xsmall-dialog-import.component';
import { TraceXsmallDialogViewComponent } from '../components/trace/trace-xsmall-dialog-view/trace-xsmall-dialog-view.component';
import { TraceRouteHistory, TraceRouteHistoryItemType } from '../models/trace-route-history';
import { PermalinkService } from './permalink.service';
import { OlTooltip } from '../models/ol-tooltip';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { TraceCreateComponent } from '../components/trace/trace-create/trace-create.component';
import { DrawDisclaimerDialogComponent } from '../components/trace/trace-create/draw-disclaimer-dialog/draw-disclaimer-dialog.component';
import { TraceCreateConfirmationDialogComponent } from '../components/trace/trace-create/trace-create-confirmation-dialog/trace-create-confirmation-dialog.component';
import { CsrfService } from './csrf.service';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import lineSliceAlong from '@turf/line-slice-along';
import turfLength from "@turf/length";
import { Feature as GeoJsonFeature, LineString as GeoJsonLineString, Geometry as GeoJsonGeometry, Geometry } from 'geojson';
import { BackEventService } from './back-event.service';
import { BottomPaneRef } from 'projects/tfv/src/app/shared/ui/bottom-pane/bottom-pane.ref';
import { BottomPaneService } from 'projects/tfv/src/app/shared/services/bottom-pane.service';
import { TraceXsmallDialogSuggestionsComponent } from '@tt-lib/components/trace/trace-xsmall-dialog-suggestions/trace-xsmall-dialog-suggestions.component';
import { MarkerService } from './marker.service';
import { TraceSuggestionsComponent } from '@tt-lib/components/trace/trace-suggestions/trace-suggestions.component';
import { LayerService } from './layers/layer.service';
import { TraceSuggestionsConfigService } from './trace-suggestions-config.service';

//SUGGESTIONS_ROUTE,IMPORT_ROUTE,VIEW_ROUTE,CREATE_ROUTE should keep the same index in this enum as the tab index of the mat-tab-group widget
export enum TraceDrawerType {
  //---------
  //enum values that correspond with tab index
  //---------
  SUGGESTIONS_ROUTE,
  IMPORT_ROUTE,
  VIEW_ROUTE,
  CREATE_ROUTE,
  //--------
  //enum values that are not linked to tab index
  //---------
  CLOSE_AND_VIEW_ROUTE
};
export enum TraceRouteDataUpdateType {
  //all values in lowercase
  UNDO = 'undo',
  REDO = 'redo',
  NEW_DATA = 'new_data',
  //when old data on the map is being replaced by new data on the map
  //this happens just before the NEW_DATA event, this also means there was no REMOVED_DATA/REMOVED_DATA_MAP event before
  REPLACING_DATA_MAP = 'replacing_data',
  //remove all data, including metadata
  REMOVED_DATA = 'removed_data',
  //remove all data, excluding metadata
  REMOVED_DATA_MAP = 'removed_data_map',
  //POI (Point Of Interest) is a wayPoint
  ADDED_POI = 'added_poi',
  ADDED_ROUTEPOINT = 'added_routepoint',
  MOVED_POI = 'moved_poi',
  MOVED_ROUTE = 'moved_route',
  REMOVED_POI = 'removed_poi',
  //when removing the endpoint (with or without autorouting active) or an intermediate point (without autorouting active)
  REMOVED_ROUTEPOINT = 'removed_routepoint_end',
  REMOVED_METADATA_INFO = 'removed_metadata_info',
  UPDATED_METADATA_INFO = 'updated_metadata_info',
  UPDATED_POI_INFO = 'updated_poi_info',
  //auto routing, temp types are not added to undo list
  ADDED_ROUTEPOINT_TEMP = 'added_routepoint_temp',
  //when removing an intermediate point with autorouting active
  REMOVED_ROUTEPOINT_TEMP = 'removed_routepoint',
  MOVED_ROUTE_TEMP = 'moved_route_temp',
  MOVED_ROUTE_AFTER_REMOVAL_TEMP = 'moved_route_after_removal_temp',
  //auto routing error, on error previously ignored values ADDED_ROUTEPOINT_TEMP or MOVED_ROUTE_TEMP will be added as ADDED_ROUTEPOINT or MOVED_ROUTE in the undo list; the data will contain the straight line(s) without routing
  MOVED_ROUTE_ERROR = 'moved_route_error',
  //special type, used to broadcast a switch between tabs (in other words, a switch between view mode and editing mode)
  COPY_TO_OTHER_TAB = 'copy_to_other_tab',
  //-----------------------------------
  //types that have no influence on the route itself
  //-----------------------------------
  //used to let the components know that the upward/downward distance has changed
  UPDATED_ELEV_UPDOWN_DIST = 'updated_elev_updown_dist'
};
export enum TraceClickActionType {
  NONE = 'clkNONE',
  CENTER = 'clkCENTER',
  ZOOMIN = 'clkZOOMIN',
  ZOOMOUT = 'clkZOOMOUT'
};
export type ClickedPoint = {
  ref: string;
  isPoi: boolean;
  isNew: boolean;
};
type ElevationProfilePointOnMap = {
  point: SegmentPoint;
  isMapFeature: boolean;
};
type TraceDummyMapInteractionEvent = {
  type: string;
  pixel: Pixel;
};
export type ElevationProfileDistanceBetweenPoints = 5 | 10 | 20;
@Injectable({
  providedIn: 'root',
})
export class TraceService {
  //true if the model is at least initialized and the vector layers are added (after calling this.shutDown(), this method will return false)
  private _isActive: WritableSignal<boolean> = signal(false);
  public isActive = this._isActive.asReadonly;

  private _traceRouteHistory: TraceRouteHistory;
  private _updatedTraceRouteHistory = new Subject<any>();
  public updatedTraceRouteHistory$ = this._updatedTraceRouteHistory as Observable<any>;

  private _traceRouteData: TraceRouteData;
  private _lastSavedObjectHash: string;
  private _updatedTraceRouteData = new Subject<TraceRouteDataUpdateType>();
  public updatedTraceRouteData$ = this._updatedTraceRouteData as Observable<TraceRouteDataUpdateType>;

  private _traceDrawLayerLineString: VectorLayer<any>;//route layer
  private _traceDrawLayerArrows: VectorLayer<any>;//arrows layer
  private _traceDrawLayerHeading: VectorLayer<any>;//heading layer
  private _traceDrawLayerPoint: VectorLayer<any>;//poi layer (a.k.a. waypoint layer)
  private _traceDrawLayerSuggestions: VectorLayer<any>;//suggestions layer
  private _traceDrawLayerHighlight: VectorLayer<any>;
  private _traceDrawLayerModifiable: VectorLayer<any>;
  private _traceDrawLayerModifiableHighlight: VectorLayer<any>;
  private _fadeInLayerTimers: {};

  //a listener per active map
  private _mapInteractionListener: { [key: string]: olEvents.EventsKey[] };

  private _inEditingMode: WritableSignal<boolean> = signal(false);
  public isInEditingMode: Signal<boolean> = computed(() => this._isActive() && this._inEditingMode());
  public isInViewMode: Signal<boolean> = computed(() => {
    return this._isActive() &&
      !this._inEditingMode() &&
      (this._hasWayPointsOnMap() || this._hasRouteOnMap() || this._hasTraceRouteWithGeneralInfo())
  }
  );

  private _hasTraceRouteWithGeneralInfo: WritableSignal<boolean> = signal(false);
  public hasTraceRouteWithGeneralInfo: Signal<boolean> = computed(() => this._isActive() && this._hasTraceRouteWithGeneralInfo());
  private _hasWayPointsOnMap: WritableSignal<boolean> = signal(false);
  public hasWayPointsOnMap: Signal<boolean> = computed(() => this._isActive() && this._hasWayPointsOnMap());
  private _hasRouteOnMap: WritableSignal<boolean> = signal(false);
  public hasRouteOnMap: Signal<boolean> = computed(() => this._isActive() && this._hasRouteOnMap());
  private _hasRouteToCreateProfile: WritableSignal<boolean> = signal(false);
  public hasRouteToCreateProfile: Signal<boolean> = computed(() => this._isActive() && this._hasRouteToCreateProfile());
  public hasTraceRouteOrWaypointOnMap: Signal<boolean> = computed(() => this._hasWayPointsOnMap() || this._hasRouteOnMap());

  private _editingModePaused: WritableSignal<boolean> = signal(false);
  public isEditingModePaused: Signal<boolean> = computed(() => this._inEditingMode() && this._editingModePaused());

  private _editingModeType: WritableSignal<'Point' | 'LineString'> = signal('LineString');;
  public getEditingModeType: Signal<'Point' | 'LineString'> = computed(() => this._editingModeType());

  private _editingTooltip: OlTooltip;

  //this value will be updated by TraceCreateUndoService
  public canUndoOrRedoEditing: boolean;

  private _handlingClickEvent: boolean;

  private _drawInterAction: Draw;
  private _snapInterAction: Snap;
  private _modifyInterAction: Modify;
  private _drawModifyToolsPaused: boolean;
  private _isDragging: boolean;
  private _dragTimer: number;
  private _draggedRouteFromCoordinate: Coordinate;
  private _draggedWayPointIndex: number;
  private _draggedRoutePointIndex: number;
  private _draggedRoutePointAddition: boolean;

  //drawings(route & poi's) can only be added inside this LB08 extent(=crossborder extent)
  //smaller extent? this._viewService.getProjection().getExtent()
  public readonly extentRestrictionLB08: olExtent.Extent = [100, 250000, 1300000, 1060000];

  private readonly _elevationSearchPixelTolerance: number = 30;
  //set in constructor, this will inherit the value from MapService.searchPixelTolerance
  private readonly _snapAndSearchPixelTolerance: number;

  public autoRouting: WritableSignal<boolean> = signal(true);
  private _autoRoutingBusy: boolean;
  private _autoRoutingIndexAfterModify: number;
  private _isAutoRoutingAfterDragging: boolean;
  private readonly _url_osrm_nearest;
  private readonly _url_osrm_route;
  private _url_osrm_route_maxdetail = true;
  private _fetchAutoRoutingDataTimestamp: number = 0;
  private _cancellingFetchAutoRoutingDataSubject: Subject<void> = new Subject<void>();
  private _autoCancellingFetchAutoRoutingTimer: number = -1;

  public highlightedWayPoint: WritableSignal<number | 'startPoint' | 'endPoint'> = signal(-1);
  public highlightedWayPointUpForEditing: WritableSignal<'edit' | 'delete'> = signal(null);
  public clickActionWayPoint: WritableSignal<TraceClickActionType> = signal(TraceClickActionType.NONE);
  public highlightedRoutePoint: WritableSignal<string> = signal('');
  public highlightedRoutePointUpForDeletion: WritableSignal<boolean> = signal(false);
  public clickActionRoutePoint: WritableSignal<TraceClickActionType> = signal(TraceClickActionType.NONE);
  private _clickedPoint = new Subject<ClickedPoint>();
  public clickedPoint$ = this._clickedPoint as Observable<ClickedPoint>;
  public highlightedSuggestionRouteId: WritableSignal<string> = signal('');

  private readonly _zoomLevelDirectionMarkers: number = 7;
  private readonly _zoomLevelRoutePointMarkers: number = 6;
  private readonly _maxAutoZoomDefault: number = 10;
  //this can be updated by an app via setter maxAutoZoom
  //max value is _maxAutoZoomDefault
  //if projection is webmercator, value 7 will be automtically added
  private _maxAutoZoom: number = this._maxAutoZoomDefault;

  //automatically update elevation data during drawing, while elevation panel is closed
  //value -1 will be updated in constructor to false in dev mode, to true in prod mode
  //if you don't want that behaviour, put a boolean value here (false if you never want an automatic update, true if you always want it)
  private readonly _elevationUpdateWhenPanelClosed: -1 | boolean = true;
  private _elevationDistanceBetweenPoints: ElevationProfileDistanceBetweenPoints;
  private _elevationProfilePoints: { [key: number]: SegmentPoint; };
  private _elevationProfilePointOnMap: ElevationProfilePointOnMap;
  private _syncElevationProfileTimestamp: number;
  private _elevationUpwardDist: number;
  private _elevationDownwardDist: number;
  private _delegatedElevationProfileRenewal = new Subject();
  public delegatedElevationProfileRenewal$ = this._delegatedElevationProfileRenewal as Observable<any>;

  private _watchingRouteWithoutDrawer: WritableSignal<boolean> = signal(false);
  public isWatchingRouteWithoutDrawer: Signal<boolean> = computed(() => this._isActive() && this._watchingRouteWithoutDrawer());

  //if suggestions tab will not be available, TraceDrawerType.IMPORT_ROUTE will become the default
  private _defaultTabIndex = TraceDrawerType.SUGGESTIONS_ROUTE;
  public tabindex: WritableSignal<TraceDrawerType> = signal(this._defaultTabIndex);

  private _traceDrawLayerLineElement: HTMLCanvasElement;

  //trace.component will update this once it's ready
  //this is used when a permalink is openend
  private _canOpenTraceTabs: boolean;
  private _postponedOpenTraceTabEvent: TraceDrawerType;

  //the update of the url parameter is often too slow to be useful for all checks, so we use this property to hold the real current value (the url value always follows with some delay)
  private _activePermalinkParam: string;

  private _appEnvironmentConfig: AppEnvironmentConfig = inject(TT_LIB_ENV_CONFIG);

  constructor(
    private _mapService: MapService,
    private _viewService: ViewService,
    private _olLayerService: OlLayerService,
    private _layerService: LayerService,
    private _popupService: PopupService,
    private _toggleEventService: ToggleEventService,
    private _httpService: HttpClient,
    private _translateService: TranslateService,
    private _alertService: AlertService,
    private _browserSniffer: BrowserSnifferService,
    private _elevationService: ElevationService,
    private _profilePathService: ProfilePathService,
    private _dialog: MatDialog,
    private _permaLinkService: PermalinkService,
    private _bottomSheet: MatBottomSheet,
    private _injector: Injector,
    private _recaptchaV3Service: ReCaptchaV3Service,
    private _backEventService: BackEventService,
    private _bottomPaneService: BottomPaneService,
    private _traceSuggestionsConfigService: TraceSuggestionsConfigService
  ) {

    this._snapAndSearchPixelTolerance = this._mapService.searchPixelTolerance;

    if (!this._appEnvironmentConfig.TRACE_SUGGESTIONS_CONFIG) {
      //even if the suggestions tab is shown, when there's no config path, the tab will be empty and useless
      this._defaultTabIndex = TraceDrawerType.IMPORT_ROUTE;
      this.tabindex.set(this._defaultTabIndex);
    }

    this._url_osrm_nearest = this._appEnvironmentConfig.OSRM_BASEURL + '/nearest' + this._appEnvironmentConfig.OSRM_URL_SUFFIX;
    this._url_osrm_route = this._appEnvironmentConfig.OSRM_BASEURL + '/route' + this._appEnvironmentConfig.OSRM_URL_SUFFIX;
    this._mapInteractionListener = {};
    this._fadeInLayerTimers = null;
    this._editingTooltip = null;
    this._modifyInterAction = null;
    this._drawInterAction = null;
    this._snapInterAction = null;
    this._drawModifyToolsPaused = false;
    this._isDragging = false;
    this._draggedWayPointIndex = -1;
    this._draggedRoutePointIndex = -1;
    this._draggedRoutePointAddition = false;
    this._autoRoutingBusy = false;
    this._syncElevationProfileTimestamp = 0
    this._elevationProfilePoints = {};
    this._elevationProfilePointOnMap = null;
    this._traceRouteData = TraceRouteData.getEmptyTraceRouteDataObject(null);
    this._postponedOpenTraceTabEvent = null;
    this._elevationUpdateWhenPanelClosed = this._elevationUpdateWhenPanelClosed === -1 ? !isDevMode() : this._elevationUpdateWhenPanelClosed;
    this._elevationUpwardDist = -1;
    this._elevationDownwardDist = 1;
    this._activePermalinkParam = null;
    this._traceRouteHistory = new TraceRouteHistory([]);

    //initial check
    this._canOpenTraceTabs = this._browserSniffer.isPhone();
    //update when necessary
    effect(() => {
      if (!this._canOpenTraceTabs && this._browserSniffer.isPhone()) {
        this._canOpenTraceTabs = true;
      }
      const suggestionsConfigDownloadStatus = this._traceSuggestionsConfigService.configDownloadStatus();
      if (suggestionsConfigDownloadStatus === 'done') {
        this._loadSuggestions()
      }
    })
  }

  startupWithTraceTab(param: string) {
    if (this._permaLinkService.getUrlParam('tracetab')) {
      //remove startup tracetab param, if necessary
      this._permaLinkService.updateTraceTabParam();
    }

    const requestedTab: TraceDrawerType = typeof param === 'string' ? TraceDrawerType[param.toUpperCase() as keyof typeof TraceDrawerType] : null;

    //IMPORT_ROUTE has value 0, so test on null
    if (requestedTab !== null) {
      if (this._browserSniffer.isPhone()) {
        switch (requestedTab) {
          case TraceDrawerType.IMPORT_ROUTE: {
            this.openImportDialog()
            break;
          }
          case TraceDrawerType.SUGGESTIONS_ROUTE: {
            /*
            this.loadSuggestions(true, true);
            */
            this._traceSuggestionsConfigService.downloadConfigData(true);
            break;
          }
          case TraceDrawerType.CREATE_ROUTE: {
            this.openCreateMobile(false);
            break;
          }
        }
      } else {
        switch (requestedTab) {
          case TraceDrawerType.SUGGESTIONS_ROUTE: {
            /*
            this.loadSuggestions(true, true);
            */
            this._traceSuggestionsConfigService.downloadConfigData(true);
            break;
          }
          default: {
            this.openTraceTab(requestedTab);
          }
        }
      }
    }
  }

  openTraceTab(traceDrawerType?: TraceDrawerType) {
    if (this._permaLinkService.getUrlParam('tracetab')) {
      //remove startup tracetab param, if necessary
      this._permaLinkService.updateTraceTabParam();
    }

    if (!this._canOpenTraceTabs) {
      //the trace component is not initialized yet
      this._postponedOpenTraceTabEvent = traceDrawerType
      return;
    }

    let sendDrawerCloseEvent = false;
    const wasActive = this._isActive();

    if (!wasActive) {
      this._startUp();
    }

    if (this._postponedOpenTraceTabEvent) {
      this._postponedOpenTraceTabEvent = null;
      this._informElevationProfileForDataUpdate();
    }

    if (traceDrawerType in TraceDrawerType) {
      if (traceDrawerType === TraceDrawerType.CLOSE_AND_VIEW_ROUTE) {
        sendDrawerCloseEvent = true;
        traceDrawerType = TraceDrawerType.VIEW_ROUTE;
      }

      if (traceDrawerType !== this.tabindex()) {
        this.tabindex.set(traceDrawerType);
      }
    } else {
      if (!wasActive) {
        // if (this.tabindex === TraceDrawerType.VIEW_ROUTE) {
        //   this.tabindex = TraceDrawerType.IMPORT_ROUTE;
        // }
        if (this.tabindex() !== this._defaultTabIndex) {
          this.tabindex.set(this._defaultTabIndex);
        }
      }
    }

    if (sendDrawerCloseEvent) {
      //if not closed yet, main trace component will close
      this._toggleEventService.toggleEvent({
        eventName: ToggleEventNameType.CLOSE_COMMAND,
        forwardedFor: 'trace',
      });
    } else {
      // if not open yet, main trace component will open, add timeout 0 at startup view route import stayed visible until animation end
      setTimeout(() => {
        this._toggleEventService.toggleEvent({
          eventName: ToggleEventNameType.OPEN_COMMAND,
          forwardedFor: 'trace',
        });
      }, 0);
    }
  }
  openTraceTab_str(traceDrawerTypeAsString?: keyof typeof TraceDrawerType) {
    this.openTraceTab(traceDrawerTypeAsString ? TraceDrawerType[traceDrawerTypeAsString] : null);
  }

  setCanOpenTraceTabs() {
    this._canOpenTraceTabs = true;
    window.setTimeout(() => {
      if (this._postponedOpenTraceTabEvent !== null) {
        this.openTraceTab(this._postponedOpenTraceTabEvent);
      }
    }, 1000);
  }

  private _closeTraceDrawer() {
    //if not closed yet, main trace component will send event ToggleEventNameType.CLOSE
    this._toggleEventService.toggleEvent({
      eventName: ToggleEventNameType.CLOSE_COMMAND,
      forwardedFor: 'trace',
    });
  }

  private _startUp() {
    if (this._isActive()) {
      return;
    }

    console.log("Traceservice startup...");

    this._isActive.set(true);
    if (!this._olLayerService.getLayerById('traceDrawLayerLineString')) {
      //zIndex just below drawLayer from measure tool
      this._traceDrawLayerLineString = new VectorLayer({
        zIndex: 97,
        source: new VectorSource(),
        updateWhileAnimating: false,
        updateWhileInteracting: false,
        //
        className: 'ol-layer traceDrawLayerLine'
      });
      this._traceDrawLayerLineString.set('customId', 'traceDrawLayerLineString');
      this._olLayerService.addOlLayerToMap(this._traceDrawLayerLineString);

      this._traceDrawLayerLineString.once('postrender', (evt) => {
        // this._traceDrawLayerLineElement = document.querySelector('.traceDrawLayerLine');
        this._traceDrawLayerLineElement = evt.context.canvas as HTMLCanvasElement;
        this._traceDrawLayerLineString.setStyle((feature: Feature, resolution: number) => {
          return this._getTraceStyle(feature, resolution, false);
        });

      });
      //quick test for blending mode
      // this._traceDrawLayerLineString.on('prerender', (evt) => {
      //   if (evt.context instanceof CanvasRenderingContext2D) {
      //     evt.context.canvas.getContext('2d').globalCompositeOperation = "multiply";
      //   }
      // });
      // this._traceDrawLayerLineString.on('postrender', (evt) => {
      //   if (evt.context instanceof CanvasRenderingContext2D) {
      //     evt.context.canvas.getContext('2d').globalCompositeOperation = "multiply";
      //   }
      // });
    }

    if (!this._olLayerService.getLayerById('traceDrawLayerPoint')) {
      //zIndex just below drawLayer from measure tool
      this._traceDrawLayerPoint = new VectorLayer({
        zIndex: 98,
        source: new VectorSource(),
        updateWhileAnimating: false,
        updateWhileInteracting: false,
      });
      this._traceDrawLayerPoint.set('customId', 'traceDrawLayerPoint');
      this._traceDrawLayerPoint.setStyle((feature: Feature, resolution: number) => {
        return this._getTraceStyle(feature, resolution, false);
      });
      this._olLayerService.addOlLayerToMap(this._traceDrawLayerPoint);
    }

    if (!this._olLayerService.getLayerById('traceDrawLayerArrows')) {
      //zIndex just below drawLayer from measure tool
      this._traceDrawLayerArrows = new VectorLayer({
        zIndex: 97,
        source: new VectorSource(),
        updateWhileAnimating: false,
        updateWhileInteracting: false,
      });
      this._traceDrawLayerArrows.set('customId', 'traceDrawLayerArrows');
      this._traceDrawLayerArrows.setStyle((feature: Feature, resolution: number) => {
        return this._getTraceStyle(feature, resolution, false, true);
      });
      this._olLayerService.addOlLayerToMap(this._traceDrawLayerArrows);
    }

    if (!this._olLayerService.getLayerById('traceDrawLayerModifiable')) {
      //this layer will be invisible and will contain a copy of the data from _traceDrawLayerLineString or _traceDrawLayerPoint during editing mode
      this._traceDrawLayerModifiable = new VectorLayer({
        zIndex: 99,
        source: new VectorSource(),
        updateWhileAnimating: false,
        updateWhileInteracting: false,
        opacity: 0
      });
      this._traceDrawLayerModifiable.set('customId', 'traceDrawLayerModifiable');
      this._traceDrawLayerModifiable.setStyle(null);
      this._olLayerService.addOlLayerToMap(this._traceDrawLayerModifiable);
    }

    if (!this._olLayerService.getLayerById('traceDrawLayerModifiableHighlight')) {
      this._traceDrawLayerModifiableHighlight = new VectorLayer({
        zIndex: 99,
        source: new VectorSource(),
        updateWhileAnimating: false,
        updateWhileInteracting: false,
      });
      this._traceDrawLayerModifiableHighlight.set('customId', 'traceDrawLayerModifiableHighlight');
      this._traceDrawLayerModifiableHighlight.setStyle(() => { return this._getTraceModifiableStyle(); });
      this._olLayerService.addOlLayerToMap(this._traceDrawLayerModifiableHighlight);
    }

    if (!this._olLayerService.getLayerById('traceDrawLayerHighlight')) {
      this._traceDrawLayerHighlight = new VectorLayer({
        zIndex: 100,
        source: new VectorSource(),
        updateWhileAnimating: false,
        updateWhileInteracting: false,
      });
      this._traceDrawLayerHighlight.set('customId', 'traceDrawLayerHighlight');
      this._traceDrawLayerHighlight.setStyle(
        (feature: Feature, resolution: number) => {
          return this._getTraceStyle(feature, resolution, true);
        }
      );
      this._olLayerService.addOlLayerToMap(this._traceDrawLayerHighlight);
    }

    if (!this._olLayerService.getLayerById('traceDrawLayerHeading')) {
      //layer traceDrawLayerPoint has zindex 98, that layer is also used to mark the link with the elevation profile
      //but if we put this layer under it, then this label is often hidden by the blue dot
      this._traceDrawLayerHeading = new VectorLayer({
        zIndex: 101,
        source: new VectorSource(),
        updateWhileAnimating: false,
        updateWhileInteracting: false,
      });
      this._traceDrawLayerHeading.set('customId', 'traceDrawLayerHeading');
      this._traceDrawLayerHeading.setStyle((feature: Feature, resolution: number) => {
        return this._getTraceStyle(feature, resolution, false, false);
      });
      this._olLayerService.addOlLayerToMap(this._traceDrawLayerHeading);
    }

    if (!this._olLayerService.getLayerById('traceDrawLayerSuggestions')) {
      this._traceDrawLayerSuggestions = new VectorLayer({
        zIndex: 102,
        source: new VectorSource(),
        updateWhileAnimating: false,
        updateWhileInteracting: false
      });
      this._traceDrawLayerSuggestions.set('customId', 'traceDrawLayerSuggestions');
      this._traceDrawLayerSuggestions.setStyle((feature: Feature, resolution: number) => {
        return this._getTraceStyle(feature, resolution, false);
      });
      this._olLayerService.addOlLayerToMap(this._traceDrawLayerSuggestions);
    }

    //only for main map at this moment
    //splitview map can be added afterwards
    this.addMapBrowserEventListeners();

    this.resetTraceRouteData(false);

    this.loadTraceRouteHistoryFromLocalStorage(true);

    this._copyCurrentPermalinkParamToModule();

    window.setTimeout(() => {
      this._initThumbnailMap();
    }, 0);

    this._toggleEventService.toggleEvent({
      eventName: ToggleEventNameType.ACTIVE,
      forwardedFor: 'trace',
    });
  }

  /**
   *
   * removes the data from the model, closes any trace panel and removes vector layers
   */
  shutDown(/*isManualShutDown: boolean*/) {
    if (!this._isActive()) {
      return;
    }

    console.log("Traceservice shutDown...");

    //this.setInEditingMode will also set this._copyingDataToEditingMode to false
    this.setInEditingMode(false);

    this._closeTraceDrawer();

    //this also calls this.setPermalinkParam(false)
    //this.resetTraceRouteData(isManualShutDown);
    this.resetTraceRouteData(true);

    this._traceRouteHistory = new TraceRouteHistory([]);

    //if (!isManualShutDown) {
    this._disposeThumbnailMap();
    //}

    for (let key in this._mapInteractionListener) {
      this.removeMapBrowserEventListeners(key);
    }

    this._forceClearAllFadeInLayers();

    if (this._olLayerService.getLayerById('traceDrawLayerLineString')) {
      this._olLayerService.removeOlLayerFromMap(this._traceDrawLayerLineString);
      this._traceDrawLayerLineString.getSource().clear();
    }

    if (this._olLayerService.getLayerById('traceDrawLayerArrows')) {
      this._olLayerService.removeOlLayerFromMap(this._traceDrawLayerArrows);
      this._traceDrawLayerArrows.getSource().clear();
    }

    if (this._olLayerService.getLayerById('traceDrawLayerPoint')) {
      this._olLayerService.removeOlLayerFromMap(this._traceDrawLayerPoint);
      this._traceDrawLayerPoint.getSource().clear();
    }

    if (this._olLayerService.getLayerById('traceDrawLayerModifiable')) {
      this._olLayerService.removeOlLayerFromMap(this._traceDrawLayerModifiable);
      this._traceDrawLayerModifiable.getSource().clear();
    }

    if (this._olLayerService.getLayerById('traceDrawLayerModifiableHighlight')) {
      this._olLayerService.removeOlLayerFromMap(this._traceDrawLayerModifiableHighlight);
      this._traceDrawLayerModifiableHighlight.getSource().clear();
    }

    if (this._olLayerService.getLayerById('traceDrawLayerHighlight')) {
      this._olLayerService.removeOlLayerFromMap(this._traceDrawLayerHighlight);
      this._traceDrawLayerHighlight.getSource().clear();
    }

    if (this._olLayerService.getLayerById('traceDrawLayerHeading')) {
      this._olLayerService.removeOlLayerFromMap(this._traceDrawLayerHeading);
      this._traceDrawLayerHeading.getSource().clear();
    }

    if (this._olLayerService.getLayerById('traceDrawLayerSuggestions')) {
      this._olLayerService.removeOlLayerFromMap(this._traceDrawLayerSuggestions);
      this._traceDrawLayerSuggestions.getSource().clear();
    }

    if (this.isElevationProfileComponentOpen()) {
      this._toggleEventService.toggleEvent({
        eventName: ToggleEventNameType.CLOSE_COMMAND,
        forwardedFor: 'elevation-profile',
      });
    }

    this._isActive.set(false);
    this.tabindex.set(this._defaultTabIndex)

    this._toggleEventService.toggleEvent({
      eventName: ToggleEventNameType.INACTIVE,
      forwardedFor: 'trace',
    });
  }

  /**
   * when active and no data on the map (routes or suggestions), it will return true
   */
  canShutDownSafely() {
    return this._isActive()
      && !this.hasTraceRouteOrWaypointOnMap()
      && !this.hasTraceRouteWithGeneralInfo()
      //in case of preview the option can be false : this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked
      /*&& (this._traceDrawLayerSuggestions.getSource().getFeatures().length === 0 || !this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked)*/
      && this._traceDrawLayerSuggestions.getSource().getFeatures().length === 0
      //if someone opens one of these quickly again after closing one (without a route on the map), the service should not shut down
      && !this._suggestionsDialog
      && !this._importDialog
      && !this._viewDialog
  }

  setInEditingMode(inEditingModeUpdate: boolean) {
    if (this._inEditingMode() === inEditingModeUpdate) {
      return;
    }

    this._inEditingMode.set(this._isActive() && inEditingModeUpdate);

    this._mapService.contextMenuPaused = { paused: this._inEditingMode(), pausedBy: 'trace' };

    if (this._popupService.isPopupAlreadyOnMap()) {
      this._popupService.hideInCaseOfType('TraceInfo');
    }

    if (this._inEditingMode()) {
      this._addDrawModifySnapIntercation();

      const mostRecentHistoryItem = this._traceRouteHistory.getMostRecentHistoryItem();
      const permalinkParam = this.getPermalinkParam();

      if (!permalinkParam || !mostRecentHistoryItem || mostRecentHistoryItem.remoteSource || mostRecentHistoryItem.dataRef !== permalinkParam) {

        this.setPermalinkParam(false);
        this.fullCleanupAndSaveForHistoryItems();
        this.addNewHistoryItemForEditing();
        this.setPermalinkParam(true);

        const metadata = TraceRouteData.deepCopy(this.getTraceRouteData().metadata);
        if (this.getTraceRouteData().metadata.name) {
          metadata.name = this.createUniqueNameForNewHistoryItem(this.getTraceRouteData().metadata.name);
        }

        //no broadcast
        this.setTraceRouteData_metadata(metadata, false, false);

        this._updateMostRecentHistoryItem_upDownDist();
      }

      if (!this._editingTooltip) {
        const newDiv = document.createElement("div");
        newDiv.classList.add('trace-edittooltip');
        this._editingTooltip = new OlTooltip(this._mapService.getMap(), newDiv);
        this._editingTooltip.setPosition(undefined);
        this._editingTooltip.setOffset([25, 50]);
        this._editingTooltip.setPositioning('bottom-left');
        this._editingTooltip.setText("");
        this._editingTooltip.activate();
      }
    } else {
      this._editingModePaused.set(false);
      this._removeDrawModifySnapIntercation();

      if (this._traceDrawLayerModifiable) {
        this._traceDrawLayerModifiable.getSource().clear();
        this._traceDrawLayerModifiableHighlight.getSource().clear();
      }

      if (this._editingTooltip) {
        this._editingTooltip.deactivate();
        this._editingTooltip = null;
      }

      if (TraceRouteData.getTraceTTSetting(this._traceRouteData.ttSettings, "wayPointSequenceNumber") === 'alpha') {
        this._traceDrawLayerPoint.changed();
      }

      this._cancelAutoRoutingRequest();
    }

    //this will add or remove the routePoints on the route (the routePoints are not shown in view mode, only in editing mode)
    this._traceDrawLayerLineString.changed();

    //it's used to let the components view/create know which one is active and which one is inactive
    this._broadcastTraceRouteDataUpdate(TraceRouteDataUpdateType.COPY_TO_OTHER_TAB);
  }

  setEditingModePaused(editingModePausedUpdate: boolean) {
    this._editingModePaused.set(this._inEditingMode() && editingModePausedUpdate);

    if (this.isInEditingMode()) {
      //something goes wrong with the zIndex after using setActive()
      //modify should stay above draw
      //this._drawInterAction.setActive(!this._editingModePaused());
      //this._modifyIntercation.setActive(!this._editingModePaused());
      //dirty solution: remove/add again
      if (this._editingModePaused()) {
        this._removeDrawModifySnapIntercation();
      } else {
        this._addDrawModifySnapIntercation();
      }

      this._mapService.contextMenuPaused = { paused: !this._editingModePaused(), pausedBy: 'trace' };
    }
  }

  setEditingModeType(drawingRoute: boolean) {
    this._removeDrawModifySnapIntercation();
    this._editingModeType.set(drawingRoute ? 'LineString' : 'Point');
    this._copyFeaturesToTraceDrawLayerModifiable();
    this._addDrawModifySnapIntercation();
  }

  private _addDrawModifySnapIntercation() {
    if (this._drawInterAction === null) {
      this._drawInterAction = new Draw({
        source: this._editingModeType() === 'LineString' ? this._traceDrawLayerLineString.getSource() : this._traceDrawLayerPoint.getSource(),
        type: 'Point',
        stopClick: true,
        condition: (e: MapBrowserEvent<any>) => {
          if (e.originalEvent.buttons === 1) {
            //left click ok
            return true;
          } else {
            return false;
          }
        },
        style: feature => { return this._getDrawStyle() }
      });
      this._drawInterAction.set("isTraceInteraction", true);
      this._drawInterAction.getOverlay().setZIndex(this._traceDrawLayerHighlight.getZIndex() ? this._traceDrawLayerHighlight.getZIndex() + 1 : 100);
      this._mapService.getCurrentMap().addInteraction(this._drawInterAction);

      this._drawInterAction.on('drawend', (event) => {
        this._handleMapDrawInteractionEvent(event);
      });
    }

    if (this._modifyInterAction === null) {
      this._modifyInterAction = new Modify({
        source: this._traceDrawLayerModifiable.getSource(),
        pixelTolerance: this._snapAndSearchPixelTolerance,
        style: (features: Feature, resolution: number) => { return this._getModifyStyle(features.get('features')[0], resolution); }
      });
      this._modifyInterAction.set("isTraceInteraction", true);
      this._modifyInterAction.getOverlay().setZIndex(this._traceDrawLayerHighlight.getZIndex() ? this._traceDrawLayerHighlight.getZIndex() + 1 : 101);
      this._mapService.getCurrentMap().addInteraction(this._modifyInterAction);

      this._modifyInterAction.on('modifystart', (event) => {
        this._handleMapModifyInteractionEvent(event);
      });
      this._modifyInterAction.on('modifyend', (event) => {
        this._handleMapModifyInteractionEvent(event);
      });
    }

    //added last
    if (this._snapInterAction === null) {
      this._snapInterAction = new Snap({
        source: this._traceDrawLayerModifiable.getSource(),
        pixelTolerance: this._snapAndSearchPixelTolerance,
      });
      this._snapInterAction.set("isTraceInteraction", true);
      this._mapService.getCurrentMap().addInteraction(this._snapInterAction);
    }
  }

  private _removeDrawModifySnapIntercation() {
    if (this._snapInterAction !== null) {
      this._mapService.getCurrentMap().removeInteraction(this._snapInterAction);
      this._snapInterAction = null;
    }

    if (this._modifyInterAction !== null) {
      this._mapService.getCurrentMap().removeInteraction(this._modifyInterAction);
      this._modifyInterAction = null;
    }

    if (this._drawInterAction !== null) {
      this._mapService.getCurrentMap().removeInteraction(this._drawInterAction);
      this._drawInterAction = null;
    }
  }

  /**
   * 
   * @returns true if other modules added a draw or modify interaction to the map
   */
  checkForExternalMapEditingInteractions() {
    const allInteraction = this._mapService.getCurrentMap().getInteractions();
    let count = 0;
    allInteraction.forEach(oneInteraction => {
      if ((oneInteraction instanceof Draw || oneInteraction instanceof Modify) && !oneInteraction.get("isTraceInteraction")) {
        count++;
      }
    });
    return count > 0;
  }

  setWatchingRouteWithoutDrawer(watchingRouteWithoutDrawer: boolean) {
    this._watchingRouteWithoutDrawer.set(this._isActive() && watchingRouteWithoutDrawer); /*&& (this._inEditingMode() || this.isInViewMode())*/
  }

  /*
   * we don't use the general pointermove listener (setPointerCursorOnFeatureHoverForLayerId) because we need to same pixel tolerance which is used here for searching&snappping (this._snapAndSearchPixelTolerance)
   */
  addMapBrowserEventListeners(mapId?: string) {
    mapId = mapId ? mapId : 'main';
    const mapForEventListener: Map = this._mapService.getMap(mapId);

    if (mapForEventListener && !this._mapInteractionListener[mapId]) {
      this._mapInteractionListener[mapId] = mapForEventListener.on(['click', 'pointermove'], (e: MapBrowserEvent<any>) => {
        //click events that are handled by the drawend event & modifystart/modifyend event will never arrive here
        this._handleMapInteractionEvent(e, mapId);
      });

      //handle context menu? 
      //not passive, we want to prevent default
      //mapForEventListener.getViewport().addEventListener('contextmenu', (e) => { e.preventDefault(); this._contextMenuHandler(mapForEventListener.getEventPixel(e as UIEvent))}, false);
    }
  }

  removeMapBrowserEventListeners(mapId?: string) {
    mapId = mapId ? mapId : 'main';
    //const mapForEventListener: Map = this._mapService.getMap(mapId);

    if (this._mapInteractionListener[mapId]) {
      unByKey(this._mapInteractionListener[mapId]);
      delete this._mapInteractionListener[mapId];

      //handle context menu? 
      //not passive, we want to prevent default
      //mapForEventListener?.getViewport().removeEventListener('contextmenu', (e) => { e.preventDefault(); this._contextMenuHandler(mapForEventListener.getEventPixel(e as UIEvent))}, false);
    }
  }

  set maxAutoZoom(value: number) {
    if (value >= 0 && value <= this._maxAutoZoomDefault) {
      this._maxAutoZoom = value;
    } else {
      this._maxAutoZoom = this._maxAutoZoomDefault
    }
  }

  private get _maxAutoZoomForPrj() {
    return this._viewService.getView().getProjection().getCode() === "EPSG:3857" ? (this._maxAutoZoom + 7) : this._maxAutoZoom;
  }

  //private _contextMenuHandler(eventPixel: Pixel) { }


  /* ************************************************************************************************************************************************************************************
  * url param functions
  ************************************************************************************************************************************************************************************ */

  /*
   * because the update of the url parameter is sometimes to slow when switching tabs, we will always check the local copy and not the url parameter itself 
   * this method is only called at start up
   */
  private _copyCurrentPermalinkParamToModule() {
    this._permaLinkService.getParams().pipe(take(1)).subscribe((params) => {
      this._activePermalinkParam = this._permaLinkService.getUrlParam('trace') && this._permaLinkService.getUrlParam('trace').length > 0 ? this._permaLinkService.getUrlParam('trace') : null;
    });
  }

  hasPermalinkParam(): boolean {
    //return this._permaLinkService.getUrlParam('trace') && this._permaLinkService.getUrlParam('trace').length > 0;
    return typeof this._activePermalinkParam === 'string' && this._activePermalinkParam.length > 0;
  }

  hasPermalinkParam_serverToken(): boolean {
    //return this.hasPermalinkParam() && !TraceRouteHistory.isLocalStorageDataReference(this._permaLinkService.getUrlParam('trace'));
    return this.hasPermalinkParam() && !TraceRouteHistory.isLocalStorageDataReference(this._activePermalinkParam);
  }

  hasPermalinkParam_dataRef(): boolean {
    //return this.hasPermalinkParam() && TraceRouteHistory.isLocalStorageDataReference(this._permaLinkService.getUrlParam('trace'));
    return this.hasPermalinkParam() && TraceRouteHistory.isLocalStorageDataReference(this._activePermalinkParam);
  }

  getPermalinkParam(): string {
    //return this.hasPermalinkParam() ? this._permaLinkService.getUrlParam('trace') : null;
    return this._activePermalinkParam;
  }

  /*
  setPermalinkParamForShare(newPermalink: string) {
    if (typeof newPermalink === "string" && newPermalink.trim().length > 0) {
      this._traceRouteHistory.addNewHistoryItemOrMoveExistingToTop(true, newPermalink);
      this.setPermalinkParam(newPermalink);
    }
  }
  */

  /**
   * while editing this method is usually called by method saveTraceRouteDataToLocalStorage in class TraceCreateComponent; 
   * in view mode this method is called directly 
   * 
   * @param newPermalink if value true, the most recent history item's dataRef value will be put in the url parameter; 
   * if value is false/null/empty string, the parameter will be removed; 
   * if the value is a non empty string, that sting will become the parameter value
   */
  setPermalinkParam(newPermalink: string | boolean) {
    if (typeof newPermalink === "string") {
      if (newPermalink.trim().length > 0 && newPermalink === this._activePermalinkParam) {
        return;
      }
      this._activePermalinkParam = newPermalink.trim().length > 0 ? newPermalink : null;
      this._permaLinkService.updateTraceParam(this._activePermalinkParam);
    } else {
      let latestRoute = newPermalink ? this._traceRouteHistory.getMostRecentHistoryItem() : null;
      if (latestRoute && latestRoute.dataRef && latestRoute.dataRef.length > 0 && latestRoute.dataRef === this._activePermalinkParam) {
        return;
      }
      this._activePermalinkParam = latestRoute && latestRoute.dataRef && latestRoute.dataRef.length > 0 ? latestRoute.dataRef : null;
      this._permaLinkService.updateTraceParam(this._activePermalinkParam);
    }

    if (this.isActive() && this._traceSuggestionsConfigService.configDownloadStatus() === 'done') {
      this.hideSuggestionsPreview();
      //make the suggestion icon disappear/appears directly, without map interaction
      this._traceDrawLayerSuggestions.changed();
      //make sure the splitview map also does this
      this._toggleEventService.toggleEvent({ forwardedFor: 'trace-suggestion-preview', eventName: ToggleEventNameType.UPDATE });
    }
  }

  /* ************************************************************************************************************************************************************************************
   * TraceRouteData storage and load functions (local & remote)
   ************************************************************************************************************************************************************************************ */

  /**
   * this method will first update the history list, set the permalink, and afterwards the data;
   * if object has no data (general info(name and/or desc), waypoint, route) then the data object will be removed from storage
   * 
   * @returns
   */
  saveTraceRouteDataToLocalStorage(): boolean {
    let isSaved = false;
    if (this._isActive() && this.canSaveDataInLocalStorage()) {
      try {
        //at least route with startPoint?
        const hasRouteOrStartPointForThumbnail = this.hasRouteOnMap();
        const hasWayPointsForThumbnail = this.hasWayPointsOnMap();
        //first update most recent item from history list
        const historyItem = this._traceRouteHistory.getMostRecentHistoryItem();
        const lastHistorySave = historyItem && historyItem.time ? historyItem.time : 0;
        const newRouteLength = this.hasRouteToCreateProfile() ? this.getRouteLength() : 0;
        //const lastHistoryRouteLength = historyItem ? historyItem.dist : newRouteLength;

        isSaved = historyItem && TraceRouteHistory.isLocalStorageDataReference(historyItem.dataRef);
        isSaved = isSaved && this._traceRouteHistory.updateMostRecentHistoryItem_baseInfo(this._traceRouteData, newRouteLength);

        if (isSaved && !hasRouteOrStartPointForThumbnail && !hasWayPointsForThumbnail) {
          //isSaved = this._traceRouteHistory.updateMostRecentHistoryItem_thumb(null);
          if (!this._setEmptyThumbnail(historyItem.dataRef)) {
            //if we cannot set the dummy empty thumbnail, then we'll try to remove it completely
            isSaved = this._traceRouteHistory.updateMostRecentHistoryItem_thumb(null);
          }
        }
        isSaved = isSaved && this._saveTraceRouteHistoryToLocalStorage();

        if (isSaved) {
          //add permalink if not yet added to the url
          this.setPermalinkParam(historyItem.dataRef);
        } else {
          console.log('unable to update traceRouteData object in localStorage because we couldn\'t first update the traceRouteHistory object');
        }

        //thumbnail yes/no : last save longer than x millisecond(s) ago 
        //add test for change in distance? Math.abs(newRouteLength - lastHistoryRouteLength) > 10)
        if (isSaved && (hasRouteOrStartPointForThumbnail || hasWayPointsForThumbnail) && historyItem && (historyItem.remoteSource || !historyItem.thumb || lastHistorySave < (new Date().getTime() - 500))) {
          window.setTimeout(() => {
            this._setRouteThumbnail(historyItem.dataRef);
          }, 500);
        }

        //history list is updated, now save the TraceRouteData object
        //the list will be cleaned up automatically afterwards
        if (isSaved) {
          //we filter out nulls first
          localStorage.setItem(
            historyItem.dataRef,
            JSON.stringify(
              TraceRouteData.getFilteredTraceRouteDataObject(
                this._traceRouteData,
                true
              )
            )
          );
          //isSaved = typeof localStorage.getItem(historyItem.dataRef) === "string";
        }
      } catch (error) {
        isSaved = false;
        console.log('unable to update traceRouteHistory and traceRouteData object in localStorage :' + error);
      }
    }
    return isSaved;
  }

  /**
   * Transforms a JSON string into a Blob and sends the file as 'multipart/form-data' 
   * Note: data can only be saved if there is at least one waypoint OR at least one route
   * @returns Observable<string> returns a unique identifier to update the permalink ('trace' param)
   */
  saveTraceRouteDataToExternalStorage(): Observable<string> {
    if (this._isActive() && this.hasTraceRouteOrWaypointOnMap()) {
      let traceJsonStr: string = JSON.stringify(TraceRouteData.getFilteredTraceRouteDataObject(this._traceRouteData, true));

      // const httpOptions = {
      //   headers: new HttpHeaders({
      //     'Accept': 'application/json'
      //   })
      // };

      let formData: any = new FormData();
      formData.append('file', new Blob([traceJsonStr], { type: 'application/json' }));

      const csrfService = this._injector.get(CsrfService);
      const csrf$ = csrfService.fetchToken();
      const recaptcha$ = this._recaptchaV3Service.execute('sendAction');
      return forkJoin([csrf$, recaptcha$]).pipe(
        switchMap(([csrfToken, recaptcha]) => {
          let headers = new HttpHeaders();
          if (csrfToken) headers = headers.set('CSRF-Token', csrfToken);
          if (recaptcha) headers = headers.set('x-csrf-token', recaptcha);
          return this._httpService
            .post(
              this._appEnvironmentConfig.BASEURL + '/tmv-services/routes/save',
              formData,
              { headers }
            ).pipe(map((res: any) => res.uniqueIdentifier));

        }),
        catchError(err => {
          console.error('save route error', err);
          console.dir(err)
          return throwError(() => err);
        })
      );


      // return this._httpService.post(environment.BASEURL +'/tmv-services/routes/save', formData, httpOptions).pipe(
      //   map((res: any) => res.uniqueIdentifier)
      // );
    }
  }

  /**
   * @returns true if localstorage is available and at least 100kb free space
   */
  canSaveDataInLocalStorage(): boolean {
    let canStoreDate = false;
    try {
      window.localStorage.setItem('TTTest', 'test');
      canStoreDate = window.localStorage.getItem('TTTest') === 'test';
      window.localStorage.removeItem('TTTest');

      canStoreDate = canStoreDate && this.getLocalStorageFreeSpace() >= 102400;
    } catch (error) {
      canStoreDate = false;
    }
    return canStoreDate;
  }

  /**
   * 
   * @returns free space in bytes
   */
  getLocalStorageFreeSpace(): number {
    let freeSpace = 0;
    try {
      let usedSpace = 0;
      for (let i = 0; i < window.localStorage.length; i++) {
        usedSpace += localStorage.key(i).length;
        usedSpace += window.localStorage.getItem(localStorage.key(i)).length
      }
      //chrome is 5MB
      freeSpace = (5 * 1048576) - usedSpace;
    } catch (error) {
    }
    return freeSpace > 0 ? freeSpace : 0;
  }

  /**
   *
   * @param canHaveMaxOneRoute if true, the route needs to be a OL linestring object, a multilinestring will be converted to a linestring if necessary
   * @param zoomToExtent if true, we will zoom to the extent of the route after adding it to the map
   * @param localStorageName if null/undefined or empty, we will get the most recent route
   */
  loadTraceRouteDataFromLocalStorage(canHaveMaxOneRoute: boolean, zoomToExtent: boolean, localStorageName?: string) {
    this._alertService.info(this._translateService.instant('trace.loadingstoredroute'), { 'autoClose': false });

    let traceRouteData = null;

    let isMostRecentHistoryItem = false;
    let checkedLocalStorageName: string = null;
    let isFromRemoteSource = false;

    //start up if necessary (this will also get the history list from localstorage)
    const wasAlreadyActive = this._isActive();
    if (!wasAlreadyActive) {
      this._startUp();
    }

    if (typeof localStorageName !== "string" || localStorageName.trim().length === 0) {
      const mostRecentHistoryItem = this._traceRouteHistory.getMostRecentHistoryItem();
      checkedLocalStorageName = mostRecentHistoryItem ? this._traceRouteHistory.getMostRecentHistoryItem().dataRef : null;
      isMostRecentHistoryItem = mostRecentHistoryItem !== null;
      isFromRemoteSource = mostRecentHistoryItem && mostRecentHistoryItem.remoteSource;
    } else {
      const position = this._traceRouteHistory.findDataRef(localStorageName);
      if (position > -1) {
        checkedLocalStorageName = this._traceRouteHistory.historyItems[position].dataRef;
        isMostRecentHistoryItem = position === (this._traceRouteHistory.historyItems.length - 1);
        isFromRemoteSource = this._traceRouteHistory.historyItems[position].remoteSource;
      } else {
        if (this.hasPermalinkParam() && this.getPermalinkParam() === localStorageName) {
          //remove invalid parameter
          this.setPermalinkParam(false);
        }
      }
    }

    if (checkedLocalStorageName) {
      try {
        const dataFromLocalStorage = localStorage.getItem(checkedLocalStorageName);

        if (dataFromLocalStorage) {

          let tempTraceRouteData = JSON.parse(dataFromLocalStorage);

          if (TraceRouteData.isTraceRouteData(tempTraceRouteData)) {
            if (tempTraceRouteData.epsgCode.toUpperCase() !== this._viewService.getProjection().getCode().toUpperCase()) {
              tempTraceRouteData = TraceRouteData.convertTraceRouteDataToMapProjection(tempTraceRouteData, this._viewService.getProjection().getCode());

              if (!tempTraceRouteData) {
                console.log('data projection transformation failed, original data was in another projection than the current map projection');
              }
            }

            if (tempTraceRouteData) {

              TraceRouteData.updateFormattedXYStrings(tempTraceRouteData, this._viewService.getProjection().getCode());

              if (canHaveMaxOneRoute && tempTraceRouteData.routes && tempTraceRouteData.routes.length > 1) {
                const routesCopy: TraceRouteType[] = [tempTraceRouteData.routes[0]];//no need for deep copy
                for (let i = 1; i < tempTraceRouteData.routes.length; i++) {
                  const routeCopy: TraceRouteType = tempTraceRouteData.routes[i];
                  for (let y = 0; y < routeCopy.routePoints.length; y++) {
                    //check if the first point is not the same as the last from the previous linestring
                    if (y == 0 && routesCopy[0].routePoints.length > 0) {
                      const lastWayPointPreviousLine = routesCopy[0].routePoints[routesCopy[0].routePoints.length - 1];
                      if (TraceRouteData.matchingCoordinates([lastWayPointPreviousLine.x, lastWayPointPreviousLine.y], [routeCopy.routePoints[0].x, routeCopy.routePoints[0].y])) {
                        continue;
                      }
                    }
                    routesCopy[0].routePoints.push(routeCopy.routePoints[y])
                  }
                }
                tempTraceRouteData.routes = routesCopy;
              }

              /*
              should we check the order of the waypoints again?
              const orderedWayPoints = TraceRouteData.orderWayPointsAlongRoute(
                tempTraceRouteData.wayPoints,
                tempTraceRouteData.routes,
                tempTraceRouteData.epsgCode,
                this._viewService.getProjection().getCode(),
                zoomToExtent
              );
              tempTraceRouteData.wayPoints = orderedWayPoints;
              */

              traceRouteData = tempTraceRouteData;

              if (isMostRecentHistoryItem) {
                this._traceRouteHistory.updateMostRecentHistoryItem_time();
              } else {
                //this moves the existing item upwards
                this._traceRouteHistory.addNewHistoryItemOrMoveExistingToTop(isFromRemoteSource, checkedLocalStorageName);
              }

              //the alert popup disappears on navigation(param update)
              //so we call setPermalinkParam at the end
              //------
              //it's possible that the history item already exists but no data in localstorage yet, but a click on that item in the import tab should work 
              //permalink should be added to url if necessary 
              //this.setPermalinkParam(checkedLocalStorageName);

              this._saveTraceRouteHistoryToLocalStorage();

              //this.openTraceTab(TraceDrawerType.VIEW_ROUTE);
              this.openTraceTab(this._browserSniffer.isPhone() ? TraceDrawerType.CLOSE_AND_VIEW_ROUTE : TraceDrawerType.VIEW_ROUTE);

              this.setTraceRouteData(
                traceRouteData.metadata,
                traceRouteData.wayPoints,
                traceRouteData.routes,
                traceRouteData.ttSettings,
                zoomToExtent
              );

              if (!wasAlreadyActive && this.isInViewMode() && this._browserSniffer.isPhone()) {
                window.setTimeout(() => {
                  this._informElevationProfileForDataUpdate();
                }, 1000);
              }
            }
          }

          if (!traceRouteData) {
            //remove invalid data from storage
            this._traceRouteHistory.removeHistoryItem(checkedLocalStorageName);
            checkedLocalStorageName = null;
          }

        } else {
          checkedLocalStorageName = null;
        }

        window.setTimeout(() => {
          this._alertService.clear();

          //the alert popup disappears on navigation(param update)
          //so we call setPermalinkParam at the end
          if (checkedLocalStorageName) {
            this.setPermalinkParam(checkedLocalStorageName);
          }
        }, traceRouteData ? 1000 : 0);

      } catch (error) {
        checkedLocalStorageName = null;
        console.log('unable to get traceRouteData object from localStorage :' + error);
      }
    }

    if (!checkedLocalStorageName) {
      //this._alertService.clear();
      this._alertService.error(this._translateService.instant('general.http-errors.client-side-error'), { 'autoClose': true });
    }

    return traceRouteData;
  }

  /**
   * this method will download the remote json file for the given token and will afterwards call this.openTraceTab(TraceDrawerType.VIEW_ROUTE) if the data was valid
   * 
   * @param zoomToExtent if true, we will zoom to the extent of the route after adding it to the map
   * @param appId 
   * @param permaLinkToken 
   * @returns Observable<any>
   */
  loadTraceRouteDataFromExternalStorage(zoomToExtent: boolean, appId: number | string, permaLinkToken: string): Observable<any> {
    this._alertService.info(this._translateService.instant('trace.loadingstoredroute'), { 'autoClose': false });
    //http GET request: get json data using permaLinkToken
    return this._fetchTraceRouteDataFromExternalStorage(appId, permaLinkToken).pipe(
      map((traceRouteData: TraceRouteData) => {
        if (TraceRouteData.isTraceRouteData(traceRouteData)) {
          if (traceRouteData.epsgCode.toUpperCase() !== this._viewService.getProjection().getCode().toUpperCase()) {
            traceRouteData = TraceRouteData.convertTraceRouteDataToMapProjection(traceRouteData, this._viewService.getProjection().getCode());

            if (!traceRouteData) {
              //this._alertService.clear();
              this._alertService.error(this._translateService.instant('general.http-errors.client-side-error'), { 'autoClose': true });

              console.log('data projection transformation failed, server data was in another projection than the current map projection');
              throw new Error('Conversion failed');
            }
          }

          TraceRouteData.updateFormattedXYStrings(traceRouteData, this._viewService.getProjection().getCode());

          /*
          should we check the order of the waypoints again?
          const orderedWayPoints = TraceRouteData.orderWayPointsAlongRoute(
            traceRouteData.wayPoints,
            traceRouteData.routes,
            traceRouteData.epsgCode,
            this._viewService.getProjection().getCode(),
            zoomToExtent
          );
          traceRouteData.wayPoints = orderedWayPoints;
          */

          //start up if necessary (this will also get the history list from localstorage)
          const wasAlreadyActive = this._isActive();
          if (!wasAlreadyActive) {
            this._startUp();
          }

          const alreadyInHistory = this._traceRouteHistory.findDataRef(permaLinkToken) > -1;
          //this moves the existing item upwards if alreadyInHistory or it creates a new item
          this._traceRouteHistory.addNewHistoryItemOrMoveExistingToTop(true, permaLinkToken);
          this._traceRouteHistory.setAppIdForMostRecentHistoryItem(appId);

          //the alert popup disappears on navigation(param update)
          //so we call setPermalinkParam at the end
          //this.setPermalinkParam(permaLinkToken);

          //this.openTraceTab(TraceDrawerType.VIEW_ROUTE);
          const xsmallScreen = this._browserSniffer.isPhone();
          this.openTraceTab(xsmallScreen ? TraceDrawerType.CLOSE_AND_VIEW_ROUTE : TraceDrawerType.VIEW_ROUTE);

          this.setTraceRouteData(
            traceRouteData.metadata,
            traceRouteData.wayPoints,
            traceRouteData.routes,
            traceRouteData.ttSettings,
            zoomToExtent
          );

          //traceRouteData doesn't need to be saved in localstorage, only the history needs to be update and/or saved
          if (!alreadyInHistory) {
            this._traceRouteHistory.updateMostRecentHistoryItem_baseInfo(this._traceRouteData, this.getRouteLength());
          }
          this._saveTraceRouteHistoryToLocalStorage();

          //add thumb
          if (!alreadyInHistory) {
            window.setTimeout(() => {
              this._setRouteThumbnail(permaLinkToken);
            }, 1000);
          }

          //this.openTraceTab(TraceDrawerType.VIEW_ROUTE);

          if (!wasAlreadyActive && this.isInViewMode() && xsmallScreen) {
            window.setTimeout(() => {
              this._informElevationProfileForDataUpdate();
            }, 1000);
          }

          window.setTimeout(() => {
            this._alertService.clear();

            //the alert popup disappears on navigation(param update)
            //so we call setPermalinkParam at the end
            this.setPermalinkParam(permaLinkToken);

            if (this._traceSuggestionsConfigService.configDownloadStatus() === 'init') {
              /*
              this.loadSuggestions(false, false);
              */
              this._traceSuggestionsConfigService.downloadConfigData(false);
            } else {
              this.checkForWrongSuggestionLang();
            }
          }, 1000);

        } else {
          //we could remove the parameter, but maybe the server or network was down for a few seconds and so we recieved an invalid object from _fetchTraceRouteDataFromExternalStorage
          //this.setPermalinkParam(false);
          //this._alertService.clear();
          this._alertService.error(this._translateService.instant('general.http-errors.client-side-error'), { 'autoClose': true });

          console.log('the server is unavailable or the downloaded data is not compatible with TraceRouteData');
          throw new Error('the server is unavailable or the downloaded data is not compatible with TraceRouteData');
        }
      })
    )
  }

  /**
 * Compares the hash of the last saved object with the hash of the one to be saved
 * @returns true if the candidate object is the same as the last one saved
 */
  hasJustBeenSavedToExternalStorage(): boolean {
    //making sure temp drawId's are removed everywhere 
    const traceRouteDataForHashCalculation = new TraceRouteData(
      this._traceRouteData.epsgCode,
      TraceRouteData.getFilteredTraceRouteDataObject(this._traceRouteData.metadata, true),
      TraceRouteData.getFilteredTraceRouteDataArray(this._traceRouteData.wayPoints, true),
      TraceRouteData.getFilteredTraceRouteDataArray(this._traceRouteData.routes, true),
      TraceRouteData.getFilteredTraceRouteDataObject(this._traceRouteData.ttSettings, true));

    return this._lastSavedObjectHash === hash(traceRouteDataForHashCalculation, { algorithm: 'md5' });
  }

  /**
   * Updates the '_lastSavedObjectHash' variable 
   * Note: it is recommended to use this function only when the saving is successful
   */
  updateLastSavedObjectHash(): void {
    //making sure temp drawId's are removed everywhere 
    const traceRouteDataForHashCalculation = new TraceRouteData(
      this._traceRouteData.epsgCode,
      TraceRouteData.getFilteredTraceRouteDataObject(this._traceRouteData.metadata, true),
      TraceRouteData.getFilteredTraceRouteDataArray(this._traceRouteData.wayPoints, true),
      TraceRouteData.getFilteredTraceRouteDataArray(this._traceRouteData.routes, true),
      TraceRouteData.getFilteredTraceRouteDataObject(this._traceRouteData.ttSettings, true));

    this._lastSavedObjectHash = hash(traceRouteDataForHashCalculation, { algorithm: 'md5' });
  }

  /**
* Getting TraceRouteData from a json file located in the NDP
* @param appId: the id of the 'download-app' created in the AC in order to store the traces
* @param traceUniqueId: a token generated when the user shares his map. see 'trace' param in the url ... 
* @returns Observable<TraceRouteData>
*/
  private _fetchTraceRouteDataFromExternalStorage(appId: number | string, traceUniqueId: string): Observable<TraceRouteData> {
    return this._httpService.get(this._appEnvironmentConfig.AC_BASEURL + '/remoteclient-open/app-' + appId.toString() + '/route-' + traceUniqueId + '/route.json').pipe(
      map((response: any) => {
        return new TraceRouteData(response.epsgCode, response.metadata, response.wayPoints, response.routes, response.ttSettings);
      }),
      catchError(error => {
        return of(new TraceRouteData(null, null, null, null, null));
      })
    );
  }


  /* ************************************************************************************************************************************************************************************
   * TraceRouteHistory storage, load and manipulation functions
   ************************************************************************************************************************************************************************************ */

  /**
   * this method will get the stored history from localstorage and initialize variable this._traceRouteHistory; if not available or invalid, this method will set variable this._traceRouteHistory as empty history list
   * 
   * @param doCleanupAfterLoad
   * @returns true if the history was loaded without errors from localstorage 
   */
  loadTraceRouteHistoryFromLocalStorage(doCleanupAfterLoad: boolean): boolean {
    let loaded = false;
    try {
      let tempTraceRouteHistory = JSON.parse(localStorage.getItem('routehistory'));
      this._traceRouteHistory = TraceRouteHistory.isArrayOfTraceRouteHistoryItemType(tempTraceRouteHistory) ? new TraceRouteHistory(tempTraceRouteHistory) : new TraceRouteHistory([]);
      loaded = true;
    } catch (error) {
      this._traceRouteHistory = new TraceRouteHistory([]);
      console.log('unable to get TraceRouteHistory object from localStorage :' + error);
    }

    if (doCleanupAfterLoad && this._traceRouteHistory.hasHistoryItems()) {
      this.fullCleanupAndSaveForHistoryItems();
    }

    return loaded;
  }

  private _saveTraceRouteHistoryToLocalStorage(): boolean {
    let isSaved = false;

    if (this._isActive()) {
      try {
        //we filter out nulls first
        localStorage.setItem(
          'routehistory',
          JSON.stringify(
            TraceRouteData.getFilteredTraceRouteDataObject(
              this._traceRouteHistory.historyItems,
              true
            )
          )
        );
        isSaved = true;
        //added null?
        this._updatedTraceRouteHistory.next(null);
      } catch (error) {
        console.log('unable to save TraceRouteHistory object in localStorage :' + error);
      }
    }

    return isSaved;
  }

  /**
   * 
   * @returns deep copy of current history items
   */
  getCopyOfTraceRouteHistoryItems(): TraceRouteHistoryItemType[] {
    return TraceRouteData.deepCopy(this._traceRouteHistory.historyItems);
  }

  addNewHistoryItemForImport(): TraceRouteHistoryItemType {
    const mostRecentHistoryItem = this._traceRouteHistory.addNewHistoryItemOrMoveExistingToTop(true);
    this._saveTraceRouteHistoryToLocalStorage();
    return mostRecentHistoryItem;
  }

  addNewHistoryItemForEditing(): TraceRouteHistoryItemType {
    const mostRecentHistoryItem = this._traceRouteHistory.addNewHistoryItemOrMoveExistingToTop(false);
    this._saveTraceRouteHistoryToLocalStorage();
    return mostRecentHistoryItem;
  }

  fullCleanupAndSaveForHistoryItems(dataRefToSkip?: string) {
    //we need to check if it's still active, because during shutdown the history list is emptied
    if (this._isActive()) {
      this._traceRouteHistory.cleanupHistoryItems(false, dataRefToSkip);
      if (this.getPermalinkParam() && this._traceRouteHistory.findDataRef(this.getPermalinkParam()) === -1) {
        this.setPermalinkParam(false);
      }
      //this._isActive() is checked again in this method
      this._saveTraceRouteHistoryToLocalStorage();
    }
  }

  removeHistoryItem(dataRef: string): boolean {
    const isRemoved = this._traceRouteHistory.removeHistoryItem(dataRef);
    this._saveTraceRouteHistoryToLocalStorage();
    return isRemoved;
  }

  private _updateMostRecentHistoryItem_upDownDist(): boolean {
    const isSaved = this._traceRouteHistory.updateMostRecentHistoryItem_upDownDistance(this._elevationUpwardDist, this._elevationDownwardDist);
    return isSaved && this._saveTraceRouteHistoryToLocalStorage();
  }

  getMostRecentHistoryItem_upwardDist(): number {
    const mostRecentHistoryItem = this._traceRouteHistory.getMostRecentHistoryItem();
    return mostRecentHistoryItem && typeof mostRecentHistoryItem.upwardDist === 'number' ? mostRecentHistoryItem.upwardDist : -1;
  }
  getMostRecentHistoryItem_downwardDist(): number {
    const mostRecentHistoryItem = this._traceRouteHistory.getMostRecentHistoryItem();
    return mostRecentHistoryItem && typeof mostRecentHistoryItem.downwardDist === 'number' ? mostRecentHistoryItem.downwardDist : 1;
  }

  createUniqueNameForNewHistoryItem(newName: string): string {
    return this._traceRouteHistory.createUniqueName(typeof newName === "string" && newName.trim().length > 0 ? newName : this._translateService.instant("trace.field.noname"));
  }

  removeSameImportsFromHistory(
    metadata: TraceMetadataType,
    wayPoints: TraceWayPointType[],
    routes: TraceRouteType[],
    ttSettings: TraceTTSettingsType) {
    const hashCode = hash(
      new TraceRouteData(
        this._viewService.getProjection().getCode(),
        TraceRouteData.getFilteredTraceRouteDataObject(metadata, true),
        TraceRouteData.getFilteredTraceRouteDataArray(wayPoints, true),
        TraceRouteData.getFilteredTraceRouteDataArray(routes, true),
        TraceRouteData.getFilteredTraceRouteDataObject(ttSettings, true)),
      { algorithm: 'md5' });
    this._traceRouteHistory.removeSameImportsFromHistory(hashCode);
  }

  isEditedHistoryItem(dataRef: string): boolean {
    return this._traceRouteHistory.isEditedHistoryItem(dataRef);
  }

  /* ************************************************************************************************************************************************************************************
   * setters for renewing object this._traceRouteData
   * getter for this._traceRouteData
   ************************************************************************************************************************************************************************************ */

  /**
   * sets this._traceRouteData with the default value (metadata with no values and empty arrays for waypoints and routes)
   * @param removePermalinkAfterManualAction if true, the parameter 'trace' is removed from the url if it's present in the query
   * @param keepMetadata optional, if present and true, this._traceRouteData.metadata will not be reset, in all other case this._traceRouteData will be completely reset
   * @returns 
   */
  resetTraceRouteData(removePermalinkAfterManualAction: boolean, keepMetadata?: boolean) {
    if (removePermalinkAfterManualAction) {
      this.setPermalinkParam(false);
    }

    if (this._popupService.isPopupAlreadyOnMap()) {
      this._popupService.hideInCaseOfType('TraceInfo');
    }

    if (keepMetadata) {
      //title, description can stay
      this._traceRouteData.wayPoints = [];
      this._traceRouteData.routes = [];
    } else {
      this._traceRouteData = TraceRouteData.getEmptyTraceRouteDataObject(this._viewService.getProjection().getCode());
    }

    this._hasWayPointsOnMap.set(false);
    this._hasRouteOnMap.set(false);
    this._hasTraceRouteWithGeneralInfo.set(false);
    this._hasRouteToCreateProfile.set(false);

    if (!this._isActive()) {
      return;
    }

    this._traceDrawLayerLineString.getSource().clear();
    this._traceDrawLayerArrows.getSource().clear();
    this._traceDrawLayerPoint.getSource().clear();
    this._traceDrawLayerModifiable.getSource().clear();
    this._traceDrawLayerModifiableHighlight.getSource().clear();
    this._traceDrawLayerHighlight.getSource().clear();
    this._forceClearAllFadeInLayers();

    //this will also cancel elevation and/or autorouting requests
    this._broadcastTraceRouteDataUpdate(keepMetadata ? TraceRouteDataUpdateType.REMOVED_DATA_MAP : TraceRouteDataUpdateType.REMOVED_DATA);

  }

  /**
   * when loading data from local storage or remote server:
   * - the input parameters should already be checked if they are valid
   * - every point (wayPoints/routePoints) should be already in the current map projection
   * - we also assume that the waypoints are already logically ordered along the route
   *
   * @param metadata
   * @param wayPoints
   * @param routes
   * @param zoomToNewExtent zoom to extent of new data
   * @returns true if data is updated (returns always false when this._isActive() is false)
   */
  setTraceRouteData(
    metadata: TraceMetadataType,
    wayPoints: TraceWayPointType[],
    routes: TraceRouteType[],
    ttSettings: TraceTTSettingsType,
    zoomToNewExtent: boolean
  ): boolean {
    if (!this._isActive()) {
      return false;
    }

    if (this._traceDrawLayerLineString.getSource().getFeatures().length > 0 || this._traceDrawLayerPoint.getSource().getFeatures().length > 0) {
      this._broadcastTraceRouteDataUpdate(TraceRouteDataUpdateType.REPLACING_DATA_MAP);
    }

    TraceRouteData.addTempDrawIdsToPoints(wayPoints);
    routes.forEach((route: TraceRouteType) => {
      TraceRouteData.addTempDrawIdsToPoints(route.routePoints);
    });

    //we take deep copies(?)
    this._traceRouteData = new TraceRouteData(
      this._viewService.getProjection().getCode(),
      TraceRouteData.getFilteredTraceRouteDataObject(metadata, false),
      TraceRouteData.getFilteredTraceRouteDataArray(wayPoints, false),
      TraceRouteData.getFilteredTraceRouteDataArray(routes, false),
      TraceRouteData.getFilteredTraceRouteDataObject(ttSettings, false)
    );

    this._traceDrawLayerLineString.getSource().clear();
    this._traceDrawLayerArrows.getSource().clear();
    this._traceDrawLayerPoint.getSource().clear();
    this._traceDrawLayerModifiable.getSource().clear();
    this._traceDrawLayerModifiableHighlight.getSource().clear();
    this._traceDrawLayerHighlight.getSource().clear();
    this._forceClearAllFadeInLayers();

    this._hasWayPointsOnMap.set(this._traceRouteData.wayPoints.length > 0);
    this._hasRouteOnMap.set(this._traceRouteData.routes.length > 0 && this._traceRouteData.routes[0].routePoints.length > 0);
    this._hasTraceRouteWithGeneralInfo.set(
      (typeof this._traceRouteData.metadata.name === 'string' && this._traceRouteData.metadata.name.length > 0)
      ||
      (typeof this._traceRouteData.metadata.desc === 'string' && this._traceRouteData.metadata.desc.length > 0)
    );
    this._hasRouteToCreateProfile.set(this._traceRouteData.routes.length > 0 && this._traceRouteData.routes[0].routePoints.length > 1);

    //fade in layer after features are added
    this._traceDrawLayerLineString.setOpacity(0);
    this._traceDrawLayerArrows.setOpacity(0);
    this._traceDrawLayerPoint.setOpacity(0);

    this._updateRoutesOnMap();
    this._updateWaypointsOnMap();

    let totalExtent: olExtent.Extent = null;

    if (this._traceDrawLayerLineString.getSource().getFeatures().length > 0) {
      totalExtent = this._traceDrawLayerLineString.getSource().getExtent();

      //fade in layer after zoom
      window.setTimeout(
        () => {
          this.fadeInLayer(this._traceDrawLayerLineString, true, false, 1, /*500*/ 300);
          this.fadeInLayer(this._traceDrawLayerArrows, true, false, 1, /*500*/ 300);
        },
        zoomToNewExtent ? 1000 : 300
      );
    } else {
      this._traceDrawLayerLineString.setOpacity(1);
      this._traceDrawLayerArrows.setOpacity(1);
    }

    if (this._traceDrawLayerPoint.getSource().getFeatures().length > 0) {
      totalExtent = totalExtent ? olExtent.extend(totalExtent, this._traceDrawLayerPoint.getSource().getExtent()) : this._traceDrawLayerPoint.getSource().getExtent();

      //fade in layer after zoom
      window.setTimeout(
        () => {
          this.fadeInLayer(this._traceDrawLayerPoint, true, false, 1, /*500*/ 300);
        },
        zoomToNewExtent ? 1000 : 300
      );
    } else {
      this._traceDrawLayerPoint.setOpacity(1);
    }

    if (totalExtent && zoomToNewExtent) {
      //zoom to extent
      this._mapService
        .getCurrentMap()
        .getView()
        .fit(olExtent.buffer(totalExtent, 0), {
          size: this._mapService.getCurrentMap().getSize(),
          padding: [0, 0, 0, 0],
          duration: 500,
          maxZoom: this._maxAutoZoomForPrj,
          callback: () => {
            this._toggleEventService.toggleEvent({
              forwardedFor: 'trace-zoomto',
              eventName: ToggleEventNameType.INACTIVE
            });
          }
        });
    }

    this._broadcastTraceRouteDataUpdate(TraceRouteDataUpdateType.NEW_DATA);

    if (window.location.href.indexOf('logimport') > -1) {
      console.log("coordinates for (multi)linestring(s) on map :");
      this._traceDrawLayerLineString.getSource().getFeatures().forEach((feature) => {
        if (feature.getGeometry() instanceof LineString || feature.getGeometry() instanceof MultiLineString) {
          let totalRouteLength = 0;
          let routeLength = 0;
          const linestrings = feature.getGeometry() instanceof LineString ? [(feature.getGeometry() as LineString)] : (feature.getGeometry() as MultiLineString).getLineStrings();
          linestrings.forEach((linestring: LineString, index: number) => {
            console.log("line coordinates (" + index + ")");
            console.log(JSON.stringify(linestring.getCoordinates()));
            routeLength = linestring.getLength();
            totalRouteLength += routeLength;
            console.log("routeLength : " + totalRouteLength);
          });

          console.log("totalRouteLength : " + totalRouteLength);
        }
      });
    }

    return true;
  }

  /**
   * @returns this._traceRouteData (no deep copy)
   */
  getTraceRouteData() {
    return this._isActive() ? this._traceRouteData : null;
  }

  /* ************************************************************************************************************************************************************************************
   * getters and setters for properties inside this._traceRouteData
   ************************************************************************************************************************************************************************************ */

  /**
  * 
  *  @param broadcastUpdate
  *  @returns true if data is updated
  */
  updateTraceRouteData_metadatatime(broadcastUpdate: boolean): boolean {
    if (!this._isActive()) {
      return false;
    }

    this._traceRouteData.metadata.time = new Date().getTime();

    if (broadcastUpdate) {
      this._broadcastTraceRouteDataUpdate(TraceRouteDataUpdateType.UPDATED_METADATA_INFO);
    }

    return true;
  }

  /**
   * @param newMetadata
   * @param isUndoAction
   * @param broadcastUpdate this should only be false if the call is followed by another method call that will do the broadcast
   * @returns true if data is updated
   */
  setTraceRouteData_metadata(newMetadata: TraceMetadataType, isUndoAction: boolean, broadcastUpdate: boolean): boolean {
    if (!this._isActive()) {
      return false;
    }

    if (TraceRouteData.isTraceMetadataType(newMetadata)) {
      this._traceRouteData.metadata = TraceRouteData.getFilteredTraceRouteDataObject(newMetadata, false);

      this._hasTraceRouteWithGeneralInfo.set(
        (typeof this._traceRouteData.metadata.name === 'string' && this._traceRouteData.metadata.name.length > 0)
        ||
        (typeof this._traceRouteData.metadata.desc === 'string' && this._traceRouteData.metadata.desc.length > 0)
      );

      //this.saveTraceRouteDataToLocalStorage();
      if (broadcastUpdate) {
        this._broadcastTraceRouteDataUpdate(isUndoAction ? TraceRouteDataUpdateType.UNDO : TraceRouteDataUpdateType.UPDATED_METADATA_INFO);
      }
      return true;
    }

    return false;
  }

  /**
   * remove metadata (name/desc)
   * 
   * @param broadcastUpdate
   * @returns true if data is updated
   */
  removeTraceRouteData_metadata(broadcastUpdate: boolean): boolean {
    if (!this._isActive()) {
      return false;
    }

    if (this.setTraceRouteData_metadata({ time: new Date().getTime() }, false, false)) {

      this._hasTraceRouteWithGeneralInfo.set(false);

      if (broadcastUpdate) {
        this._broadcastTraceRouteDataUpdate(TraceRouteDataUpdateType.REMOVED_METADATA_INFO);
      }
      return true;
    }

    return false;
  }

  /**
   * reordering is done automatically
   * 
   * @param newWayPoints
   * @param traceRouteDataUpdateType
   * @returns true if data is updated
   */
  setTraceRouteData_wayPoints(newWayPoints: TraceWayPointType[], traceRouteDataUpdateType: TraceRouteDataUpdateType): boolean {
    if (!this._isActive()) {
      return false;
    }

    let orderedWayPoints = Array.isArray(newWayPoints)
      ? TraceRouteData.getFilteredTraceRouteDataArray(newWayPoints, false)
      : new Array[0]();

    TraceRouteData.addTempDrawIdsToPoints(orderedWayPoints);

    orderedWayPoints = TraceRouteData.orderWayPointsAlongRoute(
      orderedWayPoints,
      this._traceRouteData.routes,
      this._viewService.getProjection().getCode(),
      this._viewService.getProjection().getCode(),
      true
    );

    this._traceRouteData.wayPoints = orderedWayPoints;
    this.updateTraceRouteData_metadatatime(false);

    this._hasWayPointsOnMap.set(this._traceRouteData.wayPoints.length > 0);

    this._updateWaypointsOnMap();

    //this.saveTraceRouteDataToLocalStorage() will be called by the listener
    this._broadcastTraceRouteDataUpdate(traceRouteDataUpdateType);

    return true;
  }

  /**
  * reordering is done automatically
  * 
  * @param position
  * @returns true if data is updated
  */
  updateTraceRouteData_wayPoint(
    position: number,
    name: string,
    desc: string
  ): boolean {
    if (this._isActive()) {
      if (
        typeof position === 'number' &&
        position >= 0 &&
        position < this._traceRouteData.wayPoints.length
      ) {

        this._traceRouteData.wayPoints[position].name = typeof name === 'string' && name.trim().length > 0 ? name.trim() : null;
        this._traceRouteData.wayPoints[position].desc = typeof desc === 'string' && desc.trim().length > 0 ? desc.trim() : null;
        this._updateWaypointsOnMap();
        this._broadcastTraceRouteDataUpdate(TraceRouteDataUpdateType.UPDATED_POI_INFO);

        return true;
      }
    }
    return false;
  }

  /**
   * reordering is done automatically
   * 
   * @param position
   * @returns true if data is updated
   */
  removeTraceRouteData_wayPoint(
    position: number
  ): boolean {
    if (this._popupService.isPopupAlreadyOnMap()) {
      this._popupService.hideInCaseOfType('TraceInfo');
    }

    if (this._isActive()) {
      if (
        typeof position === 'number' &&
        position >= 0 &&
        position < this._traceRouteData.wayPoints.length
      ) {

        const wayPointsCopy = TraceRouteData.deepCopy(this._traceRouteData.wayPoints);
        wayPointsCopy.splice(position, 1);
        //reordering, timestamp, storage, _broadcastTraceRouteDataUpdate and map update is done by setTraceRouteData_wayPoints
        return this.setTraceRouteData_wayPoints(wayPointsCopy, TraceRouteDataUpdateType.REMOVED_POI);
      }
    }

    return false;
  }

  getTraceRouteData_wayPoint(wayPointIndex: number): TraceWayPointType {
    return wayPointIndex >= 0 &&
      wayPointIndex < this._traceRouteData.wayPoints.length
      ? this._traceRouteData.wayPoints[wayPointIndex]
      : null;
  }

  /**
   *
   * @param newRoutes
   * @param traceRouteDataUpdateType if param orderWayPointsAlongNewRoute is true, then this value will be passed on to method setTraceRouteData_wayPoints, otherwise this value is ignored
   * @param orderWayPointsAlongNewRoute this should only be false if you are going to update the waypoint array directly after calling this method
   * @returns true if data is updated
   */
  setTraceRouteData_routes(newRoutes: TraceRouteType[], traceRouteDataUpdateType: TraceRouteDataUpdateType, orderWayPointsAlongNewRoute: boolean): boolean {
    if (!this._isActive()) {
      return false;
    }

    newRoutes = Array.isArray(newRoutes) ? newRoutes : new Array[0]();
    newRoutes.forEach((route: TraceRouteType) => {
      TraceRouteData.addTempDrawIdsToPoints(route.routePoints);
    });

    this._traceRouteData.routes = TraceRouteData.getFilteredTraceRouteDataArray(newRoutes, false)

    this._hasRouteOnMap.set(this._traceRouteData.routes.length > 0 && this._traceRouteData.routes[0].routePoints.length > 0);
    this._hasRouteToCreateProfile.set(this._traceRouteData.routes.length > 0 && this._traceRouteData.routes[0].routePoints.length > 1);

    this._updateRoutesOnMap();

    if (orderWayPointsAlongNewRoute) {
      //timestamp, storage and _broadcastTraceRouteDataUpdate is done by setTraceRouteData_wayPoints
      this.setTraceRouteData_wayPoints(this._traceRouteData.wayPoints, traceRouteDataUpdateType);
    }

    return true;
  }

  private _replaceTraceRouteData_route(newRoute: TraceRouteType, position: number, traceRouteDataUpdateType: TraceRouteDataUpdateType): boolean {
    if (!this._isActive()) {
      return false;
    }

    if (
      typeof position === 'number' &&
      position >= 0 &&
      position < this._traceRouteData.routes.length
    ) {

      TraceRouteData.addTempDrawIdsToPoints(newRoute.routePoints);

      this._hasRouteOnMap.set(this._traceRouteData.routes.length > 0 && this._traceRouteData.routes[0].routePoints.length > 0);
      this._hasRouteToCreateProfile.set(this._traceRouteData.routes.length > 0 && this._traceRouteData.routes[0].routePoints.length > 1);

      this._traceRouteData.routes[position] = TraceRouteData.getFilteredTraceRouteDataObject(newRoute, false);

      this._updateRoutesOnMap();

      //timestamp, storage and _broadcastTraceRouteDataUpdate is done by setTraceRouteData_wayPoints
      this.setTraceRouteData_wayPoints(this._traceRouteData.wayPoints, traceRouteDataUpdateType);

      return true;
    }

    return false;
  }

  removeTraceRouteData_routePoint(routeIndex: number, routePointIndex: number) {
    if (this._isActive()) {
      if (this.isBusyWithAutoRouting()) {
        //alert('busy');
        this._alertService.info(this._translateService.instant('trace.create-route.auto-routing-busy'), { 'autoClose': true });
        return false;
      }

      if (routeIndex >= 0 && routePointIndex >= 0 && this._traceRouteData.routes.length > routeIndex && this._traceRouteData.routes[routeIndex].routePoints.length > routePointIndex) {

        if (this._traceRouteData.routes[routeIndex].routePoints[routePointIndex].autoDraw) {
          //normally these point cannot be deleted manually
          return;
        }

        //we don't want angular to update the right panel while editing the array
        const wasEndPoint = routePointIndex === this._traceRouteData.routes[routeIndex].routePoints.length - 1;
        const routePointsCopy: TraceWayPointType[] = TraceRouteData.deepCopy(this._traceRouteData.routes[routeIndex].routePoints);
        const fromToIndexes = this._findAutoDrawRoutePointsAroundIndex(routePointsCopy, routePointIndex, false);

        if (fromToIndexes[0] === fromToIndexes[1]) {
          routePointsCopy.splice(fromToIndexes[0], 1);
        } else {
          routePointsCopy.splice(fromToIndexes[0], (fromToIndexes[1] - fromToIndexes[0]) + 1);
        }

        this._traceRouteData.routes[routeIndex].routePoints = routePointsCopy;

        this._hasRouteOnMap.set(this._traceRouteData.routes.length > 0 && this._traceRouteData.routes[0].routePoints.length > 0);
        this._hasRouteToCreateProfile.set(this._traceRouteData.routes.length > 0 && this._traceRouteData.routes[0].routePoints.length > 1);

        this._updateRoutesOnMap();

        //timestamp, storage and _broadcastTraceRouteDataUpdate is done by setTraceRouteData_wayPoints
        this.setTraceRouteData_wayPoints(this._traceRouteData.wayPoints, wasEndPoint || !this.autoRouting() ?
          TraceRouteDataUpdateType.REMOVED_ROUTEPOINT : TraceRouteDataUpdateType.REMOVED_ROUTEPOINT_TEMP);

        if (this.autoRouting() && !wasEndPoint && this._traceRouteData.routes[routeIndex].routePoints.length > 1) {
          //this extra broadcast makes sure that the resulting TraceRouteDataUpdateType.MOVED_ROUTE event after autorouting is not added to the undo list
          this._broadcastTraceRouteDataUpdate(TraceRouteDataUpdateType.MOVED_ROUTE_AFTER_REMOVAL_TEMP);
          this._handleMapInteraction_autorouting(this._traceRouteData.routes.length - 1, TraceRouteData.deepCopy(this._traceRouteData.routes[routeIndex]), true, (fromToIndexes[0] - 1) < 0 ? 0 : (fromToIndexes[0] - 1), true);
        }

        return true;
      }
    }
    return false;
  }

  /**
   * 
   * @param routePoints 
   * @param routePointIndex 
   * @param includePrevioisAndNextFixedPoint
   * @returns array with two indexes; if the first index equals routePointIndex (and includePrevioisAndNextFixedPoint is false), than there are no points before routePointIndex with autoDraw value true; if the second index equals routePointIndex (and includePrevioisAndNextFixedPoint is false), than there are no points after routePointIndex with autoDraw value true
   */
  private _findAutoDrawRoutePointsAroundIndex(routePoints: TraceWayPointType[], routePointIndex: number, includePreviousAndNextFixedPoint: boolean): number[] {
    if (!Array.isArray(routePoints) || routePointIndex < 0 || routePointIndex >= routePoints.length) {
      return [routePointIndex, routePointIndex];
    }

    let autoDrawFromIndex = routePointIndex;

    for (let i = routePointIndex - 1; i >= 0; i--) {
      if (routePoints[i].autoDraw) {
        autoDrawFromIndex = i;
      } else {
        if (includePreviousAndNextFixedPoint) {
          autoDrawFromIndex = i;
        }
        break;
      }
    }

    let autoDrawUntilIndex = routePointIndex;

    for (let i = routePointIndex + 1; i < routePoints.length; i++) {
      if (routePoints[i].autoDraw) {
        autoDrawUntilIndex = i;
      } else {
        if (includePreviousAndNextFixedPoint) {
          autoDrawUntilIndex = i;
        }
        break;
      }
    }

    return [autoDrawFromIndex, autoDrawUntilIndex];
  }

  getTraceRouteData_routePoint(routeIndex: number, routePointIndex: number): TraceWayPointType {
    return routeIndex >= 0 &&
      routeIndex < this._traceRouteData.routes.length &&
      routePointIndex >= 0 &&
      routePointIndex < this._traceRouteData.routes[routeIndex].routePoints.length
      ? this._traceRouteData.routes[routeIndex].routePoints[routePointIndex]
      : null;
  }

  reverseRoute() {
    if (!this.hasRouteOnMap()) {
      return;
    }
    this._traceDrawLayerArrows.getSource().clear();
    const routesCopy: TraceRouteType[] = TraceRouteData.deepCopy(this._traceRouteData.routes);
    const reversedRoutes: TraceRouteType[] = [];

    for (let i = routesCopy.length - 1; i >= 0; i--) {
      const reversedRoutePoints: TraceWayPointType[] = [];

      for (let y = routesCopy[i].routePoints.length - 1; y >= 0; y--) {
        reversedRoutePoints.push(routesCopy[i].routePoints[y]);
      }

      routesCopy[i].routePoints = reversedRoutePoints;

      reversedRoutes.push(routesCopy[i]);
    }

    this.setTraceRouteData_routes(reversedRoutes, TraceRouteDataUpdateType.MOVED_ROUTE, true);
    if (this._browserSniffer.isPhone()) {
      this.closeViewDialog();
    }
  }

  /* ************************************************************************************************************************************************************************************
   * utilities for properties inside this._traceRouteData
   ************************************************************************************************************************************************************************************ */

  getTraceRouteData_metadatatime_string(addSeconds: boolean, forHtml: boolean): string {
    if (!this._isActive()) {
      return '';
    }
    return TraceRouteData.formatTime(this._traceRouteData.metadata.time, addSeconds, forHtml, '');
  }

  /*
  private _getWayPointIndex(wayPointId: 'startPoint' | 'endPoint' | TraceWayPointDrawIdType): number {
    let index = -1;
    if (typeof wayPointId === 'string' && /^(startPoint|endPoint)$/.test(wayPointId)) {
      if (this._traceRouteData.routes.length > 0) {
        index = wayPointId === 'startPoint' ? 0 : this._traceRouteData.routes.length - 1
      }
    } else {
      //case TraceWayPointDrawIdType
      //normally only used for POI's in create mode, because they are often reordered, no fixed index
      let tempIndex = 0;
      this._traceRouteData.wayPoints.every(value => {
        if (wayPointId === value.drawId) {
          index = tempIndex;
        }
        tempIndex++;
        return index === -1;
      });

      return index;
    }
  }
  */

  private _getWayPointAsPoint(wayPointIndex: number | 'startPoint' | 'endPoint'): Point {
    if (typeof wayPointIndex === 'number') {
      const traceWayPoint = this.getTraceRouteData_wayPoint(wayPointIndex);
      if (traceWayPoint) {
        return new Point([traceWayPoint.x, traceWayPoint.y]);
      }
    } else {
      if (this._traceRouteData.routes.length > 0) {

        let oneRoute =
          this._traceRouteData.routes[
          wayPointIndex === 'startPoint'
            ? 0
            : this._traceRouteData.routes.length - 1
          ];

        if (oneRoute.routePoints.length > 0) {
          return new Point([
            oneRoute.routePoints[
              wayPointIndex === 'startPoint'
                ? 0
                : oneRoute.routePoints.length - 1
            ].x,
            oneRoute.routePoints[
              wayPointIndex === 'startPoint'
                ? 0
                : oneRoute.routePoints.length - 1
            ].y,
          ]);
        }
      }
    }

    return null;
  }

  /*
  formatWayPointCoords(
    wayPointIndex: number | 'startPoint' | 'endPoint',
    decimals: number
  ): string {
    return TraceRouteData.formatPointForHtmlView(this._getWayPointAsPoint(wayPointIndex), this._viewService.getProjection().getCode());
  }
  */

  private _getRoutePointAsPoint(routeIndex: number, routePointIndex: number): Point {
    let traceRoutePoint = this.getRoutePoint(routeIndex, routePointIndex);

    if (traceRoutePoint) {
      return new Point([traceRoutePoint.x, traceRoutePoint.y]);
    }

    return null;
  }

  getRoutePoint(routeIndex: number, routePointIndex: number): TraceWayPointType {
    let traceRoutePoint: TraceWayPointType = null;

    if (routeIndex >= 0 && routePointIndex >= 0 && this._traceRouteData.routes.length > routeIndex && this._traceRouteData.routes[routeIndex].routePoints.length > routePointIndex) {
      traceRoutePoint = this._traceRouteData.routes[routeIndex].routePoints[routePointIndex];
    }

    return traceRoutePoint;
  }

  /*
  formatRoutePointCoords(routeIndex: number, routePointIndex: number, decimals: number): string {
    return TraceRouteData.formatPointForHtmlView(this._getRoutePointAsPoint(routeIndex, routePointIndex), this._viewService.getProjection().getCode());
  }
  */

  /* ************************************************************************************************************************************************************************************
  * broadcast utility to let all components know that the model has changed
  ************************************************************************************************************************************************************************************ */

  private _broadcastTraceRouteDataUpdate(updateType: TraceRouteDataUpdateType) {


    if (updateType.indexOf('_temp') > -1) {
      //if too many chunks, the elevation requests can keep all http connections busy, so the snap to road requests stay in the queue for a long time
      this._resetElevationProfileData();
    } else if (updateType.indexOf('removed_data') === 0) {
      //user stopped using trace component
      this._resetElevationProfileData();
      this._cancelAutoRoutingRequest();
    }

    //test for '_route' includes '_routepoint' 
    //test for '_data' includes '_data_map' 
    const isFinalizedDataUpdate = updateType.indexOf('_temp') === -1 && (updateType.indexOf('_route') > -1 || updateType.indexOf('_poi') > -1 || updateType.indexOf('_data') > -1 || updateType === TraceRouteDataUpdateType.UNDO || updateType === TraceRouteDataUpdateType.REDO)

    if (updateType === TraceRouteDataUpdateType.NEW_DATA) {
      this.updateElevationUpDownDist(this.hasRouteOnMap() ? this.getMostRecentHistoryItem_upwardDist() : -1, this.hasRouteOnMap() ? this.getMostRecentHistoryItem_downwardDist() : 1);
    } else if (isFinalizedDataUpdate) {
      //not necessary for changes related to POI, but at the moment we don't make a difference, because ElevationProfileComponent also doens't have seperate methods for route or poi
      //(*) we leave the meters until the new data comes in -> otherwise you can remove the if and uncomment the two calls to this._resetElevationProfileData() below (*)
      if (!this.hasRouteToCreateProfile()) {
        this.updateElevationUpDownDist(-1, 1);
      }
    }

    this._updatedTraceRouteData.next(updateType);

    if (isFinalizedDataUpdate) {
      //for some types we don't need to execute this part, e.g. *_TEMP , *_METADATA_* , COPY_TO_OTHER_TAB , UPDATED_ELEV_UPDOWN_DIST

      //in the future make different call(s) to ElevationProfileComponent when only the POI's are updated? no need to calculate all again
      if (updateType === TraceRouteDataUpdateType.NEW_DATA || updateType === TraceRouteDataUpdateType.REMOVED_DATA_MAP || updateType === TraceRouteDataUpdateType.REMOVED_DATA || !this.hasRouteToCreateProfile()) {
        this._resetElevationProfileData();
      }

      if (updateType !== TraceRouteDataUpdateType.REMOVED_DATA_MAP && updateType !== TraceRouteDataUpdateType.REMOVED_DATA) {
        if (this.hasRouteToCreateProfile() && (this.isElevationProfileComponentOpen() || this.canUpdateElevationWhenPanelClosed())) {
          if (
            //normally new route, view mode
            updateType === TraceRouteDataUpdateType.NEW_DATA
            //normally second point during editing mode, up/down distance not yet calculated
            || this._elevationUpwardDist < 0
            //reverse route during view mode (during editing mode the else clause will take care of it)
            || (this.isInViewMode() && updateType === TraceRouteDataUpdateType.MOVED_ROUTE)
          ) {
            //(*) see comment above
            //this._resetElevationProfileData();
            //in case of NEW_DATA we use a longer timeout, the map is then centering and fading in, this normally only happens 1 time
            window.setTimeout(() => {
              this.renewElevationProfileData(false);
            }, updateType === TraceRouteDataUpdateType.NEW_DATA ? 1500 : 500);
          } else if (this.isInEditingMode()) {
            //(*) see comment above
            //this._resetElevationProfileData();
            //this call will make sure quick sequential edits are not causing to many graph redraws
            //added null?
            this._delegatedElevationProfileRenewal.next(null);
          }
        } else if (this.hasPermalinkParam()) {
          //not necessary for changes related to POI, but at the moment we don't make a difference, because ElevationProfileComponent also doens't have seperate methods for route or poi
          this._updateMostRecentHistoryItem_upDownDist();
        }
      }
    }
  }

  /* ************************************************************************************************************************************************************************************
   * vector layer styling
   *
   * zIndexes : 
   * > vectorlayer   (layers for the modify animation have the same zIndex)
   *   - linestroke 0
   *   - line 1
   *   - routepoint(=line point)/waypoint/routepoint highlight/waypoint highlight/closest linesegment/line arrow  2
   *   - extra linestroke during autorouting 2
   *   - endPoint 3
   *   - startPoint  4
   * > vectorlayer highlight :  zIndex vectorlayer + 1 
   * > draw intercation overlay :  zIndex vectorlayer highlight + 1
   * > modify intercation overlay :  zIndex vectorlayer highlight + 2
   * 
   ************************************************************************************************************************************************************************************ */
  private _getTraceStyle(feature: any, resolution: number, isTraceDrawLayerHighlight: boolean, isArrowStyle?: boolean): Style[] {
    const isPdfLayer = this._traceDrawLayerLineString.get('pdflayer');
    let view_mode = isPdfLayer || this.isInViewMode()
    let stylesArray = [];

    const orangeColor = 'rgba(255, 91, 2, 1.0)';
    const orangeColorLighter = 'rgba(255, 119, 50, 1.0)';
    //const ngiOrangeColor = 'rgba(234, 89, 1, 1.0)';
    const ngiOrangeColorDarker = 'rgba(225, 62, 1, 1.0)';
    const ngiOrangeColorVeryDark = 'rgba(141, 38, 0, 1.0)';
    const ngiDarkBlueColor = 'rgba(40, 60, 86, 1.0)';
    const ngiBlueColor = 'rgba(0, 108, 157, 1.0)';
    const ngiBlueGreenColor = 'rgba(6, 138, 155, 1.0)';
    const ngiGreenBlueColor = 'rgba(47, 162, 152, 1.0)';
    const ngiDarkGreenBlueColor = 'rgba(29, 134, 123, 1.0)';
    //  const ngiDarkGreenBlueColor = 'rgba(0, 80, 74, 1.0)';
    //const zoomLevel = this._viewService.getZoom();    
    const zoomLevel = this._mapService.getCurrentMap().getView().getZoomForResolution(resolution);

    const isSuggestion = feature.get("isSuggestion");
    if (isSuggestion && !feature.get("isVisible")) {
      return stylesArray;
    }

    switch (feature.getGeometry().getType()) {
      case 'Point': {

        //const defaultPointRad = zoomLevel < 5 ? 2 : zoomLevel < 8 ? 4 : 6;
        const defaultPointRad = zoomLevel < 5 ? 3 : zoomLevel < 7 ? 5 : zoomLevel < 9 ? 10 : 12;
        const defaultPointStroke = zoomLevel < 7 ? 1 : 2;

        if (feature.get('drawnPoint')) {
          //drawnPoint : we never show this point, it will be deleted directly after drawing
          return stylesArray;
        }

        if (isTraceDrawLayerHighlight && this._isDragging) {
          //we don't show any highlighted feature during dragging
          return stylesArray;
        }

        if (feature.get('startPoint') || feature.get('endPoint')) {
          const isStartPoint = feature.get('startPoint');
          stylesArray = [
            new Style({
              zIndex: feature.get('startPoint') ? 4 : 3,
              text: new Text({
                font: '22px "NGI-IGN_ICONS"',
                text: String.fromCharCode(0xe062),
                textAlign: 'center',
                offsetY: -17,
                fill: new Fill({
                  //ngi green and darkblue
                  color: isStartPoint ? ngiGreenBlueColor : ngiDarkBlueColor
                }),
                stroke: new Stroke({
                  color: [255, 255, 255, 1.0],
                  width: 4
                })
              }),
            }),
          ];
        } else if (feature.get('highlightCircle')) {
          stylesArray = [
            new Style({
              image: new Circle({
                fill: new Fill({
                  color: [224, 190, 12, 0.3]
                }),
                stroke: new Stroke({
                  color: [224, 190, 12, 1.0],
                  width: 1
                }),
                radius: feature.get('clickCircle') ? 25 : defaultPointRad + 15,
              })
            })
          ];
        } else if (feature.get('elevationProfile')) {
          stylesArray = [
            new Style({
              image: new Circle({
                radius: 6,
                stroke: new Stroke({
                  color: 'rgba(255, 255, 255, 1.0)',
                  width: 2
                }),
                fill: new Fill({
                  color: 'rgba(0, 108, 157, 1.0)'
                })
              }),
              zIndex: 3
            })
          ];
        } else if (feature.get('routePointHighlight')) {
          let color = feature.get('upForDeletion') ? 'rgba(255, 0, 0, 1.0)' : ngiBlueColor;

          stylesArray = [
            new Style({
              // image: new RegularShape({
              image: new Circle({
                //radius: 6,
                radius: 6,
                stroke: new Stroke({
                  color: 'rgba(255, 255, 255, 1.0)',
                  width: 1
                }),
                fill: new Fill({
                  color: color
                }),
                //points: 4,
                //angle: Math.PI / 4,
                /*
                angle: Math.PI / 2,
                rotation: -rotation 
                */
              }),
              zIndex: 2
            }),
          ];

        } else if (typeof feature.get('route_ref') === 'number') {
          if (!view_mode) {
            //after the first click we only have one routepoint, not yet a line
            //there will already be the startPoint marker, but startPoint/endPoint are ignored by the pointermove event 
            //we put a transparent point so that it can be found during the pointermove event 
            stylesArray = [
              new Style({
                image: new Circle({
                  radius: defaultPointRad,
                  stroke: new Stroke({
                    color: 'transparent',
                    width: 1,
                  }),
                  fill: new Fill({
                    color: 'transparent'
                  }),
                }),
                zIndex: 2
              }),
            ];
          }
        } else if (isSuggestion) {

          if (this._activePermalinkParam && (this._traceSuggestionsConfigService.configData().route_id_prefix + feature.get("route_id") + TraceSuggestionsComponent.findRouteIdSuffixForRequestedLanguage(this._traceSuggestionsConfigService.configData().route_id_suffix, this._translateService.getCurrentLang())) === this._activePermalinkParam) {
            return stylesArray;
          }

          //if we arrive here, then we don't need to check this boolean
          //this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked
          let centroidIcon = this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.map_icon_url ? this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.map_icon_url : '';
          let fontSize = zoomLevel < 2 ? 15 : zoomLevel < 3 ? 30 : zoomLevel < 8 ? 45 : 60;

          const labelStyle = feature.get("highlight") && feature.get("label") ?


            new Text({
              font: '600 14px "Source Sans Pro"',
              padding: [3, 7, 3, 7],
              text: feature.get('label'),
              textAlign: 'center',
              offsetY: -55,//(fontSize / 2) * -1,
              // offsetX: (fontSize / 2),
              overflow: true,
              fill: new Fill({
                color: ngiGreenBlueColor,
              }),
              backgroundFill: new Fill({
                color: 'rgba(255,255,255, 0.8)'
              }),

              // backgroundStroke: new Stroke({
              //   color: 'rgba(255, 255, 255, 1.0)',
              //   width: 2
              // }),
              placement: 'point'
            })

            : null;



          // const labelStyle = feature.get("highlight") && feature.get("label") ? new Text({
          //   font: '16px "Source Sans Pro"',
          //   text: feature.get('label'),
          //   textAlign: 'start',
          //   offsetY: (fontSize / 2) * -1,
          //   offsetX: (fontSize / 2),
          //   overflow: true,
          //   fill: new Fill({
          //     color: ngiDarkBlueColor,
          //   }),
          //   stroke: new Stroke({
          //     color: 'rgba(255, 255, 255, 1.0)',
          //     width: 2
          //   }),
          //   placement: 'point'
          // }) : null;



          if (centroidIcon && centroidIcon.indexOf("font://") === 0) {
            centroidIcon = centroidIcon.substring(7);
            centroidIcon = centroidIcon.replaceAll('-', '');
            let charCode = (centroidIcon in MarkerService.markerCodes) ? MarkerService.markerCodes[centroidIcon] : MarkerService.markerCodes['default'];

            //  let fontSize = zoomLevel < 2 ? 15 : zoomLevel < 3 ? 30 : zoomLevel < 8 ? 45 : 60;
            //  let fontSize = zoomLevel < 2 ? 15 : zoomLevel < 3 ? 30 : zoomLevel < 8 ? 30 : 45;
            //  let fontSize = 15 + (30/9)*(zoomLevel-1);
            let fontSize = zoomLevel < 2 ? 15 : zoomLevel < 8 ? 15 + (30 / 7) * (zoomLevel - 2) : 38;
            //console.log('zoomlevel, size', zoomLevel, fontSize);

            //zindex above index of line
            stylesArray.push(
              new Style({
                text: new Text({
                  font: (feature.get("highlight") ? fontSize + 3 : fontSize) + 'px "NGI-IGN_ICONS"', //'30px "NGI-IGN_ICONS"',
                  text: String.fromCharCode(charCode), //"⭓", //ue058
                  textAlign: 'center',
                  //offsetY:  (feature.get("highlight") ? -5 : 0),
                  fill: new Fill({
                    color: feature.get("highlight") ? ngiDarkGreenBlueColor : ngiGreenBlueColor
                  }),
                  stroke: new Stroke({
                    color: [255, 255, 255, 1.0],
                    width: 3
                  }),
                }),
                zIndex: 4
              }),
              new Style({

                zIndex: 4,
                text: labelStyle
              }),
              //add a little white circle behind the marker to cover the hole, the font software support only monochrome (in free mode) output  
              new Style({
                image: new Circle({
                  //displacement: [0, 19],
                  radius: fontSize / 2.5,//13,
                  fill: new Fill({
                    color: [255, 255, 255, 1.0]
                  }),
                }),
                zIndex: 2,
              })



              // new Style({
              //   image: new Circle({
              //     radius: (fontSize / 2) + 2,
              //     fill: new Fill({
              //       color: feature.get("highlight") ? [224, 190, 12, 0.3] : 'rgba(255, 255, 255, 0.5)'
              //     })
              //   }),
              //   zIndex: 2,
              //   text: labelStyle
              // }),
              // new Style({
              //   geometry: feature.getGeometry(),
              //   text: new Text({
              //     font: fontSize + 'px "NGI-IGN_ICONS"',
              //     text: String.fromCharCode(charCode),
              //     textAlign: 'center',
              //     fill: new Fill({
              //       color: ngiDarkBlueColor,
              //     })
              //   }),
              //   zIndex: 3
              // })





            );
          } else if (/^http(s)?\/\//.test(centroidIcon)) {
            stylesArray.push(
              new Style({
                image: new Circle({
                  radius: (fontSize / 2) + 2,
                  fill: new Fill({
                    color: feature.get("highlight") ? [224, 190, 12, 0.3] : 'rgba(255, 255, 255, 0.5)'
                  })
                }),
                zIndex: 2,
                text: labelStyle
              }),
              new Style({
                image: new Icon({
                  src: centroidIcon,
                  anchor: [0, 0],
                  width: fontSize
                }),
                zIndex: 3
              })
            );
          }
        } else {
          //waypoint or wayPointHighlight
          // let color = 'rgba(255, 91, 2, 1.0)';
          let color = ngiBlueGreenColor;
          let labelStyle = null;
          if (feature.get('wayPointHighlight')) {
            if (feature.get('editingType')) {
              color = feature.get('editingType') === 'delete' ? 'rgba(201, 0, 0, 1.0)' : 'rgb(139, 195, 74, 1.0)';
            } else {
              color = ngiBlueColor;
            }
          }
          //  else {
          // if (this._draggedWayPointIndex !== feature.get('wayPoint_ref')) {
          // labelStyle = this._getPoiLabelStyle(feature, zoomLevel, defaultPointRad, 'rgba(255, 255, 255, 1.0)');
          // }
          // }
          labelStyle = this._getPoiLabelStyle(feature, zoomLevel, defaultPointRad, 'rgba(255, 255, 255, 1.0)');
          stylesArray = [
            new Style({
              image: new Circle({
                radius: feature.get('wayPoint_ref') === 'startPoint' || feature.get('wayPoint_ref') === 'endPoint' ? 5 : defaultPointRad,
                fill: new Fill({
                  color: color
                }),
                stroke: new Stroke({
                  color: 'rgba(255, 255, 255, 1.0)',
                  width: defaultPointStroke,
                }),
                rotateWithView: true
              }),
              //when stacking point symbols with text, you need to give every point its own (increasing) zIndex if you want the text to stick to the symbol
              zIndex: typeof feature.get('wayPoint_ref') === 'number' ? feature.get('wayPoint_ref') : 2,
              text: labelStyle,
            }),
          ];


          if (this._draggedWayPointIndex === feature.get('wayPoint_ref')) {
            //dragged point stays on the same position, but different color
            stylesArray.push(
              new Style({
                geometry: feature.getGeometry(),
                image: new Circle({
                  radius: defaultPointRad,
                  fill: new Fill({
                    color: 'rgba(255, 255, 255, 0.5)'
                  })
                }),
                zIndex: 2
              }));
          }
        }

        break;
      }
      case 'MultiLineString':
      case 'LineString': {

        let lineWidth: number;
        let lineStrokeWidth: number;
        if (!view_mode) {
          lineWidth = zoomLevel < 5 ? 2 : zoomLevel < 8 ? 3 : zoomLevel < 9 ? 4 : 4;
          lineStrokeWidth = zoomLevel < 5 ? 4 : zoomLevel < 8 ? 6 : zoomLevel < 9 ? 8 : 8;
        } else {
          // lineWidth = zoomLevel < 5 ? 2 : zoomLevel < 8 ? 3 : zoomLevel < 9 ? 4 : 6;
          lineWidth = zoomLevel < 5 ? 2 :
            zoomLevel < 8 ? 3 + zoomLevel / 6 :
              zoomLevel < 9 ? 4 + zoomLevel / 6 :
                6 + zoomLevel / 2;
          lineStrokeWidth = zoomLevel < 5 ? 4 :
            zoomLevel < 8 ? 6 + zoomLevel / 6 :
              zoomLevel < 9 ? 6 + zoomLevel / 6 :
                10 + zoomLevel / 2;
        }
        
        const isRouteSegmentHighlight = feature.get('routeSegmentHighlight');
        const isRouteHeading = !isRouteSegmentHighlight && feature.get('isRouteHeading');

        /*
        blending by css
        */
        if (!isSuggestion) {
          if (this._traceDrawLayerLineElement && view_mode) {
            if (zoomLevel > 7) {
              this._traceDrawLayerLineElement.classList.add('multiply');
            }
            else {
              this._traceDrawLayerLineElement.classList.remove('multiply');
            }
          }
          else {
            this._traceDrawLayerLineElement.classList.remove('multiply');
          }
        } else if (this._activePermalinkParam && (this._traceSuggestionsConfigService.configData().route_id_prefix + feature.get("route_id") + TraceSuggestionsComponent.findRouteIdSuffixForRequestedLanguage(this._traceSuggestionsConfigService.configData().route_id_suffix, this._translateService.getCurrentLang())) === this._activePermalinkParam) {
          //extra test for suggestions
          //usually already returned above because of property isVisible
          return stylesArray;
        }

        if (!isArrowStyle) {
          //route highlight
          stylesArray = isRouteSegmentHighlight
            ? [
              new Style({
                stroke: new Stroke({
                  color: ngiOrangeColorVeryDark,
                  width: zoomLevel < 9 ? 1.3 : 1.7,
                  lineDash: [1, 3],
                  lineCap: 'square',
                }),
                text: zoomLevel < 8 || !feature.get('label') || feature.get('label').length === 0 ? null : new Text({
                  font: zoomLevel < 9 ? '13px "Source Sans Pro"' : '16px "Source Sans Pro"',
                  text: feature.get('label'),
                  textAlign: 'center',
                  offsetY: 10,
                  fill: new Fill({
                    color: ngiOrangeColorVeryDark,
                  }),
                  stroke: new Stroke({
                    color: 'rgba(255, 255, 255, 1.0)',
                    width: 2
                  }),
                  placement: 'line'
                }),
                //zIndex: 2,
                zIndex: 0
              }),
            ]
            : isRouteHeading
              //route heading & distance indicator
              ? this._getRouteHeadingStyle(feature, lineWidth, ngiBlueColor, zoomLevel)
              //  ? this._getRouteHeadingStyle(feature, lineWidth, ngiDarkGreenBlueColor, zoomLevel)
              :
              //route
              [
                //background stroke
                new Style({
                  stroke: new Stroke({
                    lineCap: 'round',
                    lineJoin: 'round',
                    // color: (zoomLevel > 8 && view_mode) ? (isSuggestion ? 'rgba(255, 255, 255, 1.0)' : ngiOrangeColorDarker) : 'rgba(255, 255, 255, 1.0)',
                    // color: (zoomLevel > 7 && view_mode) ? (isSuggestion ? 'rgba(255, 255, 255, 1.0)' : orangeColor ) : 'rgba(255, 255, 255, 1.0)',//ngiDarkGreenBlueColor
                    // color: (zoomLevel > 7 && view_mode) ? (isSuggestion ? 'rgba(255, 255, 255, 1.0)' : ngiOrangeColorVeryDark) : 'rgba(255, 255, 255, 1.0)',//ngiDarkGreenBlueColor
                    color: (zoomLevel > 7 && view_mode) ? (isSuggestion ? 'rgba(255, 255, 255, 1.0)' : ngiOrangeColorVeryDark) : 'rgba(255, 255, 255, 1.0)',//ngiDarkGreenBlueColor
                    width: (isSuggestion ? lineStrokeWidth + 3 : lineStrokeWidth)
                  }),
                  zIndex: 0,
                }),
                //foreground line
                new Style({
                  stroke: new Stroke({
                    lineCap: 'round',
                    lineJoin: 'round',
                    // color: zoomLevel < 8 ? (isSuggestion ? ngiDarkGreenBlueColor : ngiOrangeColorDarker) : (isSuggestion ? ngiDarkGreenBlueColor : orangeColor),//orangeColor
                    // color: zoomLevel < 8 ? (isSuggestion ? ngiDarkGreenBlueColor : ngiOrangeColorDarker) : (isSuggestion ? ngiDarkGreenBlueColor : 'rgba(225, 159, 134, 1.0)'),//orangeColor
                    color: zoomLevel < 8 ? (isSuggestion ? ngiDarkGreenBlueColor : ngiOrangeColorDarker) :  (isSuggestion ? ngiDarkGreenBlueColor :zoomLevel > 8 ? orangeColorLighter: orangeColor),//ngiGreenBlueColor
                    width: (isSuggestion ? lineWidth + 1 : lineWidth)
                  }),
                  zIndex: 1,
                }),
              ];
        }

        if (!isRouteSegmentHighlight && !isSuggestion) {
          let geometries: LineString[] = [];

          if (feature.getGeometry() instanceof MultiLineString) {
            geometries = (feature.getGeometry() as MultiLineString).getLineStrings()
          } else {
            geometries = [feature.getGeometry() as LineString];
          }

          if (!view_mode && (zoomLevel >= this._zoomLevelRoutePointMarkers) && !isPdfLayer && !isArrowStyle) {
            let geomCount = 0;
            geometries.forEach((geometry) => {
              let coordCount = 0;
              geometry.forEachSegment((start: Coordinate,
                end: Coordinate) => {
                /*
                const dx = end[0] - start[0];
                const dy = end[1] - start[1];
                const rotation = Math.atan2(dy, dx);
                */
                //add points 
                if (coordCount === 0) {
                  stylesArray.push(this._getLinePointStyle(start, lineWidth, true /*,rotation*/));
                }
                coordCount++;
                //show autodraw=true at a certain zoomlevel? 
                //e.g. (!this._traceRouteData.routes[geomCount].routePoints[coordCount].autoDraw || (zoomLevel >= this._maxAutoZoomForPrj))
                if (this._traceRouteData.routes[geomCount].routePoints[coordCount] && !this._traceRouteData.routes[geomCount].routePoints[coordCount].autoDraw) {
                  stylesArray.push(this._getLinePointStyle(end, lineWidth, !this._traceRouteData.routes[geomCount].routePoints[coordCount].autoDraw /*,rotation*/));
                }
              });
              geomCount++;
            });
          }
          //arrows
          //style is removed from arrow layer in modifystart method, so we don't need an extra check here for this._isDragging
          //if (isArrowStyle && (zoomLevel >= this._zoomLevelDirectionMarkers) && (!this._isDragging || this._draggedWayPointIndex > -1)) 
          if (isArrowStyle && (zoomLevel >= this._zoomLevelDirectionMarkers)) {
            //add direction markers
            const distBetweenArrows = zoomLevel >= 10 ? 150 : zoomLevel >= 8 ? 300 : zoomLevel >= 6 ? 600 : zoomLevel >= 4 ? 1200 : 2500;
            const arrowFontSize = zoomLevel < 8 ? 15 : zoomLevel < 9 ? 17 : 19;
            //the coordinate for the arrow will be in the middle of the arrow
            const arrowFontSizeToMapMeters = (arrowFontSize / 2) * METERS_PER_UNIT.m;
            const arrowStrokeWidth = zoomLevel < 8 ? 2 : zoomLevel < 9 ? 3 : 4;
            const mapRotation = this._mapService.getMap().getView().getRotation();

            let distSinceLastArrow = 0;
            let exceededDistance = 0;
            let rotation = 0;
            let previousRotation = 0;

            geometries.forEach((geometry) => {
              geometry.forEachSegment((
                start: Coordinate,
                end: Coordinate
              ) => {

                let remainingSegmentLength = new LineString([start, end]).getLength();

                previousRotation = rotation;
                rotation = Math.atan2(end[1] - start[1], end[0] - start[0]) - mapRotation;

                if (distSinceLastArrow > distBetweenArrows) {
                  exceededDistance += distSinceLastArrow - distBetweenArrows;
                  distSinceLastArrow = distBetweenArrows;
                }

                //test if next arrow can be put not too close to start and especially not too close to end of segment (retest at end of while loop)
                let canDrawArrowInNormalConditions = distBetweenArrows - distSinceLastArrow >= arrowFontSizeToMapMeters && remainingSegmentLength - (arrowFontSizeToMapMeters * 2) >= distBetweenArrows - distSinceLastArrow;

                while (canDrawArrowInNormalConditions || (distBetweenArrows === distSinceLastArrow && (remainingSegmentLength >= (arrowFontSizeToMapMeters * 2) || (exceededDistance >= distBetweenArrows / 5)))) {

                  //const d = canDrawArrowInNormalConditions ? (distBetweenArrows - distSinceLastArrow) / remainingSegmentLength : arrowFontSizeToMapMeters < remainingSegmentLength ? arrowFontSizeToMapMeters / remainingSegmentLength : 0;
                  //const d = (distBetweenArrows - distSinceLastArrow) / remainingSegmentLength;

                  let d: number;
                  if (canDrawArrowInNormalConditions) {
                    d = (distBetweenArrows - distSinceLastArrow) / remainingSegmentLength;
                  } else if (remainingSegmentLength >= (arrowFontSizeToMapMeters * 2)) {
                    d = arrowFontSizeToMapMeters / remainingSegmentLength;
                  } else {
                    //if we land here, it means several short segments were skipped
                    //we will put the arrow between two segments and use the average rotation of those two segments
                    d = 0;
                    const sumSin = (Math.sin(rotation) + Math.sin(previousRotation)) / 2;
                    const sumCos = (Math.cos(rotation) + Math.cos(previousRotation)) / 2;
                    rotation = Math.atan2(sumSin, sumCos);
                  }

                  exceededDistance = 0;
                  distSinceLastArrow = 0;

                  const x = start[0] + (end[0] - start[0]) * d;
                  const y = start[1] + (end[1] - start[1]) * d;

                  //arrow
                  stylesArray.push(
                    new Style({
                      geometry: new Point([x, y]),
                      text: new Text({
                        font: arrowFontSize + 'px "NGI-IGN_ICONS"',
                        text: String.fromCharCode(0xe092),//String.fromCharCode(0xf1df), //"⭓", //ue058 //f1df
                        textAlign: 'center',
                        offsetY: 1,
                        fill: new Fill({
                          color: ngiDarkBlueColor,
                        }),
                        stroke: new Stroke({
                          color: 'rgba(255, 255, 255, 1.0)',
                          width: arrowStrokeWidth
                        }),
                        rotation: -rotation
                      }),
                      zIndex: 2
                    })
                  );

                  start = [x, y]
                  remainingSegmentLength = new LineString([start, end]).getLength();
                  //test if next arrow can be put not too close to end of segment (at least one arrow is already added on the segment at this moment)  
                  canDrawArrowInNormalConditions = remainingSegmentLength - (arrowFontSizeToMapMeters * 2) >= distBetweenArrows
                }

                //exceeded distance between arrows is tested above
                distSinceLastArrow += remainingSegmentLength;
              });
            });
          }

          //part of line that is being updated by auto routing will have another styling
          if (!view_mode && this.isBusyWithAutoRouting()) {
            //in editing mode we only have one linestring, not multilinestring
            const routePoints = this._traceRouteData.routes[0].routePoints;
            if (routePoints.length > 1) {

              let fromToIndexes: number[];
              if (this._autoRoutingIndexAfterModify > -1) {
                fromToIndexes = this._findAutoDrawRoutePointsAroundIndex(routePoints, this._autoRoutingIndexAfterModify, true);
                if (!this._isAutoRoutingAfterDragging) {
                  //user removed a point
                  fromToIndexes[0] = this._autoRoutingIndexAfterModify;
                }
              } else {
                fromToIndexes = this._findAutoDrawRoutePointsAroundIndex(routePoints, routePoints.length - 1, true);
              }

              if (fromToIndexes[0] !== fromToIndexes[1]) {
                let lineCoordinates: Coordinate[] = [];

                for (let i = fromToIndexes[0]; i < fromToIndexes[1]; i++) {
                  lineCoordinates.push([routePoints[i].x, routePoints[i].y]);
                }

                lineCoordinates.push([routePoints[fromToIndexes[1]].x, routePoints[fromToIndexes[1]].y]);

                stylesArray.push(
                  new Style({
                    geometry: new LineString(lineCoordinates),
                    stroke: new Stroke({
                      color: 'rgba(255, 255, 255, 0.5)',
                      width: lineWidth,
                    }),
                    zIndex: 2
                  })
                );
              }
            }
          }

          //part of line that is being dragged will have another styling
          if (!view_mode && !this.isBusyWithAutoRouting() && this._isDragging && this._draggedRoutePointIndex > - 1) {
            //in editing mode we only have one linestring, not multilinestring
            const routePoints = this._traceRouteData.routes[0].routePoints;
            if (routePoints.length > 1) {
              const fromToIndexes = this._findAutoDrawRoutePointsAroundIndex(routePoints, this._draggedRoutePointIndex, true);

              if (this._draggedRoutePointAddition) {
                fromToIndexes[0] = this._draggedRoutePointIndex;
                //fromToIndexes[1] += 1;
              }

              if (fromToIndexes[0] !== fromToIndexes[1]) {
                let count = 0;
                let lineCoordinates: Coordinate[] = [];

                geometries[0].forEachSegment((start: Coordinate, end: Coordinate) => {
                  if (count === 0 && fromToIndexes[0] === 0) {
                    lineCoordinates.push(start);
                  }
                  count++;
                  if ((count > 0 && count === fromToIndexes[0]) || (count > fromToIndexes[0] && count <= fromToIndexes[1])) {
                    lineCoordinates.push(end);
                  }
                });

                stylesArray.push(
                  new Style({
                    geometry: new LineString(lineCoordinates),
                    stroke: new Stroke({
                      color: 'rgba(255, 255, 255, 0.5)',
                      width: lineWidth,
                    }),
                    zIndex: 2
                  })
                );
              }
            }
          }
        }

        break;
      }
    }

    return stylesArray;
  }

  private _getLinePointStyle(coordinate: Coordinate, lineWidth: number, isFixedPoint: boolean /*, rotation: number*/): Style {//
    // const zoomLevel = this._viewService.getZoom();
    // const defaultPointRad = zoomLevel < 5 ? 2 : zoomLevel < 8 ? 4 : 6;
    //points on line
    return new Style({
      geometry: new Point(coordinate),

      image: new Circle({
        // radius: defaultPointRad,
        radius: isFixedPoint ? lineWidth + 2 : lineWidth - 2,
        stroke: isFixedPoint ? new Stroke({
          color: 'rgba(255, 255, 255, 1.0)',
          width: 2
        }) : null,
        fill: new Fill({
          color: isFixedPoint ? 'rgba(255, 91, 2, 1.0)' : 'rgba(0, 0, 0, 1.0)'
        })
      }),
      zIndex: 2
    })
  }

  private _getPoiLabelStyle(
    feature: Feature,
    zoomLevel: number,
    pointRadius: number,
    fontColor: string
  ): Text {
    let textValueParam = feature.get('wayPoint_ref');

    if (typeof textValueParam !== 'number' || (zoomLevel < 7 && !this._traceDrawLayerPoint.get('pdflayer_forceWayPointLabel'))) {
      return null;
    }

    const offsetXParam = 0;
    // const offsetYParam = pointRadius * -1;
    const offsetYParam = 0;
    // const textSizeParam = zoomLevel < 6 ? 10 : zoomLevel < 8 ? 12 : 14;
    const textSizeParam = zoomLevel < 5 ? 8 : zoomLevel < 7 ? 10 : zoomLevel < 9 ? 12 : 14;
    const fontWeightParam = 'bold';
    const lineHeightParam = '1.2';

    let labelText: string;
    if (this.isInViewMode() && this._traceRouteData.wayPoints.length < 27 && TraceRouteData.getTraceTTSetting(this._traceRouteData.ttSettings, "wayPointSequenceNumber") === 'alpha') {
      labelText = TraceRouteData.convertWayPointIndexToLetter(textValueParam + 1) + '';
    } else {
      labelText = (textValueParam + 1) + '';
    }

    if (this._isDragging && this._draggedWayPointIndex > -1) {
      labelText = '(' + labelText + ')';
    }

    return new Text({
      placement: 'point',
      textAlign: 'center',
      textBaseline: 'middle',
      font:
        fontWeightParam +
        ' ' +
        textSizeParam +
        'px' +
        // 'px/' +
        // lineHeightParam +
        ' "Source Sans Pro"',
      text: labelText,
      fill: new Fill({ color: fontColor }),
      // stroke: new Stroke({ color: '#ffffff', width: 2 }),
      backgroundFill: new Fill({ color: 'transparent' }),
      backgroundStroke: null,
      offsetX: offsetXParam,
      offsetY: offsetYParam,
      /*45%*/
      maxAngle: 0.7853981633974483,
      rotation: 0.0,
      overflow: true,
      padding: [0, 0, 0, 0],
    });
  }


  private _getRouteHeadingStyle(feature: any, lineWidth: number, color: string, zoomLevel: number): Style[] {
    const lineString = feature.getGeometry() as LineString;
    const endPoint: Coordinate = lineString.getCoordinates()[feature.getGeometry().getCoordinates().length - 1]; // last punt
    const startPoint: Coordinate = lineString.getCoordinates()[0]; // last punt
    const beforeEndPoint: Coordinate = lineString.getCoordinates().length >= 2 ? feature.getGeometry().getCoordinates()[feature.getGeometry().getCoordinates().length - 2] : null; // before last punt
    //calculate labelpoint offsets based on line angle
    const labelPoint = lineString.getCoordinateAt(0.7);
    const p1 = lineString.getCoordinateAt(0.85);
    const p2 = lineString.getCoordinateAt(0.55);
    // angle of the line segment at 'midpoint'   
    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const angle = Math.atan2(dy, dx);
    // perpendicular line _|_
    const perpX = -Math.sin(angle);
    const perpY = Math.cos(angle);
    // const side = perpY > 0 ? -1 : 1;
    const side = perpY >= 0 ? -1 : 1;
    const offsetPx = zoomLevel > 8 ? 69 : 53;
    const offsetX = side * perpX * offsetPx;
    const offsetY = -side * perpY * offsetPx;
    // console.log('perpX perpY', perpX, perpY);
    let rotationArrow: number;

    //     const svg = `
    //   <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 24 24">
    //     <path d="M12,2 L22,22 L12,17 L2,22 Z" fill="${color}" stroke="${color}" stroke-width="4" stroke-linejoin="round"/>
    //   </svg>
    // `; 

    if (beforeEndPoint && endPoint) {
      const dx = endPoint[0] - beforeEndPoint[0];
      const dy = endPoint[1] - beforeEndPoint[1];
      rotationArrow = Math.atan2(dy, dx);
    }

    let styles: Style[] = null;

    //if you change the test here, zoomlevel 7, also check the tests in method _getRouteHeading
    if (zoomLevel <= 7) {
      styles = [
        // new Style({
        //   geometry: new Point(feature.getGeometry().getCoordinates()[0]),
        //   image: new Circle({
        //     radius: lineWidth + 2,
        //     fill: new Fill({
        //       color: 'rgba(255, 255, 255, 1.0)'
        //     })
        //   }),
        //   zIndex: 3
        // }),
        // new Style({
        //   geometry: new Point(feature.getGeometry().getCoordinates()[0]),
        //   image: new Circle({
        //     radius: lineWidth,
        //     fill: new Fill({ color: color })
        //   }),
        //   zIndex: 4,
        //   text: new Text({
        //     font: '16px "Source Sans Pro"',
        //     text: feature.get('label'),
        //     textAlign: 'start',
        //     offsetY: lineWidth * -3,
        //     offsetX: lineWidth * 3,
        //     overflow: true,
        //     fill: new Fill({
        //       color: color,
        //     }),
        //     stroke: new Stroke({
        //       color: 'rgba(255, 255, 255, 1.0)',
        //       width: 2
        //     }),
        //     placement: 'point'
        //   })
        // }),
      ];
    } else if (beforeEndPoint && endPoint) {
      styles = [
        //background circlepoint white
        new Style({
          geometry: new Point(feature.getGeometry().getCoordinates()[0]),
          image: new Circle({
            radius: lineWidth + 3,//lineWidth * 2,
            fill: new Fill({
              color: 'rgba(255, 255, 255, 1.0)'
            })
          }),
          zIndex: 3
        }),
        //arrow background
        new Style({
          geometry: new Point(endPoint),
          image: new RegularShape({
            stroke: new Stroke({
              lineJoin: 'round',
              color: 'rgba(255, 255, 255, 1.0)',
              width: lineWidth + 6,
            }),
            points: 3,
            radius: lineWidth * 1.4,
            rotateWithView: true,
            rotation: -rotationArrow,
            angle: Math.PI / 2
          }),
          zIndex: 3
        }),
        // //background linestring
        new Style({
          stroke: new Stroke({
            color: 'rgba(255, 255, 255, 1.0)',
            width: lineWidth + 6,//+2
          }),
          zIndex: 4,
          /* label along the line */
          // text: new Text({
          //   font: '26px "Source Sans Pro"',
          //   text: feature.get('label'),
          //   textAlign: 'left',//'start',
          //   // offsetY: 0,
          //   // offsetX: 0,
          //    overflow: true,
          //   //  maxAngle: 1,  
          //    keepUpright: true,
          //   placement:  'line',            
          //   fill: new Fill({
          //     color: color,
          //   }),
          //   stroke: new Stroke({
          //     color: 'rgba(255, 255, 255, 1.0)',
          //     width: 8
          //   }),
          // })
        }),
        //foreground line 
        new Style({
          stroke: new Stroke({
            color: color,
            width: lineWidth,
          }),
          zIndex: 8
        }),
        //label placement
        new Style({
          geometry: new Point(labelPoint),
          // test to show position of labelpoint
          // image: new Circle({
          //   radius: 4,
          //   fill: new Fill({ color: 'black' })
          // }),
          zIndex: 18,
          text: new Text({
            font: '400 16px Source Sans Pro',
            text: feature.get('label'),
            rotation: 0,
            offsetX: offsetX,
            offsetY: offsetY,
            textAlign: 'center',
            textBaseline: 'middle',
            backgroundStroke: new Stroke({
              color: color,
              width: 5,
              // offset:6,
              lineJoin: 'round'
            }),
            backgroundFill: new Fill({
              color: color,
            }),
            padding: [2, 2, 0, 4],
            // overflow: true,
            fill: new Fill({
              color: 'white',
            }),
            placement: 'point'
          })

        }),
        //foreground circle
        new Style({
          geometry: new Point(feature.getGeometry().getCoordinates()[0]),
          image: new Circle({
            radius: lineWidth,
            fill: new Fill({ color: color })
          }),
          zIndex: 8,
          /* label at starting point instead of along the line */
          // text: new Text({
          //   font: '16px "Source Sans Pro"',
          //   text: feature.get('label'),
          //   textAlign: 'start',
          //   offsetY: 90,//lineWidth,
          //   offsetX: 90,//lineWidth,
          //   overflow: true,
          //   fill: new Fill({
          //     color: color,
          //   }),
          //   stroke: new Stroke({
          //     color: 'rgba(255, 255, 255, 1.0)',
          //     width: 2
          //   }),
          //   placement: 'point'
          // })
        }),
        //arrow foreground

        // new Style({
        //   geometry: new Point(endPoint),
        //   image: new Icon({
        //     src: 'data:image/svg+xml;utf8,' + encodeURIComponent(svg),
        //     anchor: [0.5, 0.5], // Center of the icon
        //     rotateWithView: true,
        //     rotation: rotationArrow, // Adjust if your SVG points a different default direction
        //     scale: 1 // Adjust size using scale if needed
        //   }),
        //   zIndex: 4
        // })
        new Style({
          geometry: new Point(endPoint),
          image: new RegularShape({
            stroke: new Stroke({
              lineJoin: 'round',
              color: color,
              width: lineWidth,
            }),
            fill: new Fill({ color: color }),
            points: 3,
            // radius: (lineWidth * 2) - 4,
            radius: lineWidth * 1.4,
            rotateWithView: true,
            rotation: -rotationArrow,
            angle: Math.PI / 2
          }),
          zIndex: 4
        })
      ];
    }
    return styles;
  }

  private _getTraceModifiableStyle(): Style {

    if (this.autoRouting()) {
      return new Style({
        stroke: new Stroke({
          color: 'rgba(255, 91, 2, 1.0)',
          width: 4,
          lineDash: [3, 10],
          lineDashOffset: 10,
          lineCap: 'square'
        })
      });
    } else {
      return new Style({
        stroke: new Stroke({
          color: 'rgba(255, 91, 2, 1.0)',
          width: 4,
          lineCap: 'square'
        })
      });

    }
  }

  private _getDrawStyle(): Style {
    //no need for zIndex: vector layer > drawInteraction layer > modifyInteraction layer

    if (this._browserSniffer.isTouchDevice && this._browserSniffer.isMobileOS) {
      //we don't show a point, because it's not useful
      //a 'touchstart' event would already trigger the rendering of this point on the map
      return null;
    }

    if (this._editingModeType() === 'Point') {
      return new Style({
        image: new Circle({
          radius: 6,
          stroke: new Stroke({
            color: 'rgba(255, 255, 255, 1.0)',
            width: 1
          }),
          fill: new Fill({
            color: 'rgba(6, 138, 155, 1.0)'//'rgba(255, 91, 2, 1.0)'
          })
        }),
        zIndex: 0
      })
    } else {
      return new Style({
        image: new Circle({
          // image: new RegularShape({
          radius: 6,
          stroke: new Stroke({
            color: 'rgba(255, 255, 255, 1.0)',
            width: 1
          }),
          fill: new Fill({ color: 'rgba(255, 91, 2, 1.0)' }),
          //points: 4,
          //angle: Math.PI / 4,
        }),
        zIndex: 0
      })
    }
  }

  private _getModifyStyle(feature: Feature, resolution: number): Style[] {
    //no need for zIndex: vector layer > drawInteraction layer > modifyInteraction layer
    let labelStyle: Text = null;
    const isWaypoint = typeof feature.get('wayPoint_ref') === 'number';
    const zoomLevel = this._mapService.getCurrentMap().getView().getZoomForResolution(resolution);
    const defaultPointRad = zoomLevel < 5 ? 3 : zoomLevel < 7 ? 5 : zoomLevel < 9 ? 10 : 12;
    //typeof feature.get('route_ref')
    labelStyle = this._getPoiLabelStyle(feature, zoomLevel, defaultPointRad, 'rgba(255, 255, 255, 1.0)');
    // if (this._draggedWayPointIndex !== -1) {
    //   labelStyle = new Text({
    //     placement: 'point',
    //     textAlign: 'center',
    //     textBaseline: 'bottom',
    //     font: 'bold 14px/1.2 "Source Sans Pro"',
    //     text: '(' + (this._draggedWayPointIndex + 1) + ')',
    //     fill: new Fill({ color: 'rgba(0, 108, 157, 1.0)' }),
    //     stroke: new Stroke({ color: '#ffffff', width: 2 }),
    //     backgroundFill: new Fill({ color: 'transparent' }),
    //     backgroundStroke: null,
    //     offsetX: 0,
    //     offsetY: -10,
    //     /*45%*/
    //     maxAngle: 0.7853981633974483,
    //     rotation: 0.0,
    //     overflow: true,
    //     padding: [0, 0, 0, 0],
    //   });
    // }

    return [new Style({
      image: new Circle({
        radius: isWaypoint ? defaultPointRad : 6,
        stroke: new Stroke({
          color: 'rgba(255, 255, 255, 1.0)',
          width: 2
        }),
        fill: new Fill({
          //ol blue
          // color: 'rgba(0, 153, 255, 1.0)'
          //ngi blue
          color: 'rgba(0, 108, 157, 1.0)'
        })
      }),
      text: labelStyle,
      zIndex: 1
    })/*, new Style({
      image: new Icon({
        src: './assets/images/markers/cursor-move.png',
        scale: 1,
        size: [24, 24],
        anchorXUnits: 'pixels',
        anchorYUnits: 'pixels',
        anchor: [40, 12],
        opacity: 0.7,
        offset: [0, 0],
        rotation: 0.0,
      }),
      zIndex: 1
    })*/
    ];
  }

  /* ************************************************************************************************************************************************************************************
   * vector layer methods
   ************************************************************************************************************************************************************************************ */

  getDrawLayerId(layerType: 'point' | 'line' | 'arrow') {
    return this._isActive() ? (layerType === 'point' ? this._traceDrawLayerPoint.get("customId") : layerType === 'line' ? this._traceDrawLayerLineString.get("customId") : this._traceDrawLayerArrows.get("customId")) : null;
  }

  /**
   * total length in meters of linestrings on the map
   */
  getRouteLength() {
    let routeLength = 0;

    if (this._isActive()) {
      this._traceDrawLayerLineString.getSource().getFeatures().forEach((feature) => {
        if (feature.getGeometry() instanceof LineString || feature.getGeometry() instanceof MultiLineString) {
          const linestrings = feature.getGeometry() instanceof LineString ? [(feature.getGeometry() as LineString)] : (feature.getGeometry() as MultiLineString).getLineStrings();
          linestrings.forEach((linestring: LineString) => {
            routeLength += linestring.getLength();
          });
        }
      });
    }
    return routeLength;
  }

  centerOrZoomInOut(point: Point, isWayPoint: boolean) {
    if (point) {
      if (!this._isWayPointInViewPort(point) || !new OlCircle(this._viewService.getCenter(), 50).intersectsCoordinate(point.getCoordinates())) {
        //we want it a little bit faster then setCenterAnimate
        //this._viewService.setCenterAnimate(point.getCoordinates());
        this._mapService.getCurrentMap().getView().animate(
          /*
          { 
            zoom: this._viewService.getZoom(), 
            easing: easeIn, 
            duration: 300 
          },
          */
          {
            center: point.getCoordinates(),
            easing: easeIn,
            duration: 300

          },
          () => {
            this._toggleEventService.toggleEvent({
              forwardedFor: 'trace-zoomto',
              eventName: ToggleEventNameType.INACTIVE
            });
          });
      } else {
        if (this._viewService.getZoom() === this._maxAutoZoomForPrj) {
          //this normally happens when a user clicked two times on the same point in right panel, zoom out again
          //also OK: fit this._traceDrawLayer.getSource().getExtent() with padding: [10, 10, 10, 10]
          //maxZoom: this._maxAutoZoomForPrj - 1 otherwise it's possible that nothing happens
          this._mapService
            .getCurrentMap()
            .getView()
            .fit(olExtent.buffer(point.getExtent(), 1500), {
              size: this._mapService.getCurrentMap().getSize(),
              padding: [0, 0, 0, 0],
              duration: 500,
              maxZoom: this._maxAutoZoomForPrj - 1,
              callback: () => {
                this._toggleEventService.toggleEvent({
                  forwardedFor: 'trace-zoomto',
                  eventName: ToggleEventNameType.INACTIVE
                });
              }
            });
        } else {
          this._mapService
            .getCurrentMap()
            .getView()
            .fit(point.getExtent(), {
              size: this._mapService.getCurrentMap().getSize(),
              padding: [0, 0, 0, 0],
              duration: 500,
              maxZoom: this._maxAutoZoomForPrj,
              callback: () => {
                this._toggleEventService.toggleEvent({
                  forwardedFor: 'trace-zoomto',
                  eventName: ToggleEventNameType.INACTIVE
                });
              }
            });
        }
      }

      window.setTimeout(() => {
      
        
        const traceClickAction = new OlCircle(this._viewService.getCenter(), 50).intersectsCoordinate(point.getCoordinates()) ?
         this._viewService.getZoom() >= this._maxAutoZoomForPrj ?
          TraceClickActionType.ZOOMOUT : 
          TraceClickActionType.ZOOMIN :
           TraceClickActionType.CENTER;
        if (isWayPoint) {
          this.clickActionWayPoint.set(traceClickAction);
        } else {
        }
      }, 550);
    }
  }

  zoomToWayPoint(wayPointIndex: number | 'startPoint' | 'endPoint', event: Event, profile?: boolean) {
    if (!this._isActive()) {
      return;
    }

    /*
    if (this._popupService.isPopupAlreadyOnMap()) {
      this._popupService.hide();
    }
    */

    const target = event && event.target ? event.target as Element : null;
    if (target && target.tagName && target.tagName.toLowerCase() === "a") {
      return;
    }

    this._toggleEventService.toggleEvent({
      forwardedFor: 'trace-zoomto',
      eventName: ToggleEventNameType.ACTIVE
    });

    if (this._popupService.isPopupAlreadyOnMap()) {
      this._popupService.hide();
    }

    if (this._browserSniffer.isPhone() && !profile) {

      this._viewService.zoomAndPan(
        this._getWayPointAsPoint(wayPointIndex).getCoordinates()[0],
        this._getWayPointAsPoint(wayPointIndex).getCoordinates()[1],
        this._maxAutoZoomForPrj,
        () => {
          this._toggleEventService.toggleEvent({
            forwardedFor: 'trace-zoomto',
            eventName: ToggleEventNameType.INACTIVE
          });
        }
      );

      this.closeViewDialog();
      return;
    }


    this.centerOrZoomInOut(this._getWayPointAsPoint(wayPointIndex), true);
  }

  zoomToRouteExtent() {

    this._toggleEventService.toggleEvent({
      forwardedFor: 'trace-zoomto',
      eventName: ToggleEventNameType.ACTIVE
    });

    let emptyExtent = true;
    const totalExtent = olExtent.createEmpty();

    if (this._traceDrawLayerLineString.getSource().getFeatures().length > 0) {
      olExtent.extend(totalExtent, this._traceDrawLayerLineString.getSource().getExtent());
      emptyExtent = false;
    }

    if (this._traceDrawLayerPoint.getSource().getFeatures().length > 0) {
      olExtent.extend(totalExtent, this._traceDrawLayerPoint.getSource().getExtent());
      emptyExtent = false;
    }


    if (this._browserSniffer.isPhone()) {
      this.closeViewDialog();
    }

    if (!emptyExtent) {
      if (this._popupService.isPopupAlreadyOnMap()) {
        this._popupService.hide();
      }

      this._viewService.flyToFeatureExtent(totalExtent, 0,
        () => {
          this._toggleEventService.toggleEvent({
            forwardedFor: 'trace-zoomto',
            eventName: ToggleEventNameType.INACTIVE
          });
        }
      );
    }
  }

  addWayPointHighlight(
    wayPointIndex: number | 'startPoint' | 'endPoint',
    isPointerEventOrEditingEvent: boolean | 'edit' | 'delete'
  ) {
    if (!this._isActive()) {
      return;
    }
    const point = this._getWayPointAsPoint(wayPointIndex);


    if (point) {
      //for the touch version we need to call also removeRoutePointHighlight
      if (this._inEditingMode()) {
        this.removeRoutePointHighlight();
      }
      this.removeWayPointHighlight();

      this._traceDrawLayerHighlight.setOpacity(0);

      if (typeof isPointerEventOrEditingEvent !== 'boolean' || !isPointerEventOrEditingEvent) {
        this._addHighlightCircle(point.getCoordinates(), false);
      }
      const wayPointHighlighFeature = new Feature();
      wayPointHighlighFeature.setGeometry(point);
      wayPointHighlighFeature.set('wayPointHighlight', true);
      wayPointHighlighFeature.set('wayPoint_ref', wayPointIndex);

      if (typeof wayPointIndex === 'string') {
        this._updateEndPointZindex(wayPointIndex === "endPoint");
        wayPointHighlighFeature.set(wayPointIndex, true);
      }
      if (isPointerEventOrEditingEvent && typeof isPointerEventOrEditingEvent === 'string') {
        wayPointHighlighFeature.set('editingType', isPointerEventOrEditingEvent);
        this.highlightedWayPointUpForEditing.set(isPointerEventOrEditingEvent);
      }

      if (!isPointerEventOrEditingEvent) {
        this.clickActionWayPoint.set(new OlCircle(this._viewService.getCenter(), 50).intersectsCoordinate(point.getCoordinates()) ? this._viewService.getZoom() >= this._maxAutoZoomForPrj ? TraceClickActionType.ZOOMOUT : TraceClickActionType.ZOOMIN : TraceClickActionType.CENTER);
      }

      this._traceDrawLayerHighlight
        .getSource()
        .addFeature(wayPointHighlighFeature);

      this.highlightedWayPoint.set(wayPointIndex);

      //highlight closest route segment (only in view mode or when editing is paused)
      if (typeof wayPointIndex === 'number' && (!this._inEditingMode() || this._editingModePaused())) {

        const wayPointAsPointFeauture = TraceRouteData.convertTraceWayPointToPointFeature(this._traceRouteData.wayPoints[wayPointIndex]);
        const closestRoutePoint = wayPointAsPointFeauture ? TraceRouteData.getClosestRoutePointForMapPoint(wayPointAsPointFeauture.getGeometry() as Point, this._traceRouteData.routes, this._viewService.getProjection().getCode()) : null;

        if (closestRoutePoint) {
          const routeSegmentFeature = new Feature();
          const geom = new LineString([(wayPointAsPointFeauture.getGeometry() as Point).getCoordinates(), closestRoutePoint.getCoordinates()]);
          routeSegmentFeature.setGeometry(geom);
          routeSegmentFeature.set('routeSegmentHighlight', true);
          routeSegmentFeature.set('label', geom.getLength() >= 10 ? TraceRouteData.formatRouteLength(geom.getLength(), false, '') : '');
          this._traceDrawLayerHighlight
            .getSource()
            .addFeature(routeSegmentFeature);
        }
      }

      /*
      automatic centering when not in viewport? 
      if (typeof isPointerEventOrEditingEvent !== 'boolean' || !isPointerEventOrEditingEvent) {
        if (!this._isWayPointInViewPort(point)) {
          //we don't want a center event if the user hovers an element just for a few milliseconds
          const lastHighlightedWayPoint = this.highlightedWayPoint;
          window.setTimeout(() => {
            if (lastHighlightedWayPoint === this.highlightedWayPoint) {
              this._viewService.setCenterAnimate(point.getCoordinates());
            }
          }, 1000);
        }
      }
      */

      this.fadeInLayer(this._traceDrawLayerHighlight, true, true, 1, 300);
    } else {
      this.highlightedWayPoint.set(-1);
      this.highlightedWayPointUpForEditing.set(null);
      this.clickActionWayPoint.set(TraceClickActionType.NONE);
    }
  }

  removeWayPointHighlight() {
    if (!this._isActive()) {
      return;
    }
    this._traceDrawLayerHighlight.getSource().clear();
    /*
    let features = this._traceDrawLayerHighlight.getSource().getFeatures();
    features.forEach((feature) => {
      if (feature.get('wayPointHighlight') || feature.get('routeSegmentHighlight')) {
        this._traceDrawLayerHighlight.getSource().removeFeature(feature);
      }
    });
    */
    this.highlightedWayPoint.set(-1);
    this.highlightedWayPointUpForEditing.set(null);
    this.clickActionWayPoint.set(TraceClickActionType.NONE);
  }


  zoomToRoutePoint(routeIndex: number, routePointIndex: number) {
    if (!this._isActive()) {
      return;
    }

    this._toggleEventService.toggleEvent({
      forwardedFor: 'trace-zoomto',
      eventName: ToggleEventNameType.ACTIVE
    });

    if (this._popupService.isPopupAlreadyOnMap()) {
      this._popupService.hide();
    }

    this.centerOrZoomInOut(this._getRoutePointAsPoint(routeIndex, routePointIndex), false);
  }
  generalInfoUpForDeletion: boolean;
  generalInfoUpForEditing: boolean;
  addGeneralInfoHighlight(type) {
    this.generalInfoUpForDeletion = (type === 'delete') ? true : false;
    this.generalInfoUpForEditing = (type === 'edit') ? true : false;
  }
  removeGeneralInfoHighlight() {
    this.generalInfoUpForDeletion = this.generalInfoUpForEditing = false;
  }

  addRoutePointHighlight(routeIndex: number, routePointIndex: number, isPointerEventOrEditingEvent: boolean | 'delete') {
    if (!this._isActive()) {
      return;
    }
    const point = this._getRoutePointAsPoint(routeIndex, routePointIndex);
    if (point) {

      const isStartPoint = routeIndex === 0 && routePointIndex === 0;
      const isEndPoint = !isStartPoint && this._traceRouteData.routes.length === (routeIndex + 1) && routePointIndex === (this._traceRouteData.routes[routeIndex].routePoints.length - 1);
      if (isStartPoint || isEndPoint) {
        this._updateEndPointZindex(isEndPoint);
      }


      //for the touch version we need to call also removeWayPointHighlight
      this.removeWayPointHighlight();
      this.removeRoutePointHighlight();

      this._traceDrawLayerHighlight.setOpacity(0);

      if (typeof isPointerEventOrEditingEvent !== 'boolean' || !isPointerEventOrEditingEvent) {
        this._addHighlightCircle(point.getCoordinates(), false);
      }

      const wayPointHighlighFeature = new Feature();
      wayPointHighlighFeature.setGeometry(point);
      wayPointHighlighFeature.set('routePointHighlight', true);
      wayPointHighlighFeature.set('upForDeletion', typeof isPointerEventOrEditingEvent === 'string' && isPointerEventOrEditingEvent === 'delete');
      this._traceDrawLayerHighlight
        .getSource()
        .addFeature(wayPointHighlighFeature);

      this.highlightedRoutePoint.set(routeIndex + '_' + routePointIndex);
      this.highlightedRoutePointUpForDeletion.set(typeof isPointerEventOrEditingEvent === 'string' && isPointerEventOrEditingEvent === 'delete');

      if (!isPointerEventOrEditingEvent) {
        this.clickActionRoutePoint.set(new OlCircle(this._viewService.getCenter(), 50).intersectsCoordinate(point.getCoordinates()) ? this._viewService.getZoom() >= this._maxAutoZoomForPrj ? TraceClickActionType.ZOOMOUT : TraceClickActionType.ZOOMIN : TraceClickActionType.CENTER);
      }

      /*
      automatic centering when not in viewport? 
      if (!this._isWayPointInViewPort(point)) {
        //we don't want a center event if the user hovers an element just for a few milliseconds
        const lastHighlightedRoutePoint = this.highlightedRoutePoint();
        window.setTimeout(() => {
          if (lastHighlightedRoutePoint === this.highlightedRoutePoint()) {
            this._viewService.setCenterAnimate(point.getCoordinates());
          }
        }, 1000);
      }
      */

      this.fadeInLayer(this._traceDrawLayerHighlight, true, true, 1, 100);
    } else {
      this.highlightedRoutePoint.set('');
      this.highlightedRoutePointUpForDeletion.set(false);
      this.clickActionRoutePoint.set(TraceClickActionType.NONE);
    }
  }

  private _updateEndPointZindex(moveEndPointToTop: boolean) {
    //change z index of endppoint if start and endpoint are in same spot
    let endpoint: any = this._traceDrawLayerPoint.getSource().getFeatureById('endPoint');
    //it's possible that there is no endpoint yet
    if (endpoint) {
      if (moveEndPointToTop) {
        let style: any = this._getTraceStyle(endpoint, 10, false)[0];
        style.setZIndex(11);
        endpoint.setStyle(style);
      } else {
        endpoint.setStyle();
      }
    }
  }

  removeRoutePointHighlight() {
    if (!this._isActive()) {
      return;
    }

    this._traceDrawLayerHighlight.getSource().clear();
    this.highlightedRoutePoint.set('');
    this.highlightedRoutePointUpForDeletion.set(false);
    this.clickActionRoutePoint.set(TraceClickActionType.NONE);
  }

  /**
   * 
   * @param e 
   * @param mapId optional, if not present we will assume the user is interacting with the main map (split maps are usually only used in view mode)
   * @returns 
   */
  private _handleMapInteractionEvent(e: MapBrowserEvent<any> | TraceDummyMapInteractionEvent, mapId?: string) {

    if (this.checkForExternalMapEditingInteractions()) {
      //e.g. measure tool is open
      return;
    }

    mapId = mapId ? mapId : 'main';

    switch (e.type) {
      case 'click':
      case 'dummy_click': {
        if (this._handlingClickEvent) {
          return;
        }
        this._handlingClickEvent = true;
        const isDummyClick = e.type === 'dummy_click';
        const handleElevationOnClick = !isDummyClick && this._browserSniffer.isTouchDevice; /* && this._browserSniffer.isMobileOS*/

        if (this.isInEditingMode()) {
          this._editingTooltip.setPosition(undefined);
          this._editingTooltip.setText('');
        }

        //dummy map event, so that this.highlightedRoutePoint/this.highlightedWayPoint is set(if possible), because on mobile devices were we don't have a pointermove event
        //we also want the line between a poi and the route to appear together with the distance
        this._handleMapInteractionEvent({ type: 'dummy_pointermove', pixel: e.pixel }, mapId);

        if (handleElevationOnClick && this._elevationProfilePointOnMap) {
          this._removeElevationProfilePointFromMap();
          this.removeElevationProfileTooltip();
        }

        this._handleClickAtPixel(e.pixel, mapId);

        if (handleElevationOnClick && this.hasElevationProfilePointsData()) {
          this._searchForElevationProfilePoint(this._mapService.getMap(mapId).getCoordinateFromPixel(e.pixel));
        }

        window.setTimeout(() => {
          this._handlingClickEvent = false;
        }, 500);

        break;
      }
      case 'pointermove':
      case 'dummy_pointermove': {
        //we don't remove previous highlights yet because maybe it will still be the same feature

        if (this._drawModifyToolsPaused) {
          //we don't want routepoint highlights & tooltip during this pause
          return;
        }

        const isDummyPointerMove = e.type === 'dummy_pointermove';
        const pixelCoordinate = this._mapService.getMap(mapId).getCoordinateFromPixel(e.pixel);//(this._mapService.getMap(mapId) as PluggableMap)

        let oldTooltipText = !isDummyPointerMove && this.isInEditingMode() && this._editingTooltip.getText() && this._editingTooltip.getText().length > 0 ? this._editingTooltip.getText() : null;

        if (this._isDragging && !isDummyPointerMove) {

          if (oldTooltipText) {
            this._editingTooltip.setPosition(undefined);
            this._editingTooltip.setText('');
            oldTooltipText = null;
          }

          if (this._draggedRoutePointIndex > -1) {
            this._traceDrawLayerModifiableHighlight.getSource().clear();
            const routePoints = this._traceRouteData.routes[0].routePoints;
            if (routePoints.length > 1) {
              const fromToIndexes = this._findAutoDrawRoutePointsAroundIndex(routePoints, this._draggedRoutePointIndex, true);

              if (this._draggedRoutePointAddition) {
                fromToIndexes[0] = this._draggedRoutePointIndex;
                //fromToIndexes[1] += 1;
              }

              let point1: Point = null;
              let point2: Point = null;
              let onlyOnePoint = false;

              if (fromToIndexes[0] !== fromToIndexes[1]) {
                if (!this._draggedRoutePointAddition && this._draggedRoutePointIndex === 0) {
                  point1 = this._getRoutePointAsPoint(0, fromToIndexes[1]);
                  onlyOnePoint = true;
                } else if (!this._draggedRoutePointAddition && this._draggedRoutePointIndex === routePoints.length - 1) {
                  point1 = this._getRoutePointAsPoint(0, fromToIndexes[0]);
                  onlyOnePoint = true;
                } else {
                  point1 = this._getRoutePointAsPoint(0, fromToIndexes[0]);
                  point2 = this._getRoutePointAsPoint(0, fromToIndexes[1]);
                }

                if ((onlyOnePoint && point1) || (point1 && point2)) {
                  this._traceDrawLayerModifiableHighlight.getSource().addFeature(new Feature({
                    geometry: new LineString([point1.getCoordinates(), pixelCoordinate])
                  }));
                }

                if (point2) {
                  this._traceDrawLayerModifiableHighlight.getSource().addFeature(new Feature({
                    geometry: new LineString([point2.getCoordinates(), pixelCoordinate])
                  }));
                }
              }
            }
          }

          return;
        }

        if (!isDummyPointerMove && this._elevationProfilePointOnMap) {
          this._removeElevationProfilePointFromMap();
          this.removeElevationProfileTooltip();
        }

        const searchForElevationProfilePoint = !isDummyPointerMove && this.hasElevationProfilePointsData();

        //look for waypoints when: not editing, editing paused, editingmodetype point
        let features = !this.isInEditingMode() || this.isEditingModePaused() || this._editingModeType() === 'Point' ? this._mapService
          .getCurrentMap()
          .getFeaturesAtPixel(e.pixel, {
            hitTolerance: this._snapAndSearchPixelTolerance,
            layerFilter: (l) => {
              return l.get('customId') === 'traceDrawLayerPoint';
            },
          }) : [];

        //first we will look for waypoints
        let foundFeatureForHighlight = false;
        let foundFeatureForCursor = false;
        let newToolTipText: string = null;

        features.every((feature) => {
          const wayPointRef = feature.get('wayPoint_ref');
          if (typeof wayPointRef === 'number') {
            foundFeatureForHighlight = true;
            if (wayPointRef !== this.highlightedWayPoint()) {
              this.removeWayPointHighlight();
              this.addWayPointHighlight(wayPointRef, true);
            }

            if (!isDummyPointerMove) {
              foundFeatureForCursor = true;
              //if (!this.isInEditingMode() || this._editingModePaused()) {
              //we don't user style.cursor because it can interfere with the general pointermove listener (setPointerCursorOnFeatureHoverForLayerId)
              this._mapService.getMap(mapId).getTargetElement().classList.add('hoverTraceFeature');
              //} else {
              newToolTipText = this.isInEditingMode() && !this._editingModePaused() ? this._translateService.instant('trace.create-route.poi-tooltip') + ' <br> ' + this._translateService.instant('trace.create-route.drag-tooltip') : null;
              //}
            }

            /*
            this would generate a tooltip with info on the graph, and it would give the wrong impression that the shown elevation is from this wayPoint
            if (searchForElevationProfilePoint) {
              const point = TraceRouteData.getClosestRoutePointForMapPoint(this._getWayPointAsPoint(wayPointRef), this._traceRouteData.routes, this._viewService.getProjection().getCode());
              if (point) {
                this._searchForElevationProfilePoint(point.getCoordinates());
              }
            }
            */

            return false;
          }
          return true;
        });

        if (!foundFeatureForHighlight) {
          //no waypoints are found
          //if we are in editing mode, not paused, find routepoints (if you would remove the not paused check, then you also need to update method _handleClickAtPixel)
          //if we only want this search starting from the level where the route points are shown, then change the test below
          const searchForRoutePoints = this.isInEditingMode() && !this.isEditingModePaused() && this._editingModeType() === 'LineString';
          //const searchForRoutePoints = this._inEditingMode() && !this._editingModePaused() && this._editingModeType() === 'LineString' && this._viewService.getZoom() >= this._zoomLevelRoutePointMarkers;
          //also check if we need to highlight a point on the elevation profile graph 

          if (searchForRoutePoints || searchForElevationProfilePoint) {

            features = this._mapService
              .getCurrentMap()
              .getFeaturesAtPixel(e.pixel, {
                hitTolerance: this._snapAndSearchPixelTolerance,
                layerFilter: (l) => {
                  return l.get('customId') === 'traceDrawLayerLineString';
                },
              });

            features.every((feature) => {
              const routeRef = feature.get('route_ref');

              if (typeof routeRef === 'number') {

                if (!isDummyPointerMove && this.isInEditingMode() && !this._editingModePaused()) {
                  foundFeatureForCursor = true;
                  //we don't user style.cursor because it can interfere with the general pointermove listener (setPointerCursorOnFeatureHoverForLayerId)
                  this._mapService.getMap(mapId).getTargetElement().classList.add('hoverTraceFeature');
                }

                if (searchForRoutePoints) {
                  const searchCircle = new OlCircle(pixelCoordinate, this._snapAndSearchPixelTolerance * this._getPointResolution(pixelCoordinate));
                  let foundRoutePointIndex = -1;
                  let foundRoutePointDistance = -1;

                  for (let i = 0; i < this._traceRouteData.routes[routeRef].routePoints.length; i++) {
                    if (!this._traceRouteData.routes[routeRef].routePoints[i].autoDraw && searchCircle.intersectsCoordinate([this._traceRouteData.routes[routeRef].routePoints[i].x, this._traceRouteData.routes[routeRef].routePoints[i].y])) {
                      const newDistance = new LineString([pixelCoordinate, [this._traceRouteData.routes[routeRef].routePoints[i].x, this._traceRouteData.routes[routeRef].routePoints[i].y]]).getLength();

                      if (foundRoutePointIndex === -1 || foundRoutePointDistance > newDistance) {
                        foundRoutePointIndex = i;
                        foundRoutePointDistance = newDistance;
                      }
                    }
                  }

                  if (foundRoutePointIndex > -1) {
                    foundFeatureForHighlight = true;

                    if ((routeRef + '_' + foundRoutePointIndex) !== this.highlightedRoutePoint()) {
                      this.removeRoutePointHighlight();
                      this.addRoutePointHighlight(routeRef, foundRoutePointIndex, true);
                    }

                    //we don't show the delete popup for routepoints anymore
                    //if (!this.isInEditingMode() || this._editingModePaused()) {
                    //we don't user style.cursor because it can interfere with the general pointermove listener (setPointerCursorOnFeatureHoverForLayerId)
                    //this._mapService.getMap(mapId).getTargetElement().classList.add('hoverTraceFeature');
                    //}
                  }


                  if (!isDummyPointerMove && this.isInEditingMode() && !this._editingModePaused()) {
                    if (routeRef === 0 && foundRoutePointIndex === 0) {
                      const hasEndPoint = this._traceRouteData.routes[0].routePoints.length >= 2;
                      const endPointEqualsStartPoint = hasEndPoint && TraceRouteData.matchingCoordinates(this._getRoutePointAsPoint(0, 0).getCoordinates(), this._getRoutePointAsPoint(0, this._traceRouteData.routes[0].routePoints.length - 1).getCoordinates());
                      newToolTipText = (endPointEqualsStartPoint || !hasEndPoint ? this._translateService.instant('trace.create-route.route-removepnt-tooltip') : this._translateService.instant('trace.create-route.route-loop-tooltip')) + '<br>' + this._translateService.instant('trace.create-route.drag-tooltip');
                    } else {
                      newToolTipText = (foundRoutePointIndex > -1 ? this._translateService.instant('trace.create-route.route-removepnt-tooltip') : this._translateService.instant('trace.create-route.route-addpnt-tooltip')) + '<br>' + this._translateService.instant('trace.create-route.drag-tooltip');
                    }
                  }

                }

                if (searchForElevationProfilePoint) {
                  this._searchForElevationProfilePoint(pixelCoordinate);
                }

                return false
              }

              return true;
            });
          }
        }

        //suggestion layer active or suggestion preview visible after zoom?
        if (this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked || this._traceDrawLayerSuggestions.getSource().getFeatures().length > 0) {
          if (this.highlightedSuggestionRouteId()) {
            this._traceDrawLayerSuggestions.getSource().getFeatures().every((feature: Feature) => {
              if (feature.get("isSuggestionCentroid") && feature.get("route_id") === this.highlightedSuggestionRouteId()) {
                feature.set("highlight", false);
                this.highlightedSuggestionRouteId.set('');
                return false;
              }
              return true;
            });
          }
          if (!this.highlightedSuggestionRouteId()) {
            features = this._mapService
              .getCurrentMap()
              .getFeaturesAtPixel(e.pixel, {
                hitTolerance: this._snapAndSearchPixelTolerance,
                layerFilter: (l) => {
                  return l.get('customId') === 'traceDrawLayerSuggestions';
                },
              });

            features.every((feature: Feature) => {
              if (feature.get("isSuggestionCentroid") && feature.get("route_id")) {
                this.highlightedSuggestionRouteId.set(feature.get("route_id"));
                foundFeatureForHighlight = true;
                feature.set("highlight", true);
                foundFeatureForCursor = true;
                this._mapService.getMap(mapId).getTargetElement().classList.add('hoverTraceFeature');
                return false;

              }
              return true;
            });
          }
        }

        if (!isDummyPointerMove) {
          if (foundFeatureForCursor && !this.isEditingModePaused() && newToolTipText) {
            this._editingTooltip.setPosition(pixelCoordinate);
            this._editingTooltip.setText(newToolTipText);
          } else {
            if (oldTooltipText) {
              //only true in editing mode
              this._editingTooltip.setPosition(undefined);
              this._editingTooltip.setText('');
            }
          }

          if (!foundFeatureForCursor) {
            ///we don't user style.cursor because it can interfere with the general pointermove listener (setPointerCursorOnFeatureHoverForLayerId)
            this._mapService.getMap(mapId).getTargetElement().classList.remove('hoverTraceFeature');
          }
        }

        if (!foundFeatureForHighlight) {
          this.removeWayPointHighlight();

          if (this._inEditingMode()) {
            this.removeRoutePointHighlight();
          }

          if (this.tabindex() === TraceDrawerType.SUGGESTIONS_ROUTE && this.highlightedSuggestionRouteId()) {
            this._traceDrawLayerSuggestions.getSource().getFeatures().every((feature: Feature) => {
              if (feature.get("isSuggestionCentroid") && feature.get("route_id") === this.highlightedSuggestionRouteId(), this._translateService.getCurrentLang()) {
                feature.set("highlight", false);
                this.highlightedSuggestionRouteId.set('');
                return false;
              }
              return true;
            });
          }
        }

        break;
      }
    }
  }

  /**
   * 
   * @param pixel 
   * @param mapId optional, if not present we will assume the user is interacting with the main map (split maps are usually only used in view mode)
   * @returns 
   */
  private _handleClickAtPixel(pixel: Pixel, mapId?: string) {

    //mapId is only useful in view mode, split map
    mapId = mapId ? mapId : 'main';

    if (this.isInEditingMode() && this._editingModeType() === 'LineString' && !this.isEditingModePaused()) {
      //this method is called after the user clicked on a line during editing e.g. inside the modifyend handler
      if (this._traceRouteData.routes.length > 0) {
        //first we will look for existing routepoints where the user possibly clicked on (autodraw true or false)
        //only one line (Linestring) during editing
        let foundRoutePointIndex = -1;
        let coordsClosestPointOnRoute: Coordinate = null;
        const highlightedRoutePointCopy = this.highlightedRoutePoint();

        if (/^0_[0-9]+$/.test(highlightedRoutePointCopy)) {
          //highlighted point has priority over clicked location (the user will see this point as highlighted when he clicks)
          foundRoutePointIndex = parseInt(highlightedRoutePointCopy.substring(highlightedRoutePointCopy.indexOf('_') + 1));
          foundRoutePointIndex = foundRoutePointIndex >= this._traceRouteData.routes[0].routePoints.length ? -1 : foundRoutePointIndex;
        }

        if (foundRoutePointIndex === -1) {
          //const pixelCoordinate = this._mapService.getCurrentMap().getCoordinateFromPixel(pixel);//(this._mapService.getCurrentMap() as PluggableMap)
          const closestPointOnRoute = TraceRouteData.getClosestRoutePointForMapPoint(new Point(this._mapService.getCurrentMap().getCoordinateFromPixel(pixel)), this._traceRouteData.routes, this._viewService.getProjection().getCode());
          coordsClosestPointOnRoute = closestPointOnRoute ? closestPointOnRoute.getCoordinates() : this._mapService.getCurrentMap().getCoordinateFromPixel(pixel);

          //existing routepoints on the line have a snap event, so we use his._snapAndSearchPixelTolerance
          const searchCircle = new OlCircle(coordsClosestPointOnRoute, this._snapAndSearchPixelTolerance * this._getPointResolution(coordsClosestPointOnRoute));
          let foundRoutePointDistance = -1;

          for (let i = 0; i < this._traceRouteData.routes[0].routePoints.length; i++) {
            //if (this._traceRouteData.routes[0].routePoints[i].autoDraw && searchCircle.intersectsCoordinate([this._traceRouteData.routes[0].routePoints[i].x, this._traceRouteData.routes[0].routePoints[i].y])) {
            if (searchCircle.intersectsCoordinate([this._traceRouteData.routes[0].routePoints[i].x, this._traceRouteData.routes[0].routePoints[i].y])) {
              const newDistance = new LineString([coordsClosestPointOnRoute, [this._traceRouteData.routes[0].routePoints[i].x, this._traceRouteData.routes[0].routePoints[i].y]]).getLength();

              if (foundRoutePointIndex === -1 || foundRoutePointDistance > newDistance) {
                foundRoutePointIndex = i;
                foundRoutePointDistance = newDistance;
              }
            }
          }
        }

        if (foundRoutePointIndex > -1) {
          //we want to see the point visually (dis)appear, so removing all intercation markers for a second
          this._drawModifyToolsPaused = true;
          this._removeDrawModifySnapIntercation();
          this.removeRoutePointHighlight();

          if (this._traceRouteData.routes[0].routePoints[foundRoutePointIndex].autoDraw) {
            //found existing routepoint, non fixed
            const copy = TraceRouteData.deepCopy(this._traceRouteData.routes[0]);
            copy.routePoints[foundRoutePointIndex].autoDraw = false;
            //no automatic routing(?)
            this._replaceTraceRouteData_route(copy, 0, TraceRouteDataUpdateType.ADDED_ROUTEPOINT);
          } else {
            //found existing routepoint, fixed
            const hasEndPoint = this._traceRouteData.routes[0].routePoints.length >= 2;
            const clickedOnEndPoint = hasEndPoint && foundRoutePointIndex === (this._traceRouteData.routes[0].routePoints.length - 1);
            const endPointEqualsStartPoint = hasEndPoint && TraceRouteData.matchingCoordinates(this._getRoutePointAsPoint(0, 0).getCoordinates(), this._getRoutePointAsPoint(0, this._traceRouteData.routes[0].routePoints.length - 1).getCoordinates());
            const clickedOnStartPoint = foundRoutePointIndex === 0;

            if (clickedOnStartPoint && hasEndPoint && !endPointEqualsStartPoint) {
              //close the route, set same coords for startpoint && endpoint
              window.setTimeout(() => {
                console.log('info: the user clicked on the start point of the route, we will close the loop');
                this._handleMapDrawInteractionEvent(new DrawEvent('drawend', new Feature(this._getRoutePointAsPoint(0, 0))));
              }, 0);
            } else {
              //remove the fixed routepoint
              //start point is always on top when hovering connected start&end point, so always delete start point in that case(?)
              //this.removeTraceRouteData_routePoint(0, (clickedOnStartPoint || clickedOnEndPoint) && endPointEqualsStartPoint ? 0 : foundRoutePointIndex);
              this.removeTraceRouteData_routePoint(0, (clickedOnStartPoint || clickedOnEndPoint) && endPointEqualsStartPoint ? this._traceRouteData.routes[0].routePoints.length - 1 : foundRoutePointIndex);
            }
          }

          //interaction markers can appear again
          window.setTimeout(() => {
            this._drawModifyToolsPaused = false;
            this._addDrawModifySnapIntercation();
          }, 500);

          return;
        }

        //there was no existing point on the route where the user clicked
        //non existing points on the line don't have a snap event, so a small circle, 2
        const searchExtent = new OlCircle(coordsClosestPointOnRoute, 2).getExtent();

        for (let z = 0; z < this._traceRouteData.routes[0].routePoints.length - 1; z++) {
          if (new LineString([[this._traceRouteData.routes[0].routePoints[z].x, this._traceRouteData.routes[0].routePoints[z].y], [this._traceRouteData.routes[0].routePoints[z + 1].x, this._traceRouteData.routes[0].routePoints[z + 1].y]]).intersectsExtent(searchExtent)) {
            foundRoutePointIndex = z;
            break;
          }
        }

        if (foundRoutePointIndex > -1) {
          //we want to see the point visually appear, so removing all intercation markers for a second
          this._drawModifyToolsPaused = true;
          this._removeDrawModifySnapIntercation();
          this.removeRoutePointHighlight();

          const copy = TraceRouteData.deepCopy(this._traceRouteData.routes[0]);
          //we found a new point between z & z+1
          copy.routePoints.splice(foundRoutePointIndex + 1, 0, TraceRouteData.convertPointFeatureToTraceWayPoint(new Feature({ geometry: new Point(coordsClosestPointOnRoute), autoDraw: false }), this._viewService.getProjection().getCode()));
          //no automatic routing(?)
          this._replaceTraceRouteData_route(copy, 0, TraceRouteDataUpdateType.ADDED_ROUTEPOINT);

          //intercation markers can appear again
          window.setTimeout(() => {
            this._drawModifyToolsPaused = false;
            this._addDrawModifySnapIntercation();
          }, 500);
        }
      }
    } else {
      /*
      it would be easier to use directly this.highlightedWayPoint() and this.highlightedRoutePoint()
      to get the clicked feature, but on touch devices this value is maybe not set or still the 
      previously clicked point 
      */
      let foundFeature = false;
      let highlighCircleFeature = null;

      let features = this._mapService
        .getMap(mapId)
        .getFeaturesAtPixel(pixel, {
          hitTolerance: this._snapAndSearchPixelTolerance,
          layerFilter: (l) => {
            return l.get('customId') === (mapId === 'main' ? 'traceDrawLayerPoint' : 'traceDrawLayerPoint_' + mapId);
          },
        });
      //first we will look for waypoints
      for (let i = 0; i < features.length; i++) {
        if (typeof features[i].get('wayPoint_ref') === 'number') {
          foundFeature = true;

          //always show popup on click or scroll in right panel when it's open?
          //if (this._watchingRouteWithoutDrawer) {this._showWayPointInfoPopup(features[i].get('wayPoint_ref'), false, mapId);} else {this._clickedPoint.next({ ref: 'wayPoint_' + features[i].get('wayPoint_ref'), isPoi: true, isNew: false });}
          //feature should contain the same info as the waypoint in the model
          this._showWayPointInfoPopup(features[i].get('wayPoint_ref'), false, mapId);

          highlighCircleFeature = this._addHighlightCircle((features[i].getGeometry() as Point).getCoordinates(), true);

          break;
        }
      }

      //we don't show the delete popup for routepoints anymore
      /*
      //if no waypoints are found, and if we are in editing mode, not paused, find routepoints
      //if we only want this search starting from the level where the route points are shown, then change the test below
      //if (!foundFeature && this._inEditingMode() && this._viewService.getZoom() >= this._zoomLevelRoutePointMarkers) {
      if (!foundFeature && this.isInEditingMode() && !this.isEditingModePaused()) {
     
        features = this._mapService
        .getMap(mapId)
          .getFeaturesAtPixel(pixel, {
            hitTolerance: this._searchPixelTolerance,
            layerFilter: (l) => {
              return l.get('customId') === 'traceDrawLayerLineString';
            },
          });
     
        features.every((feature) => {
          const routeRef = feature.get('route_ref');
     
          if (typeof routeRef === 'number') {
     
            const pixelCoordinate = this._mapService.getMap(mapId).getCoordinateFromPixel(pixel);//(this._mapService.getMap(mapId) as PluggableMap)
            //we put take the circle a bit bigger then on pointermove, otherwise you often miss the routepoint on click
            const searchCircle = new OlCircle(pixelCoordinate, this._searchPixelTolerance * this._getPointResolution(pixelCoordinate));
            let foundRoutePointIndex = -1;
            let foundRoutePointDistance = -1;
     
            for (let i = 0; i < this._traceRouteData.routes[routeRef].routePoints.length; i++) {
     
              if (!this._traceRouteData.routes[routeRef].routePoints[i].autoDraw && searchCircle.intersectsCoordinate([this._traceRouteData.routes[routeRef].routePoints[i].x, this._traceRouteData.routes[routeRef].routePoints[i].y])) {
                const newDistance = new LineString([pixelCoordinate, [this._traceRouteData.routes[routeRef].routePoints[i].x, this._traceRouteData.routes[routeRef].routePoints[i].y]]).getLength();
     
                if (foundRoutePointIndex === -1 || foundRoutePointDistance > newDistance) {
                  foundRoutePointIndex = i;
                  foundRoutePointDistance = newDistance;
                }
              }
            }
     
            if (foundRoutePointIndex > -1) {
              foundFeature = true;
              //always show popup on click or scroll in right panel when it's open?
              //if (this._watchingRouteWithoutDrawer) {...} else {...;this._clickedPoint.next({...});}
              this._showRoutePointInfoPopup(routeRef, foundRoutePointIndex);
              highlighCircleFeature = this._addHighlightCircle([this._traceRouteData.routes[routeRef].routePoints[foundRoutePointIndex].x, this._traceRouteData.routes[routeRef].routePoints[foundRoutePointIndex].y], true);
            }
          }
     
          return !foundFeature;
        });
      }
      */

      /*
      if (!highlighCircleFeature) {
        highlighCircleFeature = this._addHighlightCircle(this._mapService.getMap(mapId).getCoordinateFromPixel(pixel), true); //(this._mapService.getMap(mapId) as PluggableMap)
      }
      */

      if (highlighCircleFeature) {
        window.setTimeout(() => {
          this._traceDrawLayerHighlight.getSource().removeFeature(highlighCircleFeature);
        }, 500);
      }

      //suggestion layer active or suggestion preview visible after zoom?
      if (!foundFeature && (this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked || this._traceDrawLayerSuggestions.getSource().getFeatures().length > 0)) {
        foundFeature = this._findSuggestionAtCoordinate(pixel, true, mapId);
      }

      if (!foundFeature && this.isInViewMode()) {
        this._getRouteHeading(this._mapService.getMap(mapId).getCoordinateFromPixel(pixel));
      }
    }
  }

  private _getPointResolution(center: Coordinate): number {
    return getPointResolution(this._viewService.getProjection(), this._viewService.getResolution(), center);//, METERS_PER_UNIT.m
  };

  private _addHighlightCircle(coordinate: Coordinate, isClick: boolean) {
    const highlighCircleFeature = new Feature();
    highlighCircleFeature.setGeometry(new Point(coordinate));
    highlighCircleFeature.set('highlightCircle', true);
    highlighCircleFeature.set('clickCircle', isClick);
    this._traceDrawLayerHighlight.getSource().addFeature(highlighCircleFeature);
    return highlighCircleFeature;
  }

  private _removeHighlightCircle() {
    const highlightCircles = [];
    this._traceDrawLayerHighlight.getSource().getFeatures().forEach((feature) => {
      if (feature.get('highlightCircle')) {
        highlightCircles.push(feature);
      }
    });
    highlightCircles.forEach((feature) => {
      this._traceDrawLayerHighlight.getSource().removeFeature(feature)
    });
  }

  /**
   * 
   * @param index 
   * @param mapId optional, if not present we will assume the user is interacting with the main map (split maps are usually only used in view mode)
   * @returns 
   */
  private _showWayPointInfoPopup(index: number, isNew: boolean, mapId?: string) {
    const wayPoint = this.getTraceRouteData_wayPoint(index);
    if (!wayPoint) {
      return;
    }

    let popupWasAlreadyOpen = false;
    if (this._popupService.isPopupAlreadyOnMap()) {
      popupWasAlreadyOpen = true;
      this._popupService.hide();
    }

    let desc =
      !isNew && wayPoint.desc && wayPoint.desc.length > 0
        ? wayPoint.desc
        : '';

    let tooLong = false;
    let remainingChars = 200;

    if (desc.length > remainingChars) {
      let tempDesc = '';
      let descCopy = desc;

      try {
        //making sure we'll find the opening tag of every link <A  should be <a
        desc = desc.replace(/(<a\s+)/gmi, '<a ');
        //making sure we'll find the closing tag of every link </A > should be </a>
        desc = desc.replace(/(<\/a\s*>)/gmi, '</a>');

        while (desc.substring(0, remainingChars).indexOf('<a ') > -1) {
          remainingChars -= desc.substring(0, desc.indexOf('<a ')).length;

          tempDesc += desc.substring(0, desc.indexOf('<a '));
          desc = desc.substring(desc.indexOf('<a '));

          tempDesc += desc.substring(0, desc.indexOf('>') + 1);
          desc = desc.substring(desc.indexOf('>') + 1);

          remainingChars -= desc.substring(0, desc.indexOf('</a>')).length;

          tempDesc += desc.substring(0, desc.indexOf('</a>') + 4);
          desc = desc.substring(desc.indexOf('</a>') + 4);
        }

        if (remainingChars > 0) {
          tempDesc += desc.substring(0, desc.length > remainingChars ? remainingChars : desc.length);
          desc = desc.length > remainingChars ? desc.substring(remainingChars) : '';
        }

        //we want complete words and complete tags
        if (desc.length > 0) {
          if (desc.indexOf('</') !== -1 && desc.indexOf('</') < desc.indexOf('<')) {
            tempDesc += desc.substring(0, desc.indexOf('</') + 2);
            desc = desc.substring(desc.indexOf('</') + 2);
            tempDesc += desc.substring(0, desc.indexOf('>') + 1);
            desc = desc.substring(desc.indexOf('>') + 1).trim();
          } else if (desc.indexOf(' ') > -1) {
            tempDesc += desc.substring(0, desc.indexOf(' '));
            desc = desc.substring(desc.indexOf(' ')).trim();
          }
        }

        tooLong = desc.length > 0;
        desc = tempDesc + (tooLong ? '&nbsp;&hellip;' : '');
      } catch (error) {
        console.log(`unable to take substring of desciption (${remainingChars} chars) : `);
        console.log(error);
        desc = descCopy;
        tooLong = false;
      }
    }

    if (desc.length === 0) {
      desc = '<span class=\"untitled\">' + this._translateService.instant(
        'trace.field.nodescription')
        + '</span>'
    }


    // .ngi-tmv-theme-light tt-trace .wayPoints .wayPoint .title > div.index-container, .ngi-tmv-theme-light tt-trace-view .wayPoints .wayPoint .title > div.index-container {
    //   position: absolute;
    //   text-align: center;
    //   left: 0;
    // }

    let sequenceNumber: string;
    if (this.isInViewMode() && this._traceRouteData.wayPoints.length < 27 && TraceRouteData.getTraceTTSetting(this._traceRouteData.ttSettings, "wayPointSequenceNumber") === 'alpha') {
      sequenceNumber = TraceRouteData.convertWayPointIndexToLetter(index + 1) + '';
    } else {
      sequenceNumber = (index + 1) + '';
    }

    let content =
      '<div class="wayPoint"><div class="title"><div class="index-container"><span>' +
      sequenceNumber +
      '</span></div>' +
      (!isNew && wayPoint.name && wayPoint.name.length > 0
        ? wayPoint.name
        : '<span class="untitled">' + this._translateService.instant('trace.field.noname')) + '</span>' +
      '</div><div class="desc">' +
      desc +
      '</div></div>';

    let popupProperties: PopupData = {
      coordinates: [wayPoint.x, wayPoint.y],
      type: 'TraceInfo',
      title: 'trace.poi',
      content: content,
      customEvent: this._inEditingMode() || tooLong ? {
        eventId: this._inEditingMode() ? 'TraceInfoEdit' : 'TraceInfoReadOn',
        data: { ref: [index], isPoi: true, isNew: isNew },
        buttonType: '',
        label: this._translateService.instant(
          this._inEditingMode() ? 'trace.edit-text' : 'trace.field.description.readon'
        ),
      } : null,
      zoomToFeatureLink: false,
      className: 'TraceInfo',
      mapId: mapId
    };

    window.setTimeout(() => { this._popupService.add(popupProperties); }, popupWasAlreadyOpen ? 300 : 0);
  }

  private _showRoutePointInfoPopup(routeIndex: number, routePointIndex: number, mapId?: string) {
    let popupWasAlreadyOpen = false;
    if (this._popupService.isPopupAlreadyOnMap()) {
      popupWasAlreadyOpen = true;
      this._popupService.hide();
    }

    mapId = mapId ? mapId : 'main';
    let popupProperties: PopupData = {
      coordinates: [this._traceRouteData.routes[routeIndex].routePoints[routePointIndex].x, this._traceRouteData.routes[routeIndex].routePoints[routePointIndex].y],
      type: 'TraceInfo',
      title: 'trace.create-route.routepoint',
      content: this._traceRouteData.routes[routeIndex].routePoints[routePointIndex].formattedXY,
      customEvent: {
        eventId: 'TraceInfoEdit',
        data: { ref: [routeIndex, routePointIndex], isPoi: false },
        label: this._translateService.instant('trace.create-route.delete'),
      },
      zoomToFeatureLink: false,
      className: 'TraceInfo',
      mapId: mapId
    };

    window.setTimeout(() => { this._popupService.add(popupProperties); }, popupWasAlreadyOpen ? 300 : 0);
  }

  /**
   * on mobile/touch device dragging the route only works when the map's target element had css rule 'touch-action: none'
   * 
   * @param e 
   */
  private _handleMapModifyInteractionEvent(e: ModifyEvent) {
    switch (e.type) {
      case 'modifystart': {
        this._draggedWayPointIndex = -1;
        this._draggedRoutePointIndex = -1;
        this._draggedRoutePointAddition = false

        this._isDragging = true;
        this._snapInterAction.setActive(false);

        if (this._editingModeType() !== 'Point') {
          //dummy map event, so that this.highlightedRoutePoint() is set(if possible), because on mobile devices were we don't have a pointermove event
          //mapId parameter can be omitted, editing always on main map
          this._handleMapInteractionEvent({ type: 'dummy_pointermove', pixel: e.mapBrowserEvent.pixel });
        }

        this._dragTimer = new Date().getTime();
        //this._draggedRouteFromCoordinate = this._mapService.getCurrentMap().getCoordinateFromPixel(e.mapBrowserEvent.pixel); //(this._mapService.getCurrentMap() as PluggableMap)
        this._draggedRouteFromCoordinate = e.mapBrowserEvent.coordinate;

        //_traceDrawLayerHighlight will be cleared by these 2 methods
        const oldHighlightedRoutePoint = this.highlightedRoutePoint();
        this.removeRoutePointHighlight();
        this.removeWayPointHighlight();

        if (this._elevationProfilePointOnMap) {
          this._removeElevationProfilePointFromMap();
          this.removeElevationProfileTooltip();
        }

        if (this._editingModeType() === 'Point') {

          e.features.forEach((feature) => {
            if (this._draggedWayPointIndex !== -1) {
              return;
            }

            if (typeof feature.get('wayPoint_ref') === 'number') {
              this._draggedWayPointIndex = feature.get('wayPoint_ref');
            }
          });

          if (this._draggedWayPointIndex !== -1) {
            this._traceDrawLayerPoint.changed();
          }

        } else {

          //do not show arrows while dragging route
          this._traceDrawLayerArrows.setStyle(null);

          if (!this.isBusyWithAutoRouting()) {

            //if a new point is being created on the route, it will not yet be in the coordinates array on modifystart

            e.features.forEach((feature) => {
              if (this._draggedRoutePointIndex !== -1) {
                return;
              }

              //normally only one line in editing mode
              if (typeof feature.get('route_ref') === 'number' && feature.get('route_ref') === 0) {

                let coordinates: Coordinate[] = [];

                let coordsClosestPointOnRoute = this._draggedRouteFromCoordinate

                if (feature.getGeometry() instanceof LineString) {
                  //line
                  //in edit mode there is no multilinestring

                  //sometimes e.mapBrowserEvent.coordinate is not completely on the line
                  if (!(feature.getGeometry() as LineString).intersectsCoordinate(coordsClosestPointOnRoute)) {
                    const closestPointOnRoute = TraceRouteData.getClosestRoutePointForMapPoint(new Point(coordsClosestPointOnRoute), this._traceRouteData.routes, this._viewService.getProjection().getCode());
                    coordsClosestPointOnRoute = closestPointOnRoute ? closestPointOnRoute.getCoordinates() : coordsClosestPointOnRoute;
                  }

                  coordinates = (feature.getGeometry() as LineString).getCoordinates();
                } else if (feature.getGeometry() instanceof Point) {
                  //only a starting point
                  coordinates = [(feature.getGeometry() as Point).getCoordinates()];

                  //sometimes e.mapBrowserEvent.coordinate is not completely on the line
                  if (!TraceRouteData.matchingCoordinates(coordinates[0], coordsClosestPointOnRoute)) {
                    coordsClosestPointOnRoute = coordinates[0];
                  }
                }


                if (coordinates.length > 1) {
                  if (/^0_[0-9]+$/.test(oldHighlightedRoutePoint)) {
                    //highlighted point has priority over clicked location (the user will see this point as highlighted when he clicks)
                    let newDraggedRoutePointIndex = parseInt(oldHighlightedRoutePoint.substring(oldHighlightedRoutePoint.indexOf('_') + 1));
                    newDraggedRoutePointIndex = newDraggedRoutePointIndex >= this._traceRouteData.routes[0].routePoints.length ? -1 : newDraggedRoutePointIndex;

                    //start point is always on top when hovering connected start&end point, so always drag start point in that case(?)
                    //this._draggedRoutePointIndex = newDraggedRoutePointIndex;

                    if (newDraggedRoutePointIndex === 0) {
                      const endPointEqualsStartPoint = this._traceRouteData.routes[0].routePoints.length >= 2 && TraceRouteData.matchingCoordinates(this._getRoutePointAsPoint(0, 0).getCoordinates(), this._getRoutePointAsPoint(0, this._traceRouteData.routes[0].routePoints.length - 1).getCoordinates());
                      this._draggedRoutePointIndex = endPointEqualsStartPoint ? this._traceRouteData.routes[0].routePoints.length - 1 : newDraggedRoutePointIndex;
                    } else {
                      this._draggedRoutePointIndex = newDraggedRoutePointIndex;
                    }
                  }

                  if (this._draggedRoutePointIndex === -1) {
                    const searchCircle = new OlCircle(coordsClosestPointOnRoute, this._snapAndSearchPixelTolerance * this._getPointResolution(coordsClosestPointOnRoute));

                    let shortestDistanceFromDraggedCoordinate = -1;

                    for (let i = 0; i < coordinates.length; i++) {
                      if (searchCircle.intersectsCoordinate(coordinates[i])) {
                        //this._draggedRoutePointIndex = i;
                        //break;
                        const newShortestDistanceToCheck = new LineString([coordsClosestPointOnRoute, coordinates[i]]).getLength();
                        if ((shortestDistanceFromDraggedCoordinate === -1) || (newShortestDistanceToCheck < shortestDistanceFromDraggedCoordinate)) {
                          this._draggedRoutePointIndex = i;
                          shortestDistanceFromDraggedCoordinate = newShortestDistanceToCheck;
                        }
                      }
                    }
                  }

                  if (this._draggedRoutePointIndex === -1) {
                    //we didn't find it, so normally a new point is added on the line, between existing points
                    const searchExtent = new OlCircle(coordsClosestPointOnRoute, 2).getExtent();

                    for (let z = 0; z < this._traceRouteData.routes[0].routePoints.length - 1; z++) {
                      if (new LineString([[this._traceRouteData.routes[0].routePoints[z].x, this._traceRouteData.routes[0].routePoints[z].y], [this._traceRouteData.routes[0].routePoints[z + 1].x, this._traceRouteData.routes[0].routePoints[z + 1].y]]).intersectsExtent(searchExtent)) {
                        this._draggedRoutePointIndex = z;
                        this._draggedRoutePointAddition = true
                        break;
                      }
                    }

                    if (this._draggedRoutePointIndex !== -1 && this._traceRouteData.routes[0].routePoints[this._draggedRoutePointIndex].autoDraw) {
                      this._draggedRoutePointIndex = this._findAutoDrawRoutePointsAroundIndex(this._traceRouteData.routes[0].routePoints, this._draggedRoutePointIndex, true)[0];
                    }
                  }
                } else {
                  this._draggedRoutePointIndex = 0;
                }
              }
            });

            if (this._draggedRoutePointIndex !== -1) {
              this._traceDrawLayerLineString.changed();
            }
          }
        }

        break;
      }
      case 'modifyend': {

        this._isDragging = false;
        this._snapInterAction.setActive(true);

        //const draggedRouteToCoordinate = this._mapService.getCurrentMap().getCoordinateFromPixel(e.mapBrowserEvent.pixel); //(this._mapService.getCurrentMap() as PluggableMap)
        const draggedRouteToCoordinate = e.mapBrowserEvent.coordinate;

        if (!olExtent.containsCoordinate(this.extentRestrictionLB08, draggedRouteToCoordinate)) {
          //undo move and stop
          this._draggedWayPointIndex = -1;
          this._draggedRoutePointIndex = -1;
          this._draggedRoutePointAddition = false;
          this._updateRoutesOnMap();

          this._alertService.info(this._translateService.instant('trace.import-outside-map'), { 'autoClose': true });

          return;
        }

        if (this._editingModeType() !== 'Point' && (this.isBusyWithAutoRouting() || this._draggedRoutePointIndex === -1)) {

          if (this.isBusyWithAutoRouting()) {
            this._alertService.info(this._translateService.instant('trace.create-route.auto-routing-busy'), { 'autoClose': true });
          }

          //undo move and stop
          this._draggedRoutePointIndex = -1;
          this._draggedRoutePointAddition = false;
          this._updateRoutesOnMap();
          return;
        }

        if (this._editingModeType() !== 'Point') {
          //show arrows after dragging route
          this._traceDrawLayerArrows.setStyle((feature: Feature, resolution: number) => {
            return this._getTraceStyle(feature, resolution, false, true);
          });

          //when the dragging time was too short or if the distance was too short, then we will consider it as a click on the line (drag inside radius of this._snapAndSearchPixelTolerance ? this._snapAndSearchPixelTolerance * 0.5 ? 2px ?)
          //a click on the line would normally result in a new fixed routepoint here (autodraw=false), but we handle that in the drawend event
          //a modifystart event also only happens for clicks on the line were there are not yet existing routepoints(=start/end of a segment), otherwise it's a normal click event
          if ((this._dragTimer + 250) > new Date().getTime() || (!this._draggedRouteFromCoordinate || new OlCircle(this._draggedRouteFromCoordinate, 2).intersectsCoordinate(draggedRouteToCoordinate))) {
            //undo move and stop
            this._draggedRoutePointIndex = -1;
            this._draggedRoutePointAddition = false;
            this._updateRoutesOnMap();
            //mapId parameter can be omitted, editing always on main map
            this._handleMapInteractionEvent({ type: 'dummy_click', pixel: e.mapBrowserEvent.pixel });
            return;
          }
        }

        let ignoreAnyRemainingFeatures = false;

        if (this._editingModeType() === 'Point') {
          this._draggedWayPointIndex = -1;

          e.features.forEach((feature) => {
            if (ignoreAnyRemainingFeatures) {
              return;
            }

            if (typeof feature.get('wayPoint_ref') === 'number') {
              ignoreAnyRemainingFeatures = true;
              //we first take a copy, the model will be updated afterwards
              const wayPointsCopy: TraceWayPointType[] = TraceRouteData.deepCopy(this._traceRouteData.wayPoints);
              wayPointsCopy[feature.get('wayPoint_ref')].x = (feature.getGeometry() as Point).getCoordinates()[0];
              wayPointsCopy[feature.get('wayPoint_ref')].y = (feature.getGeometry() as Point).getCoordinates()[1];
              this.setTraceRouteData_wayPoints(wayPointsCopy, TraceRouteDataUpdateType.MOVED_POI);
            }
          });

          this._traceDrawLayerPoint.changed();

        } else {

          e.features.forEach((feature) => {
            if (ignoreAnyRemainingFeatures) {
              return;
            }

            //normally only one line in editing mode
            if (typeof feature.get('route_ref') === 'number' && feature.get('route_ref') === 0) {
              ignoreAnyRemainingFeatures = true;

              //we want to keep the old autoDraw values
              //remove all points with autoDraw value true, between dragged point and previous and/or next point with autoDraw value false
              let newRoutePoints: TraceWayPointType[] = [];
              const fixedPointsAroundDraggedIndex = this._findAutoDrawRoutePointsAroundIndex(this._traceRouteData.routes[0].routePoints, this._draggedRoutePointIndex, true);
              if (this._draggedRoutePointAddition) {
                fixedPointsAroundDraggedIndex[0] = this._draggedRoutePointIndex;
              }

              for (let i = 0; this._draggedRoutePointAddition || fixedPointsAroundDraggedIndex[0] !== this._draggedRoutePointIndex ? i <= fixedPointsAroundDraggedIndex[0] : i < fixedPointsAroundDraggedIndex[0]; i++) {
                newRoutePoints.push(this._traceRouteData.routes[0].routePoints[i])
              }
              const updatedPointPosition = newRoutePoints.length;
              newRoutePoints.push({
                x: draggedRouteToCoordinate[0],
                y: draggedRouteToCoordinate[1],
                autoDraw: false,
                formattedXY: TraceRouteData.formatPointForHtmlView(new Point(draggedRouteToCoordinate), this._viewService.getProjection().getCode())
              })
              for (let i = this._draggedRoutePointAddition || fixedPointsAroundDraggedIndex[1] !== this._draggedRoutePointIndex ? fixedPointsAroundDraggedIndex[1] : fixedPointsAroundDraggedIndex[1] + 1; i < this._traceRouteData.routes[0].routePoints.length; i++) {
                newRoutePoints.push(this._traceRouteData.routes[0].routePoints[i])
              }

              //test if user dragged a point on the previous or next point (autoDraw false), then we can remove one of the points
              let tooCloseTooDraggedPoint = -1;
              for (let i = 0; i < newRoutePoints.length; i++) {
                //TraceRouteData.coordinatesCloserThanDistance([newRoutePoints[i].x, newRoutePoints[i].y], draggedRouteToCoordinate, 5)
                if ((i !== updatedPointPosition) && !newRoutePoints[i].autoDraw && TraceRouteData.coordinatesCloserThanPixels([newRoutePoints[i].x, newRoutePoints[i].y], draggedRouteToCoordinate, 5, this._getPointResolution(draggedRouteToCoordinate))) {
                  tooCloseTooDraggedPoint = i;
                  break;
                }
              }

              if (tooCloseTooDraggedPoint > -1) {
                let fromIndex = updatedPointPosition < tooCloseTooDraggedPoint ? updatedPointPosition : tooCloseTooDraggedPoint;
                let toIndex = updatedPointPosition < tooCloseTooDraggedPoint ? tooCloseTooDraggedPoint : updatedPointPosition;

                if (fromIndex === 0 && toIndex === (newRoutePoints.length - 1)) {
                  //special case
                  //the user is closing the loop, the start point is dropped on the end point or vice versa
                  console.log('info: the user dropped the start and end point of the route on eachother, we will close the loop');

                  newRoutePoints[toIndex].x = newRoutePoints[fromIndex].x;
                  newRoutePoints[toIndex].y = newRoutePoints[fromIndex].y;
                  if (newRoutePoints[fromIndex].z) {
                    newRoutePoints[toIndex]['z'] = newRoutePoints[fromIndex].z;
                  } else {
                    if (newRoutePoints[toIndex]['z']) {
                      delete newRoutePoints[toIndex].z;
                    }
                  }
                } else {

                  if (fromIndex === 0
                    && newRoutePoints[0].x === newRoutePoints[newRoutePoints.length - 1].x
                    && newRoutePoints[0].y === newRoutePoints[newRoutePoints.length - 1].y
                    && ((toIndex - fromIndex) > (newRoutePoints.length - 1 - toIndex))) {
                    //start and end point are identical
                    //the user dropped the dragged point on the start/end point (the fromIndex will always be 0 in the search above)
                    //but the dragged point index is closer to the index of the end point, so we need to switch the indexes
                    fromIndex = toIndex;
                    toIndex = newRoutePoints.length - 1;
                  }

                  let onlyAutoDrawPointsBetweenFromAndTo = true;

                  for (let i = fromIndex + 1; onlyAutoDrawPointsBetweenFromAndTo && i < toIndex; i++) {
                    onlyAutoDrawPointsBetweenFromAndTo = newRoutePoints[i].autoDraw;
                  }

                  if (onlyAutoDrawPointsBetweenFromAndTo) {
                    //only when neighbouring points are dropped on each other
                    console.log('info: the user dropped the dragged point on a neighbouring route point, so we removed one of the points');
                    const copyRoutePointIndex = this._draggedRoutePointIndex;
                    this._draggedRoutePointIndex = -1;
                    this.removeTraceRouteData_routePoint(0, copyRoutePointIndex);
                    return;
                  }
                }
              }

              //we first take a copy, the model will be updated afterwards
              const routeCopyOld: TraceRouteType = TraceRouteData.deepCopy(this._traceRouteData.routes[0]);
              const routeCopyNew: TraceRouteType = TraceRouteData.deepCopy(this._traceRouteData.routes[0]);
              routeCopyNew.routePoints = newRoutePoints;
              this._replaceTraceRouteData_route(routeCopyNew, 0, this.autoRouting() ? TraceRouteDataUpdateType.MOVED_ROUTE_TEMP : TraceRouteDataUpdateType.MOVED_ROUTE);

              if (this.autoRouting()) {
                //const copyRoutePointIndex = this._draggedRoutePointIndex;
                this._handleMapInteraction_autorouting(0, routeCopyOld, true, updatedPointPosition, false);
              }

              this._traceDrawLayerModifiableHighlight.getSource().clear();
            }
          });

          this._draggedRoutePointIndex = -1;
          this._draggedRoutePointAddition = false;
          this._traceDrawLayerLineString.changed();

        }

        break;
      }
    }
  }

  private _handleMapDrawInteractionEvent(e: DrawEvent) {
    switch (e.type) {
      case 'drawend': {
        //we can't prevent this point being added to the layer, but without style the user will never see it
        //the point is added a few milliseconds after this event, so we add a timeout
        const drawTime = new Date().getTime();
        e.feature.setId('drawnPoint_' + drawTime);
        e.feature.set('drawnPoint', true);
        e.feature.set('fileDownloadIgnore', true);

        window.setTimeout(() => {
          const lastFeature: any = this._editingModeType() === 'Point' ? this._traceDrawLayerPoint.getSource().getFeatureById('drawnPoint_' + drawTime) : this._traceDrawLayerLineString.getSource().getFeatureById('drawnPoint_' + drawTime);
          if (lastFeature) {
            if (this._editingModeType() === 'Point') {
              this._traceDrawLayerPoint.getSource().removeFeature(lastFeature);
            } else {
              this._traceDrawLayerLineString.getSource().removeFeature(lastFeature);
            }
          }
        }, 500);

        if (!olExtent.containsCoordinate(this.extentRestrictionLB08, (e.feature.getGeometry() as Point).getCoordinates())) {
          this._alertService.info(this._translateService.instant('trace.import-outside-map'), { 'autoClose': true });
          return;
        }

        const calculateRoutingAfterwards = this.autoRouting() && this._editingModeType() === 'LineString';

        if (calculateRoutingAfterwards && this.isBusyWithAutoRouting()) {
          //alert('busy');
          this._alertService.info(this._translateService.instant('trace.create-route.auto-routing-busy'), { 'autoClose': true });
          return;
        }

        const oldRoute = calculateRoutingAfterwards && this._traceRouteData.routes.length > 0 ? TraceRouteData.deepCopy(this._traceRouteData.routes[this._traceRouteData.routes.length - 1]) : null;
        const pointWasAdded = this._handleMapDrawInteraction_manual((e.feature.getGeometry() as Point).getCoordinates());

        if (pointWasAdded && calculateRoutingAfterwards) {
          this._handleMapInteraction_autorouting(this._traceRouteData.routes.length - 1, oldRoute, false, -1, false);
        }

        break;
      }
    }
  }

  /**
   * 
   * @param coordinate 
   * @returns true if the new point could be added
   */
  private _handleMapDrawInteraction_manual(coordinate: Coordinate): boolean {
    if (!coordinate) {
      return false;
    }

    const drawTime = new Date().getTime();

    if (this._editingModeType() === 'LineString') {
      const routesCopy = TraceRouteData.deepCopy(this._traceRouteData.routes);
      if (routesCopy.length === 0) {
        routesCopy.push({ wasTrack: false, routePoints: [] });
      }
      routesCopy[routesCopy.length - 1].routePoints.push({ x: coordinate[0], y: coordinate[1], formattedXY: TraceRouteData.formatPointForHtmlView(new Point(coordinate), this._viewService.getProjection().getCode()) });
      this.setTraceRouteData_routes(routesCopy, this.autoRouting() ? TraceRouteDataUpdateType.ADDED_ROUTEPOINT_TEMP : TraceRouteDataUpdateType.ADDED_ROUTEPOINT, true);
    } else {
      let wayPointsCopy: TraceWayPointType[] = TraceRouteData.deepCopy(this._traceRouteData.wayPoints);
      const tempDrawId = ('drawnPoint_' + drawTime) as TraceWayPointDrawIdType;
      wayPointsCopy.push({ x: coordinate[0], y: coordinate[1], drawId: tempDrawId, formattedXY: TraceRouteData.formatPointForHtmlView(new Point(coordinate), this._viewService.getProjection().getCode()) });
      this.setTraceRouteData_wayPoints(wayPointsCopy, TraceRouteDataUpdateType.ADDED_POI);
    }

    if (!this.autoRouting() || this._editingModeType() !== 'LineString') {
      window.setTimeout(() => {
        if (this._editingModeType() === 'LineString') {
          if (!this._watchingRouteWithoutDrawer) {
            const indexes: number[] = []
            indexes.push(this._traceRouteData.routes.length - 1);
            indexes.push(this._traceRouteData.routes[indexes[0]].routePoints.length - 1);
            this.addRoutePointHighlight(indexes[0], indexes[1], true);
            this._clickedPoint.next({ ref: 'routePoint_' + indexes[0] + '_' + indexes[1], isPoi: false, isNew: true });
          }
        } else {
          //waypoints always get reordered, so we don't know the index yet, we search by id
          let count = 0
          this._traceRouteData.wayPoints.every((wayPoint) => {
            if (('drawnPoint_' + drawTime) === wayPoint.drawId) {
              if (this._browserSniffer.isPhone() || this._watchingRouteWithoutDrawer) {
                this._showWayPointInfoPopup(count, true);
              } else {
                this.addWayPointHighlight(count, true);
                this._clickedPoint.next({ ref: 'wayPoint_' + count, isPoi: true, isNew: true });
              }
              return false;
            }
            count++;
            return true;
          });
        }
      }, 500);
    }

    return true
  }

  private _isWayPointInViewPort(point: Point) {
    return olExtent.containsXY(
      this._mapService
        .getCurrentMap()
        .getView()
        .calculateExtent(this._mapService.getCurrentMap().getSize()),
      point.getCoordinates()[0],
      point.getCoordinates()[1]
    );
  }

  private _updateWaypointsOnMap() {
    if (this.isInEditingMode() && this._modifyInterAction && this._editingModeType() === 'Point') {
      this._modifyInterAction.setActive(false);
      this._snapInterAction.setActive(false);
    }

    //startPoint & endPoint are also in this layer but they will be updated in method _updateRoutesOnMap() because they are "part" of the route
    let upForRemoval = [];
    this._traceDrawLayerPoint.getSource().getFeatures().forEach((feature) => {
      if (typeof feature.get('wayPoint_ref') === 'number') {
        upForRemoval.push(feature)
      }
    });
    upForRemoval.forEach((feature) => {
      this._traceDrawLayerPoint.getSource().removeFeature(feature);
    })

    let count = 0;
    this._traceRouteData.wayPoints.forEach((wayPoint) => {
      const feature =
        TraceRouteData.convertTraceWayPointToPointFeature(wayPoint);
      feature.setId('wayPoint_' + count);
      feature.set('wayPoint_ref', count);
      this._traceDrawLayerPoint.getSource().addFeature(feature);
      count++;
    });

    if (this.isInEditingMode() && this._editingModeType() === 'Point') {
      this._copyFeaturesToTraceDrawLayerModifiable();
    }

    if (this.isInEditingMode() && this._modifyInterAction && this._editingModeType() === 'Point') {
      this._modifyInterAction.setActive(true);
      this._snapInterAction.setActive(true);
    }
  }

  private _updateRoutesOnMap() {
    if (this.isInEditingMode() && this._modifyInterAction && this._editingModeType() === 'LineString') {
      this._modifyInterAction.setActive(false);
      this._snapInterAction.setActive(false);
    }

    let upForRemoval = [];
    this._traceDrawLayerLineString.getSource().getFeatures().forEach((feature) => {
      if (typeof feature.get('route_ref') === 'number') {
        upForRemoval.push(feature);
      }
    });
    upForRemoval.forEach((feature) => {
      this._traceDrawLayerLineString.getSource().removeFeature(feature);
    })

    //startPoint & endPoint are in another layer but they will be updated here because they are "part" of the route
    upForRemoval = [];
    this._traceDrawLayerPoint.getSource().getFeatures().forEach((feature) => {
      if (feature.get('endPoint') || feature.get('startPoint')) {
        upForRemoval.push(feature);
      }
    });
    upForRemoval.forEach((feature) => {
      this._traceDrawLayerPoint.getSource().removeFeature(feature);
    })

    let count = 0;
    this._traceRouteData.routes.forEach((route) => {
      const feature =
        TraceRouteData.convertTraceRouteToLineStringOrPointFeature(route);
      if (feature) {
        feature.setId('route_' + count);
        feature.set('route_ref', count);
        this._traceDrawLayerLineString.getSource().addFeature(feature);
        count++;
      }
    });

    //arrows
    this._traceDrawLayerArrows.getSource().clear();
    this._traceDrawLayerLineString.getSource().getFeatures().forEach((feature) => {
      if (typeof feature.get('route_ref') === 'number') {
        //this._traceDrawLayerArrows.getSource().addFeature(feature.clone());
        this._traceDrawLayerArrows.getSource().addFeature(feature);
      }
    });

    if (this.isInEditingMode() && this._editingModeType() === 'LineString') {
      this._copyFeaturesToTraceDrawLayerModifiable();
    }

    if (count > 0) {
      //add start and end marker
      let feature = new Feature();
      let oneRoute = this._traceRouteData.routes[0];
      feature.setId('startPoint');
      feature.set('startPoint', true);
      feature.set('fileDownloadIgnore', true);
      feature.setGeometry(
        new Point([oneRoute.routePoints[0].x, oneRoute.routePoints[0].y])
      );
      this._traceDrawLayerPoint.getSource().addFeature(feature);

      if (count > 1 || this._traceRouteData.routes[0].routePoints.length > 1) {
        feature = new Feature();
        oneRoute =
          this._traceRouteData.routes[this._traceRouteData.routes.length - 1];
        feature.setId('endPoint');
        feature.set('endPoint', true);
        feature.set('fileDownloadIgnore', true);
        feature.setGeometry(
          new Point([
            oneRoute.routePoints[oneRoute.routePoints.length - 1].x,
            oneRoute.routePoints[oneRoute.routePoints.length - 1].y,
          ])
        );
        this._traceDrawLayerPoint.getSource().addFeature(feature);
      }
    }

    if (this.isInEditingMode() && this._modifyInterAction && this._editingModeType() === 'LineString') {
      this._modifyInterAction.setActive(true);
      this._snapInterAction.setActive(true);
    }
  }

  private _copyFeaturesToTraceDrawLayerModifiable() {
    this._traceDrawLayerModifiable.getSource().clear();
    this._traceDrawLayerModifiableHighlight.getSource().clear();
    if (this.isInEditingMode()) {
      if (this._editingModeType() === 'Point') {
        this._traceDrawLayerPoint.getSource().getFeatures().forEach((feature) => {
          if (typeof feature.get('wayPoint_ref') === 'number') {
            this._traceDrawLayerModifiable.getSource().addFeature(feature.clone());
          }
        });
      } else {
        this._traceDrawLayerLineString.getSource().getFeatures().forEach((feature) => {
          if (typeof feature.get('route_ref') === 'number') {
            this._traceDrawLayerModifiable.getSource().addFeature(feature.clone());
          }
        });
      }
    }
  }

  fadeInLayer(
    layerObj: Layer,
    isStart: boolean,
    canInterruptPrevious: boolean,
    endOpacity: number,
    milliseconds: number
  ) {
    try {
      const layerId = layerObj ? layerObj.get('customId') : null;

      if (layerId) {
        const fadeInterval = 15;

        if (isStart) {
          if (!this._fadeInLayerTimers) {
            this._fadeInLayerTimers = {};
          }

          if (
            this._fadeInLayerTimers[layerId] &&
            typeof this._fadeInLayerTimers[layerId].timerId === 'number'
          ) {
            if (canInterruptPrevious) {
              this._forceClearFadeInLayer(layerObj);
            } else {
              return;
            }
          }

          layerObj.setOpacity(0);

          this._fadeInLayerTimers[layerId] = {
            value: 0,
            endOpacity:
              typeof endOpacity === 'number' &&
                endOpacity > 0 &&
                endOpacity <= 1
                ? endOpacity
                : 1,
            timerId: null,
          };

          const that = this;
          this._fadeInLayerTimers[layerId].timerId = setInterval(() => {
            that.fadeInLayer(layerObj, false, false, endOpacity, milliseconds);
          }, fadeInterval);
        } else {
          //not yet cleared before finish?
          if (this._fadeInLayerTimers && this._fadeInLayerTimers[layerId]) {
            //round to 3 decimal numbers?
            this._fadeInLayerTimers[layerId].value += 1.0 / (milliseconds / fadeInterval);

            if (this._fadeInLayerTimers[layerId].value > endOpacity) {
              layerObj.setOpacity(endOpacity);
              this._forceClearFadeInLayer(layerObj);
            } else {
              layerObj.setOpacity(this._fadeInLayerTimers[layerId].value);
            }
          }
        }
      }
    } catch (error) {
      console.log('error during layer fade in event : ' + error);
      try {
        layerObj.setOpacity(
          typeof endOpacity === 'number' && endOpacity > 0 && endOpacity <= 1
            ? endOpacity
            : 1
        );
      } catch (err) {
        console.log('error during layer fade in event (2) : ' + error);
      }
    }
  }

  private _forceClearFadeInLayer(layerObj: Layer) {
    if (this._fadeInLayerTimers && layerObj) {
      let layerId = layerObj.get('customId');

      if (layerId && this._fadeInLayerTimers[layerId]) {
        if (typeof this._fadeInLayerTimers[layerId].timerId === 'number') {
          clearInterval(this._fadeInLayerTimers[layerId].timerId);
          this._fadeInLayerTimers[layerId].timerId = null;
        }

        layerObj.setOpacity(
          this._fadeInLayerTimers[layerId].endOpacity === 'number'
            ? this._fadeInLayerTimers[layerId].endOpacity
            : 1
        );

        this._fadeInLayerTimers[layerId] = null;
      }
    }
  }

  private _forceClearAllFadeInLayers() {
    if (this._fadeInLayerTimers) {
      for (let key in this._fadeInLayerTimers) {
        if (this._fadeInLayerTimers[key]) {
          this._forceClearFadeInLayer(this._olLayerService.getLayerById(key) as Layer);
        }
      }
      this._fadeInLayerTimers = null;
    }
  }

  private _addFeaturesToSuggestionsLayer(features: Feature[]) {
    if (this._isActive()) {
      if (features && features.length > 0) {
        const centroids: Feature[] = [];
        features.forEach((feature: Feature, index: number) => {
          feature.set("isSuggestion", true);
          feature.set("isVisible", false);
          feature.set("isSuggestionCentroid", false);
          //feature.setId("suggestion_" + index);

          const centroid = new Feature(new Point(olExtent.getCenter(feature.getGeometry().getExtent())));
          centroid.set("isSuggestion", true);
          centroid.set("isVisible", true);
          centroid.set("isSuggestionCentroid", true);
          //centroid.setId("suggestion_centroid_" + index);
          centroid.set("route_id", feature.get("route_id"));
          const labelForRouteId = TraceSuggestionsComponent.findTextForRouteId(this._traceSuggestionsConfigService.configData().routes, feature.get("route_id"), true, this._translateService.getCurrentLang());
          centroid.set("label", labelForRouteId ? labelForRouteId.length > 53 ? labelForRouteId.substring(0, 50) + '...' : labelForRouteId : null);
          centroids.push(centroid);
        });

        this._traceDrawLayerSuggestions.getSource().addFeatures(features);
        this._traceDrawLayerSuggestions.getSource().addFeatures(centroids);
      }
    }
  }

  toggleSuggestionsLayer(show: boolean) {
    //suggestions layer needs to be available
    if (!this._isActive()) {
      if (show) {
        this._startUp();
      } else {
        return;
      }
    }

    if (this._isActive()) {
      this._traceDrawLayerSuggestions.getSource().clear();

      if (show) {
        const features: Feature[] = TraceSuggestionsComponent.convertConfigDataToFeatures(this._traceSuggestionsConfigService.configData(), this._viewService.getProjection().getCode());
        if (features.length > 0) {
          this._addFeaturesToSuggestionsLayer(features);
        }
      }
    }
  }

  highlightSuggestionOnMap(route_id: string, highligth: boolean) {

    if (this._isActive() && route_id) {
      this._traceDrawLayerSuggestions.getSource().getFeatures().every((feature: Feature) => {
        if (feature.get("isSuggestionCentroid") && feature.get("route_id") === route_id) {
          if (highligth) {
            feature.set("highlight", true);
            /*
            this._mapService
              .getCurrentMap()
              .getView()
              .fit(olExtent.buffer(feature.getGeometry().getExtent(), 50000), {
                size: this._mapService.getCurrentMap().getSize(),
                padding: [0, 0, 0, 0],
                duration: 500,
                maxZoom: this._maxAutoZoomForPrj - 1
              });
            */
          } else {
            feature.unset("highlight");
          }
          return false;
        }
        return true;
      });
    }
  }

  private _findSuggestionAtCoordinate(pixel: Pixel, wasManualClick: boolean, mapId?: string) {

    let foundSuggestion = false;

    const features = this._mapService
      .getCurrentMap()
      .getFeaturesAtPixel(pixel, {
        hitTolerance: this._snapAndSearchPixelTolerance,
        layerFilter: (l) => {
          return l.get('customId') === 'traceDrawLayerSuggestions';
        },
      });

    foundSuggestion = features.length > 0;
    if (foundSuggestion) {
      //do in seperate thread, return already result foundSuggestion below
      window.setTimeout(() => {
        //feature closest to click, normally first from array
        const routeId = features[0].get("route_id");
        let routeFeature: Feature = null;

        this._traceDrawLayerSuggestions.getSource().getFeatures().every((feature: Feature) => {
          //only available as center point
          if (!feature.get("isSuggestionCentroid") && feature.getGeometry().getType() !== 'Point' && feature.get("route_id") === routeId) {
            routeFeature = feature;
            setTimeout(() => {
              feature.set("isVisible", true);
            }, 250);

            return false;
          }
          return !routeFeature;
        });

        /*
        this._mapService
          .getCurrentMap()
          .getView()
          .fit(olExtent.buffer(routeFeature ? routeFeature.getGeometry().getExtent() : features[0].getGeometry().getExtent(), 2000), {
            size: this._mapService.getCurrentMap().getSize(),
            padding: [0, 0, 0, 0],
            duration: 500,
            maxZoom: this._maxAutoZoomForPrj
          });
        */

        if (wasManualClick) {

          let closedPopup = false;
          if (this._popupService.isPopupAlreadyOnMap()) {
            closedPopup = true;
            this._popupService.hide();
          }

          //above extent route [centerX, maxY]
          //const centerX = olExtent.getCenter(routeFeature ? routeFeature.getGeometry().getExtent() : features[0].getGeometry().getExtent())[0];
          //const maxY = olExtent.getTopRight(routeFeature ? routeFeature.getGeometry().getExtent() : features[0].getGeometry().getExtent())[1];
          const center = olExtent.getCenter(features[0].getGeometry().getExtent());
          const titleForRouteId = TraceSuggestionsComponent.findTextForRouteId(this._traceSuggestionsConfigService.configData().routes, routeId, true, this._translateService.getCurrentLang());
          const descriptionForRouteId = TraceSuggestionsComponent.findTextForRouteId(this._traceSuggestionsConfigService.configData().routes, routeId, false, this._translateService.getCurrentLang());
          const distanceForRouteId = TraceSuggestionsComponent.findDistanceForRouteId(this._traceSuggestionsConfigService.configData().routes, routeId, true);
          const elevationForRouteId = TraceSuggestionsComponent.findDistanceForRouteId(this._traceSuggestionsConfigService.configData().routes, routeId, false);
          let popupProperties: PopupData = {
            coordinates: center,
            type: 'TraceSuggestionInfo',
            title: 'trace.suggestion_info',
            content:
              '<div class="popover-route-title mat-title-small">'
              + titleForRouteId
              + '</div>'
              + '<div class="routeData flex-container">'
              + '<a title="' + this._translateService.instant('trace.total-distance') + '"><span class="ngi mat-icon measure"></span> ' + TraceRouteData.formatRouteLength(distanceForRouteId, true, '0 <span>m</span>') + '</a>'
              + '<a title="' + this._translateService.instant('trace.total-height') + '"><div class="ngi"><span class="ngi mat-icon elevation-profile"></span></div> ' + TraceRouteData.formatRouteLength(elevationForRouteId, true, '0 <span>m</span>') + '</a>'
              + '</div>',
            zoomToFeatureLink: true,
            zoomlevel: 7,
            customEvent: {
              eventId: 'TraceSuggestionInfo',
              data: { routeId: routeId },
              label: this._translateService.instant(
                'trace.open-panel'
              ),
              buttonType: 'tonal',
              buttonClass: 'ngi-green-blue-button'
            },
            className: 'TraceSuggestionInfo',
            mapId: mapId ? mapId : 'main'
          };
          this._popupService.onPopupDestroy$.pipe(take(1)).subscribe(() => {
            //also possible
            //this.hideSuggestionsPreview();
            routeFeature?.set("isVisible", false);
          })

          window.setTimeout(() => { this._popupService.add(popupProperties); }, closedPopup ? 300 : 0);
        }
      }, 0);
    }

    return foundSuggestion;
  }

  /* ************************************************************************************************************************************************************************************
  * suggestions config data
  *************************************************************************************************************************************************************************************/

  /**
   * 
   * @param forceShowSuggestionsLayer if true, we will ignore the original value from the config file "form_options.route_centroids_on_map.form_checked" and display the layer, if necessary we update the config value also to true
   * @param setForceValueAsdefault if true, then we update the value of "form_options.route_centroids_on_map.form_checked" in the config file with the value of parameter forceShowSuggestionsLayer
   * @param retrialCount optional, this param should not be added by another method when calling this method, or with value 0
   * /
  loadSuggestions(forceShowSuggestionsLayer: boolean, setForceValueAsdefault: boolean, retrialCount: number = 0) {
    if (this._traceSuggestionsConfigService.configDownloadStatus() === 'init') {

      this._traceSuggestionsConfigService.setDownloadstatus('busy');

      if (typeof this._appEnvironmentConfig.TRACE_SUGGESTIONS_CONFIG === "string" && (/^http(s)?\/\//.test(this._appEnvironmentConfig.TRACE_SUGGESTIONS_CONFIG) || this._appEnvironmentConfig.TRACE_SUGGESTIONS_CONFIG.indexOf('assets/') > -1)) {
        this._httpService.get(this._appEnvironmentConfig.TRACE_SUGGESTIONS_CONFIG).subscribe({
          next: (data) => {
            const checkConfigData = TraceSuggestionsComponent.checkConfigData(data);

            if (setForceValueAsdefault) {
              checkConfigData.form_options.route_centroids_on_map.form_checked = forceShowSuggestionsLayer;
            }

            this._traceSuggestionsConfigService.setDownloadedConfigData(checkConfigData);
            //normally already set to done by previous method
            this._traceSuggestionsConfigService.setDownloadstatus('done');

            if (this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked) {
              this.toggleSuggestionsLayer(true);

              / *
              open the trace drawer?
              if(!this._browserSniffer.isPhone()) {
                this.openTraceTab(TraceDrawerType.SUGGESTIONS_ROUTE);
              }
              * /
            }

            this.checkForWrongSuggestionLang();
          },
          error: (error) => {
            this._traceSuggestionsConfigService.setDownloadstatus('init');
            console.error("error downloading suggestions config", error);
          }
        });
      } else {
        this._traceSuggestionsConfigService.setDownloadstatus('init');
      }
    } else if (this._traceSuggestionsConfigService.configDownloadStatus() !== 'done' && (typeof retrialCount !== 'number' || retrialCount < 20)) {
      const forceShow = forceShowSuggestionsLayer;
      const setDefault = setForceValueAsdefault;
      const count = typeof retrialCount === 'number' && retrialCount >= 0 ? (retrialCount + 1) : 0;
      window.setTimeout(() => {
        this.loadSuggestions(forceShow, setDefault, count);
      }, 300);

    } else if (this._traceSuggestionsConfigService.configDownloadStatus() === 'done' && this._traceSuggestionsConfigService.configData() && (this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked || forceShowSuggestionsLayer)) {
      if (setForceValueAsdefault) {
        this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked = forceShowSuggestionsLayer;
      }

      if (this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked) {
        this.toggleSuggestionsLayer(true);
      }
    }
  }
  */
  private _loadSuggestions() {
    if (this._traceSuggestionsConfigService.configDownloadStatus() === 'done') {
      if (this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked) {
        this.toggleSuggestionsLayer(true);
        /*
        open the trace drawer?
        if(!this._browserSniffer.isPhone()) {
          this.openTraceTab(TraceDrawerType.SUGGESTIONS_ROUTE);
        }
        */
      }
      
      this.checkForWrongSuggestionLang();
    }
  }

  changedSuggestionsSetting(forDefaultMap: boolean) {
    if (forDefaultMap) {
      this._traceSuggestionsConfigService.configData().form_options.default_layer.form_checked = !this._traceSuggestionsConfigService.configData().form_options.default_layer.form_checked;
    } else {
      this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked = !this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked;
      //this._toggleRoutesToMap(this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked);
      this.toggleSuggestionsLayer(this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked);
    }
  }

  checkForWrongSuggestionLang() {
    let wasWrongSuggestionLang = false;
    if (this._isActive()
      && this._activePermalinkParam
      && this._traceSuggestionsConfigService.configDownloadStatus() === 'done'
      && this._traceSuggestionsConfigService.configData().route_id_prefix
      && this._activePermalinkParam.indexOf(this._traceSuggestionsConfigService.configData().route_id_prefix) === 0) {
      this._traceSuggestionsConfigService.configData().routes.every((route) => {
        if (this._activePermalinkParam.substring(this._traceSuggestionsConfigService.configData().route_id_prefix.length).indexOf(route.route_id) === 0) {
          const newPermaLinkParam = this._traceSuggestionsConfigService.configData().route_id_prefix + route.route_id + TraceSuggestionsComponent.findRouteIdSuffixForRequestedLanguage(this._traceSuggestionsConfigService.configData().route_id_suffix, this._translateService.getCurrentLang());

          if (this._activePermalinkParam !== newPermaLinkParam) {
            wasWrongSuggestionLang = true;
            this.openSuggestionsRoute(route.route_id, true);
          }
          return false;
        }
        return true;
      })
    }
    return wasWrongSuggestionLang;
  }

  openSuggestionsRoute(route_id: string, isSwitchToSameRouteOtherlanguage: boolean) {
    if (this._popupService.isPopupAlreadyOnMap()) {
      this._popupService.hideInCaseOfType('TraceSuggestionInfo');
    }

    //maybe there was a preview via the zoom button
    //then there was no popup and no close event that hide the feature
    this.hideSuggestionsPreview();

    //if loading the same route in the other language fails, you still have this version
    if (!isSwitchToSameRouteOtherlanguage) {
      this.resetTraceRouteData(true);
    }

    if (this._traceSuggestionsConfigService.configData().form_options.default_layer.form_checked
      && this._traceSuggestionsConfigService.configData().form_options.default_layer.layer_id) {

      const primaryLayerId = Array.isArray(this._traceSuggestionsConfigService.configData().form_options.default_layer.layer_id) ? this._traceSuggestionsConfigService.configData().form_options.default_layer.layer_id[0] : this._traceSuggestionsConfigService.configData().form_options.default_layer.layer_id;

      if (typeof primaryLayerId === 'string' && primaryLayerId !== this._layerService.getActiveBaselayerId()) {
        //set layer
        //if (this._permaLinkService.getUrlParam('viewmode') !== 'swipe') {
        this._layerService.getBaseLayers().forEach(layer => {
          if (layer.id === primaryLayerId) {
            this._layerService.setSelectedBaselayer(layer);
            if (this._permaLinkService.getUrlParam('viewmode') !== 'swipe') {
              this._layerService.showSelectedBaselayer(layer);
            }
            return false;
          }
          return true;
        });
        //}
      }

      const newSecondaryLayerId = Array.isArray(this._traceSuggestionsConfigService.configData().form_options.default_layer.layer_id) && this._traceSuggestionsConfigService.configData().form_options.default_layer.layer_id.length > 1 ? this._traceSuggestionsConfigService.configData().form_options.default_layer.layer_id[1] : null;
      const viewMode = this._permaLinkService.getUrlParam('viewmode');

      if (typeof newSecondaryLayerId === 'string' && typeof viewMode === 'string' && viewMode !== 'swipe' && viewMode !== 'split') {
        //console.log(newSecondaryLayerId)
        //console.log(viewMode)
        this._permaLinkService.updateViewModeParam('swipe')
      }

      //tell other components a suggested route is opening (the swipe component will check if it needs to switch the primary layer)
      this._toggleEventService.toggleEvent({ eventName: ToggleEventNameType.OPEN, forwardedFor: "trace-suggestion" })
    }

    const completeRouteId = this._traceSuggestionsConfigService.configData().route_id_prefix + route_id + TraceSuggestionsComponent.findRouteIdSuffixForRequestedLanguage(this._traceSuggestionsConfigService.configData().route_id_suffix, this._translateService.getCurrentLang());

    this.loadTraceRouteDataFromExternalStorage(true, this._appEnvironmentConfig.AC_TRACE_DOWNLOAD_APP_ID, completeRouteId).subscribe();
  }

  zoomToSuggestionOnMap(route_id: string, isRecursiveCall?: boolean) {
    if (this._isActive() && route_id) {
      if (this._popupService.isPopupAlreadyOnMap()) {
        this._popupService.hide();
      }

      //maybe there was a preview via the zoom button
      //then there was no popup and no close event that hides this feature
      this.hideSuggestionsPreview();

      if (this._browserSniffer.isPhone()) {
        this.closeSuggestionsDialog();

      }

      let previewFeature: Feature = null;
      this._traceDrawLayerSuggestions.getSource().getFeatures().every((feature: Feature) => {
        if (!feature.get("isSuggestionCentroid") && feature.get("route_id") === route_id) {
          previewFeature = feature;
          return false;
        }
        return true;
      });

      if (previewFeature) {
        //feature is already added to the map
        //this can be because the user actived the layer (all features are added to layer) 
        //or it was added by the current/previous zoom action in the else clause below (feature is added on the fly during zoom action)

        if (!this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked) {
          //in this case the centroid was hidden by call to hideSuggestionsPreview
          this._traceDrawLayerSuggestions.getSource().getFeatures().every((feature: Feature) => {
            if (feature.get("isSuggestionCentroid") && feature.get("route_id") === route_id) {
              feature.set("isVisible", true);
              return false;
            }
            return true;
          });
        }

        this._mapService
          .getCurrentMap()
          .getView()
          .fit(olExtent.buffer(previewFeature.getGeometry().getExtent(), previewFeature.getGeometry().getType() === 'Point' ? 1000 : 10), {
            size: this._mapService.getCurrentMap().getSize(),
            padding: [0, 0, 0, 0],
            duration: 500,
            maxZoom: this._maxAutoZoomForPrj - 1,
            callback: () => {
              window.setTimeout(() => {
                //previewFeature.set("isVisible", true);
                this._findSuggestionAtCoordinate(this._mapService.getMap().getPixelFromCoordinate(olExtent.getCenter(previewFeature.getGeometry().getExtent())), this._browserSniffer.isPhone() ? true : false);
              }, 300);
            }
          });

      } else if (!isRecursiveCall && !this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked) {
        //the layer has normally no features, or they were added here for the zoom action 
        this._traceSuggestionsConfigService.configData().routes.every(route => {
          if (route.route_id === route_id) {

            const geometryType = route.show && route.route_line_coordinates.length > 0 ? TraceSuggestionsComponent.getGeometryTypeForCoordinates(route.route_line_coordinates) : null;
            if (geometryType) {
              const geometry = geometryType === 'MultiLineString' ? new MultiLineString(<number[][][]>route.route_line_coordinates) : geometryType === 'LineString' ? new LineString(<number[][]>route.route_line_coordinates) : new Point(<number[]>route.route_line_coordinates);
              if (this._viewService.getProjection().getCode() !== route.route_epsg) {
                geometry.transform(route.route_epsg, this._viewService.getProjection().getCode());
              }
              const feature = new Feature(geometry);
              feature.set("route_id", route.route_id);
              this._addFeaturesToSuggestionsLayer([feature]);

              this.zoomToSuggestionOnMap(route_id, true);
            }

            return false;
          }
          return true;
        })
      }
    }
  }

  hideSuggestionsPreview() {
    if (this._isActive()) {
      this._traceDrawLayerSuggestions.getSource().getFeatures().forEach((feature: Feature) => {
        if (!this._traceSuggestionsConfigService.configData().form_options.route_centroids_on_map.form_checked
          || !feature.get("isSuggestionCentroid")) {
          feature.set("isVisible", false);
        }
      });
    }
  }

  /* ************************************************************************************************************************************************************************************
   * auto routing methods
   *************************************************************************************************************************************************************************************/

  isBusyWithAutoRouting() {
    return this.autoRouting() && this._autoRoutingBusy;
  }

  private _cancelAutoRoutingRequest() {
    if (this._autoCancellingFetchAutoRoutingTimer > 0) {
      window.clearTimeout(this._autoCancellingFetchAutoRoutingTimer);
      this._autoCancellingFetchAutoRoutingTimer = -1;
    }
    const wasBusy = this._fetchAutoRoutingDataTimestamp > 0 || this._autoRoutingBusy;
    //if(this.autoRouting() && this._fetchAutoRoutingDataTimestamp > 0 && this._autoRoutingBusy)
    this._fetchAutoRoutingDataTimestamp = 0
    this._autoRoutingBusy = false;
    if (wasBusy) {
      this._cancellingFetchAutoRoutingDataSubject.next();
      this._traceDrawLayerLineString.changed();
      this._mapService.getMap().renderSync();
    }
  }
  private _resetAfterAutoRoutingSuccess() {
    if (this._autoCancellingFetchAutoRoutingTimer > 0) {
      window.clearTimeout(this._autoCancellingFetchAutoRoutingTimer);
      this._autoCancellingFetchAutoRoutingTimer = -1;
    }
    this._fetchAutoRoutingDataTimestamp = 0
    this._autoRoutingBusy = false;
  }

  /**
   * @parame routeIndex
   * @param oldRoute with this data we can quickly return to the old state if routing fails
   * @param isModifyAction true if called by modifyend event, false if called by drawend event
   * @param routePointIndexOfModifyAction the index of the routepoint during a modify action; if param isModifyAction is false, then this parameter is ignored
   * @param autoRoutingAfterPointRemoval a point was removed (manually by the user or automatically during modify); if param isModifyAction is false, then this parameter is ignored
   * @returns 
   */
  private _handleMapInteraction_autorouting(routeIndex: number, oldRoute: TraceRouteType, isModifyAction: boolean, routePointIndexOfModifyAction: number, autoRoutingAfterPointRemoval: boolean) {
    if (this._autoRoutingBusy) {
      this._handleMapInteraction_autorouting_error(routeIndex, oldRoute, false);
      return;
    }

    this._autoRoutingIndexAfterModify = isModifyAction ? routePointIndexOfModifyAction : -1;
    this._isAutoRoutingAfterDragging = isModifyAction && !autoRoutingAfterPointRemoval;
    this._autoRoutingBusy = true;

    if (isModifyAction && (routePointIndexOfModifyAction < 0 || (!this.getRoutePoint(routeIndex, routePointIndexOfModifyAction)))) {
      this._handleMapInteraction_autorouting_error(routeIndex, oldRoute, true);
      return;
    }

    const routePointIndexes: number[] = []
    if (routeIndex >= 0 && routeIndex < this._traceRouteData.routes.length) {
      for (let i = 0; i < this._traceRouteData.routes[routeIndex].routePoints.length; i++) {
        if (i === 0 || i === this._traceRouteData.routes[routeIndex].routePoints.length - 1 || !this._traceRouteData.routes[routeIndex].routePoints[i].autoDraw) {
          routePointIndexes.push(i);
        }
      }
    }

    if (routePointIndexes.length === 0) {
      this._handleMapInteraction_autorouting_error(routeIndex, oldRoute, true);
      return;
    }

    //check if every index is existing?
    for (let i = 0; i < routePointIndexes.length; i++) {
      if (!this.getRoutePoint(routeIndex, routePointIndexes[i])) {
        this._handleMapInteraction_autorouting_error(routeIndex, oldRoute, true);
        return;
      }
    }

    //we will only update the necessary segment of the route 
    let segmentIndexes: number[] = [];
    if (routePointIndexes.length > 1) {
      if (isModifyAction) {
        let count = 0;
        routePointIndexes.every((routePointIndex) => {
          if (routePointIndex === routePointIndexOfModifyAction) {
            if (!autoRoutingAfterPointRemoval && count > 0) {
              segmentIndexes.push(routePointIndexes[count - 1]);
            }
            segmentIndexes.push(routePointIndexOfModifyAction);
            if (count < routePointIndexes.length - 1) {
              segmentIndexes.push(routePointIndexes[count + 1]);
            }
            return false;
          }
          count++;
          return true;
        });
      } else {
        //user drew new point at end of line, so take last segment
        segmentIndexes = [routePointIndexes[routePointIndexes.length - 2], routePointIndexes[routePointIndexes.length - 1]];
      }
    } else {
      //not a line yet, only start point
      segmentIndexes = routePointIndexes;
    }

    if (segmentIndexes.length === 0) {
      this._handleMapInteraction_autorouting_error(routeIndex, oldRoute, true);
      return;
    }

    //did the user click on the start point to close the loop? 
    //or did he drop start on end or vice versa?
    const startPoint = this._traceRouteData.routes.length > 0 && this._traceRouteData.routes[0].routePoints.length > 0 ? this._getRoutePointAsPoint(0, 0) : null;
    const endPoint = this._traceRouteData.routes[routeIndex].routePoints.length > 0 && (routeIndex > 0 || this._traceRouteData.routes[routeIndex].routePoints.length > 1) ? this._getRoutePointAsPoint(routeIndex, this._traceRouteData.routes[routeIndex].routePoints.length - 1) : null;
    const closingLoop = (!isModifyAction || ((routeIndex === 0 && routePointIndexOfModifyAction === 0) || (routeIndex === (this._traceRouteData.routes.length - 1) && routePointIndexOfModifyAction === (this._traceRouteData.routes[routeIndex].routePoints.length - 1)))) && startPoint && endPoint && TraceRouteData.matchingCoordinates(startPoint.getCoordinates(), endPoint.getCoordinates());

    //if the route becomes too long, the line doesn't always appear directly, we could add an extra timeout so that the udpated layer can always render?
    this._traceDrawLayerLineString.changed();
    //window.setTimeout(() => { next part of the function }, startPoint ? 300 : 0);

    const localFetchAutoRoutingDataTimestamp = new Date().getTime();
    this._fetchAutoRoutingDataTimestamp = localFetchAutoRoutingDataTimestamp;

    if (this._autoCancellingFetchAutoRoutingTimer > - 1) {
      window.clearTimeout(this._autoCancellingFetchAutoRoutingTimer);
    }
    this._autoCancellingFetchAutoRoutingTimer = window.setTimeout(() => {
      this._autoCancellingFetchAutoRoutingTimer = -1;
      if (this._autoRoutingBusy && this._fetchAutoRoutingDataTimestamp === localFetchAutoRoutingDataTimestamp) {
        this._handleMapInteraction_autorouting_error(routeIndex, oldRoute, true);
      }
    }, 5000);

    const snapPointToRoadObservables = [];
    segmentIndexes.forEach((segmentIndex) => {
      const routePointCoord: Coordinate = [this._traceRouteData.routes[routeIndex].routePoints[segmentIndex].x, this._traceRouteData.routes[routeIndex].routePoints[segmentIndex].y];
      snapPointToRoadObservables.push(this._snapPointToRoad(routePointCoord /*,localFetchAutoRoutingDataTimestamp*/));
    });

    forkJoin(snapPointToRoadObservables).subscribe(
      //forkJoin snapPointToRoadObservables success
      (snappedPoints: Coordinate[]) => {

        if (!this._isActive()) {
          //user closed module?
          //this._autoRoutingBusy = false;
          this._cancelAutoRoutingRequest();
          return;
        }

        //snappedPoints are in EPSG:4326, we don't transform them yet (_snapLineToRoad)
        if (Array.isArray(snappedPoints) && snappedPoints.length === segmentIndexes.length) {

          if (segmentIndexes.length === 1) {
            //1 index in array: only the start point of the route exists, the route is not a line yet
            let copiedRoute: TraceRouteType = TraceRouteData.deepCopy(this._traceRouteData.routes[routeIndex]);
            const newCoordinate = TraceRouteData.updatePointProjection(snappedPoints[0], 'EPSG:4326', this._viewService.getProjection().getCode())
            copiedRoute.routePoints[segmentIndexes[0]].x = newCoordinate[0];
            copiedRoute.routePoints[segmentIndexes[0]].y = newCoordinate[1];
            copiedRoute.routePoints[segmentIndexes[0]].autoDraw = false;
            this._replaceTraceRouteData_route(copiedRoute, routeIndex, isModifyAction ? TraceRouteDataUpdateType.MOVED_ROUTE : TraceRouteDataUpdateType.ADDED_ROUTEPOINT);

            //also cancels timer
            this._resetAfterAutoRoutingSuccess();
          } else {

            //already cancelled and maybe replaced by new requests, or module closed?
            if (localFetchAutoRoutingDataTimestamp !== this._fetchAutoRoutingDataTimestamp || !this._isActive()) {
              if (!this._isActive() && this._autoRoutingBusy) {
                //this._autoRoutingBusy = false;
                this._cancelAutoRoutingRequest();
              }
              return;
            }

            //the route is already a line with at least two routepoints
            this._snapLineToRoad(snappedPoints /*,localFetchAutoRoutingDataTimestamp*/).subscribe(
              //this._snapLineToRoad success
              (snappedLineCoordinates: Coordinate[]) => {

                //already cancelled and maybe replaced by new requests, or module closed?
                if (localFetchAutoRoutingDataTimestamp !== this._fetchAutoRoutingDataTimestamp || !this._isActive()) {
                  if (!this._isActive() && this._autoRoutingBusy) {
                    //this._autoRoutingBusy = false;
                    this._cancelAutoRoutingRequest();
                  }
                  return;
                }

                //snappedLineCoordinates are in the map projection
                if (Array.isArray(snappedLineCoordinates) && snappedLineCoordinates.length >= (snappedPoints.length)) {

                  const locatedSnappedPoints = [];
                  for (let i = 0; i < snappedPoints.length; i++) {
                    const isStartOrEnd = i === 0 || i === snappedPoints.length - 1;
                    locatedSnappedPoints.push({ coord: TraceRouteData.updatePointProjection(snappedPoints[i], 'EPSG:4326', this._viewService.getProjection().getCode()), foundPosition: isStartOrEnd ? i : -1, distance: isStartOrEnd ? 0 : -1 });
                  }

                  const newRoutePoints: TraceWayPointType[] = [];
                  for (let h = 0; h < snappedLineCoordinates.length; h++) {
                    //start and end are never with value autoDraw true
                    let autoDraw = h > 0 && h < snappedLineCoordinates.length - 1;

                    if (autoDraw && segmentIndexes.length > 2) {
                      //start and end are never with value autoDraw true
                      //if segmentIndexes contained an extra point, apart from start and end, then find that point
                      for (let i = 1; i < locatedSnappedPoints.length - 1; i++) {
                        //maybe the routing didn't give back the exact same point, so check in a circle
                        if (TraceRouteData.coordinatesCloserThanDistance(snappedLineCoordinates[h], locatedSnappedPoints[i].coord, 5)) {
                          const dist = new LineString([snappedLineCoordinates[h], locatedSnappedPoints[i].coord]).getLength();

                          //when foundPosition is not -1 anymore, distance will also be not -1 anymore
                          if (locatedSnappedPoints[i].foundPosition === -1 || dist < locatedSnappedPoints[i].distance) {
                            if (locatedSnappedPoints[i].foundPosition !== -1) {
                              newRoutePoints[locatedSnappedPoints[i].foundPosition].autoDraw = true;
                            }
                            locatedSnappedPoints[i].foundPosition = h;
                            locatedSnappedPoints[i].distance = dist;
                            autoDraw = false;
                          }
                          break;
                        }
                      }
                    }

                    //check for doubles
                    let isMatchingWithNext = h < snappedLineCoordinates.length - 1 && TraceRouteData.matchingCoordinates(snappedLineCoordinates[h], snappedLineCoordinates[h + 1]);

                    if (isMatchingWithNext && h === snappedLineCoordinates.length - 2) {
                      //temporary, needed for the locatedSnappedPoints check above
                      newRoutePoints.push(null);
                      continue;
                    }

                    newRoutePoints.push({ x: snappedLineCoordinates[h][0], y: snappedLineCoordinates[h][1], autoDraw: autoDraw, formattedXY: TraceRouteData.formatPointForHtmlView(new Point(snappedLineCoordinates[h]), this._viewService.getProjection().getCode()) });

                    if (isMatchingWithNext) {
                      //temporary, needed for the locatedSnappedPoints check above
                      newRoutePoints.push(null);
                      h++;
                    }
                  }

                  //remove temporary nulls 
                  newRoutePoints.filter(n => n);

                  //now we have the updated segment, insert it in the old line 
                  let copiedRoute: TraceRouteType = TraceRouteData.deepCopy(this._traceRouteData.routes[routeIndex]);
                  copiedRoute.routePoints.splice(segmentIndexes[0], (segmentIndexes[segmentIndexes.length - 1] - segmentIndexes[0]) + 1, ...newRoutePoints);

                  if (closingLoop && !TraceRouteData.matchingCoordinates(startPoint.getCoordinates(), [copiedRoute.routePoints[copiedRoute.routePoints.length - 1].x, copiedRoute.routePoints[copiedRoute.routePoints.length - 1].y])) {
                    //closing the loop but the resulting endpoint doesn't perfectly match the startpoint
                    if (TraceRouteData.coordinatesCloserThanDistance(startPoint.getCoordinates(), [copiedRoute.routePoints[copiedRoute.routePoints.length - 1].x, copiedRoute.routePoints[copiedRoute.routePoints.length - 1].y], 5)) {
                      copiedRoute.routePoints[copiedRoute.routePoints.length - 1].x = startPoint.getCoordinates()[0];
                      copiedRoute.routePoints[copiedRoute.routePoints.length - 1].y = startPoint.getCoordinates()[1];
                    } else {
                      copiedRoute.routePoints[copiedRoute.routePoints.length - 1].autoDraw = true;
                      copiedRoute.routePoints.push({ x: startPoint.getCoordinates()[0], y: startPoint.getCoordinates()[1], autoDraw: false, formattedXY: TraceRouteData.formatPointForHtmlView(startPoint, this._viewService.getProjection().getCode()) });
                    }
                  }

                  this._replaceTraceRouteData_route(copiedRoute, routeIndex, isModifyAction ? TraceRouteDataUpdateType.MOVED_ROUTE : TraceRouteDataUpdateType.ADDED_ROUTEPOINT);

                  //also cancels timer
                  this._resetAfterAutoRoutingSuccess();
                } else {
                  this._handleMapInteraction_autorouting_error(routeIndex, oldRoute, true);
                }
              },
              //this._snapLineToRoad error
              (error) => {
                this._handleMapInteraction_autorouting_error(routeIndex, oldRoute, true);
                //console.error(error);
              });
          }
        } else {
          this._handleMapInteraction_autorouting_error(routeIndex, oldRoute, true);
        }
      },
      //forkJoin snapPointToRoadObservables error
      (error) => {
        this._handleMapInteraction_autorouting_error(routeIndex, oldRoute, true);
        //console.error(error);
      });
  }

  private _handleMapInteraction_autorouting_error(routeIndex: number, oldRoute: TraceRouteType, resetAutoRoutingBusy: boolean) {

    if (!this._isActive()) {
      //user closed module?
      if (resetAutoRoutingBusy) {
        //this._autoRoutingBusy = false;
        this._cancelAutoRoutingRequest();
      }
      return;
    }

    if (resetAutoRoutingBusy) {
      //routing failed, we set the temporary straight segment(s) as finalized segments(=color of line changes)
      //this._autoRoutingBusy = false;
      this._cancelAutoRoutingRequest();
      this._updateRoutesOnMap();
      this._broadcastTraceRouteDataUpdate(TraceRouteDataUpdateType.MOVED_ROUTE_ERROR);
      this._alertService.info(this._translateService.instant('trace.create-route.auto-routing-failed'), { 'autoClose': true });
    } else {
      //this normally means that a previous routing process is still busy, we remove the last change
      //no error message
      if (oldRoute) {
        this._replaceTraceRouteData_route(oldRoute, routeIndex, TraceRouteDataUpdateType.UNDO);
      }
      /* 
      else {
        this.setTraceRouteData_routes([], TraceRouteDataUpdateType.UNDO, true);
      }
      */
    }
  }

  /**
   * 
   * @param coordinate coordinate in current map projection
   * @returns Observable<Coordinate>, where coordinate will be in EPSG:4326
   */
  private _snapPointToRoad(coordinate: Coordinate /*,localFetchAutoRoutingDataTimestamp: number*/): Observable<Coordinate> {
    const coord4326 = TraceRouteData.updatePointProjection(coordinate, this._viewService.getProjection().getCode(), 'EPSG:4326');
    return this._httpService.get(this._url_osrm_nearest + coord4326.join()).pipe(
      takeUntil(this._cancellingFetchAutoRoutingDataSubject)
      /*, takeWhile(() => localFetchAutoRoutingDataTimestamp === this._fetchAutoRoutingDataTimestamp)*/
    ).pipe(
      map((response: any) => {
        if (response && typeof response.code === 'string' && response.code.toLowerCase() === 'ok' && Array.isArray(response.waypoints) && response.waypoints.length > 0 && TraceRouteData.isCoordinate(response.waypoints[0].location)) {
          return response.waypoints[0].location;
        }
        else {
          throw new Error(response ? JSON.stringify(response) : "invalid server response");
        }
      }),
      catchError(error => {
        //throw new Error("we couldn't snap point to road: " + JSON.stringify(error));
        console.error("we couldn't snap point to road:", error)
        return [];
      })
    );
  };

  /**
   * 
   * @param coordinates coordinates in EPSG:4326
   * @returns Observable<Coordinate[]>, where coordinates will be in current map projection
   */
  private _snapLineToRoad(coordinates: Coordinate[] /*,localFetchAutoRoutingDataTimestamp: number*/): Observable<Coordinate[]> {
    let coordinateString = "";
    coordinates.forEach((coordinate) => {
      coordinateString += (coordinateString.length > 0 ? ';' : '') + coordinate.join();
    });

    return this._httpService.get(this._url_osrm_route + coordinateString + (this._url_osrm_route_maxdetail ? '?overview=full' : ''))
      .pipe(
        takeUntil(this._cancellingFetchAutoRoutingDataSubject)
        /*, takeWhile(() => localFetchAutoRoutingDataTimestamp === this._fetchAutoRoutingDataTimestamp)*/
      )
      .pipe(
        map((response: any) => {
          if (response
            && typeof response.code === "string"
            && response.code.toLowerCase() === 'ok'
            && Array.isArray(response.routes)
            && response.routes.length > 0
            && response.routes[0].geometry) {
            try {
              const route = new Polyline({
                factor: 1e5
              }).readGeometry(response.routes[0].geometry, {
                dataProjection: 'EPSG:4326',
                featureProjection: this._viewService.getProjection()
              });
              if (route instanceof LineString) {
                return (route.simplify(0.6) as LineString).getCoordinates();
              }
            } catch (error) {
              //we throw the error below
            }
          }

          throw new Error(response ? JSON.stringify(response) : "invalid server response");
        }),
        catchError(error => {
          //throw new Error("we couldn't snap line to road: " + JSON.stringify(error));
          console.error("we couldn't snap line to road:", error)
          return [];
        })
      );
  };

  /* ************************************************************************************************************************************************************************************
  * elevation profile 
  *************************************************************************************************************************************************************************************/

  canUpdateElevationWhenPanelClosed() {
    return this._elevationUpdateWhenPanelClosed;
  }

  isElevationProfileComponentOpen() {
    return document.getElementById("elevationProfile").classList.contains("shown");
  }

  isElevationProfileComponentBuilding() {
    return document.getElementById("elevationProfile").classList.contains("building");
  }

  toggleElevationProfile() {
    const wasOpen = this.isElevationProfileComponentOpen();
    this._toggleEventService.toggleEvent({ eventName: wasOpen ? ToggleEventNameType.CLOSE_COMMAND : ToggleEventNameType.OPEN_COMMAND, forwardedFor: "elevation-profile" });

    if (!wasOpen && !this.isElevationProfileComponentBuilding() && !this.hasElevationProfilePointsData()) {
      window.setTimeout(() => {
        this.renewElevationProfileData(false);
      }, 300);
    }
  }
  closeElevationProfile() {
    this._toggleEventService.toggleEvent({ eventName: ToggleEventNameType.CLOSE_COMMAND, forwardedFor: "elevation-profile" });
  }

  hasElevationProfilePointsData() {
    //Object.keys(obj).length  is slower then for/in
    //return ((objToCheckIfEmpty: {}): boolean => { for (let i in objToCheckIfEmpty) return true; return false; })(this._elevationProfilePoints ? this._elevationProfilePoints : {})
    return ((objToCheckIfEmpty: {}): boolean => { return !TraceRouteData.isEmptyObject(objToCheckIfEmpty) })(this._elevationProfilePoints ? this._elevationProfilePoints : {})
  }

  private _resetElevationProfileData() {
    this._syncElevationProfileTimestamp = 0;
    //any active http requests in elevation service will be cancelled
    this._elevationService.resetData();

    this._elevationProfilePoints = {};
    if (this._elevationProfilePointOnMap) {
      this._removeElevationProfilePointFromMap();
    }
  }

  /**
   * 
   * @param recursiveCall is recursive call or not
   * @returns 
   */
  renewElevationProfileData(recursiveCall: boolean) {
    let wasResettingProfile = false;
    if (this._syncElevationProfileTimestamp > 0) {
      this._resetElevationProfileData();
      wasResettingProfile = true;
    }

    const syncingTimestamp = new Date().getTime();
    this._syncElevationProfileTimestamp = syncingTimestamp;

    window.setTimeout(() => {

      // const currentRouteDistance = this.getRouteLength();
      // this._elevationDistanceBetweenPoints = currentRouteDistance > 100000 ? 20 : currentRouteDistance > 50000 ? 10 : 5;

      //we only show first route/track, what if there are more routes/tracks?
      const coordinates: Coordinate[] = TraceRouteData.convertTaceRoutesToCoordinates(this._traceRouteData.routes, true);
      //elevation service expects EPSG:3812
      if (this._viewService.getProjection().getCode() !== "EPSG:3812") {
        coordinates.forEach((coordinate) => {
          const newCoordinate = TraceRouteData.updatePointProjection(coordinate, this._viewService.getProjection().getCode(), "EPSG:3812");
          coordinate[0] = newCoordinate[0];
          coordinate[1] = newCoordinate[1];
        });
      }

      if (coordinates && coordinates.length > 1 && this._syncElevationProfileTimestamp === syncingTimestamp) {

        let computePathStarted = false;
        this._elevationService.$isComputingPoints
          .pipe(
            takeUntil(this._elevationService.$cancelFetch),
            takeWhile((stillComputing: boolean) => this._syncElevationProfileTimestamp === syncingTimestamp && ((computePathStarted && stillComputing === true) || !computePathStarted), true)
          )
          .subscribe((stillComputing: boolean) => {
            if (computePathStarted && !stillComputing && this._syncElevationProfileTimestamp === syncingTimestamp) {
              //finished computing points

              this._elevationService.$comingPoints.pipe(take(1)).subscribe((points: SegmentPoint[]) => {
                //we don't need the calculated elevation of all points, only id & x & y, so we can stop listening after the first event (ElevationService > fetchElevations > setMinimalProfile)
                if (points && this._syncElevationProfileTimestamp === syncingTimestamp) {
                  points.forEach((point: SegmentPoint) => {
                    this._elevationProfilePoints['' + point.getId()] = point;
                  });
                }
              });

              let buildingProfileStarted = false;
              this._elevationService.$isBuildingProfile
                .pipe(
                  takeUntil(this._elevationService.$cancelFetch),
                  takeWhile((stillBuilding: boolean) => this._syncElevationProfileTimestamp === syncingTimestamp && ((buildingProfileStarted && stillBuilding === true) || !buildingProfileStarted), true)
                )
                .subscribe((stillBuilding: boolean) => {
                  if (buildingProfileStarted && !stillBuilding && this._syncElevationProfileTimestamp === syncingTimestamp) {
                    //finished building profile
                    this._syncElevationProfileTimestamp = 0;
                  }
                });

              buildingProfileStarted = true;
              this._elevationService.fetchElevations();
            }
          });

        const useAlphaLabels = TraceRouteData.getTraceTTSetting(this._traceRouteData.ttSettings, "wayPointSequenceNumber") === 'alpha' && this._traceRouteData.wayPoints.length < 27;
        const elevationPois: ElevationPoiType[] = [];
        let count = 1;
        this._traceRouteData.wayPoints.forEach((wayPoint, index) => {
          elevationPois.push({
            closestPointId: -1,
            closestPointElevation: -0.1,
            id: index,
            label: '' + (useAlphaLabels ? TraceRouteData.convertWayPointIndexToLetter(count) : count),
            x: wayPoint.x,
            y: wayPoint.y,
          })
          count++;
        });
        this._elevationService.setPois(elevationPois);

        computePathStarted = true;
        this._elevationService.computePath(coordinates, this._viewService.getProjection());
      } else {
        //there was no line on the map yet
        if (this._syncElevationProfileTimestamp === syncingTimestamp) {
          this._syncElevationProfileTimestamp = 0;
        }
      }
    }, wasResettingProfile ? 300 : 10);
  }

  refreshElevationProfileGraph() {
    this._elevationService.refreshGraph();
  }

  private _searchForElevationProfilePoint(pixelCoordinate: Coordinate) {
    if (!this.isElevationProfileComponentOpen()) {
      return;
    }

    //the circle needs to be big enough otherwise the user needs to hover and follow the line very precisely
    const searchCircle = new OlCircle(pixelCoordinate, this._elevationSearchPixelTolerance * this._getPointResolution(pixelCoordinate));
    let foundElevationProfilePoint: SegmentPoint = null;
    let foundElevationProfilePointDistance: number = -1;
    for (let id in this._elevationProfilePoints) {
      if (searchCircle.intersectsCoordinate([this._elevationProfilePoints[id].getX(), this._elevationProfilePoints[id].getY()])) {
        const newDistance = new LineString([pixelCoordinate, [this._elevationProfilePoints[id].getX(), this._elevationProfilePoints[id].getY()]]).getLength();
        if (foundElevationProfilePoint === null || foundElevationProfilePointDistance > newDistance) {
          foundElevationProfilePoint = this._elevationProfilePoints[id];
          foundElevationProfilePointDistance = newDistance;
          if (foundElevationProfilePointDistance < (this._elevationDistanceBetweenPoints / 2)) {
            break;
          }
        }
      }
    }

    if (foundElevationProfilePoint) {
      //in edit mode we don't add this extra point to the route line, the modify point feature is already snapping to the route
      if (this.isInViewMode() && this.isElevationProfileComponentOpen()) {
        //this method will also set this._elevationProfilePointOnMap
        this.addElevationProfilePointToMap(foundElevationProfilePoint, pixelCoordinate);
      } else {
        this._elevationProfilePointOnMap = { point: foundElevationProfilePoint, isMapFeature: false };
      }

      this._profilePathService.activateMapPoint(foundElevationProfilePoint);
    }
  }

  removeElevationProfileTooltip() {
    if (this.isElevationProfileComponentOpen()) {
      this._profilePathService.activateMapPoint(null);
    }
  }

  addElevationProfilePointToMap(elevationProfilePoint: SegmentPoint, snapToCursorPosition: Coordinate) {
    if (this._elevationProfilePointOnMap) {
      if (this._elevationProfilePointOnMap.point.getId() === elevationProfilePoint.getId()) {
        return;
      }
      this._removeElevationProfilePointFromMap();
    }

    let point = snapToCursorPosition ? TraceRouteData.getClosestRoutePointForMapPoint(new Point(snapToCursorPosition), this._traceRouteData.routes, this._viewService.getProjection().getCode()) : new Point([elevationProfilePoint.getX(), elevationProfilePoint.getY()]);

    if (point) {
      const newElevationProfilePoint = new Feature({
        geometry: point
      });
      newElevationProfilePoint.set('elevationProfile', true);
      newElevationProfilePoint.set('fileDownloadIgnore', true);
      this._traceDrawLayerPoint.getSource().addFeature(newElevationProfilePoint)
      this._elevationProfilePointOnMap = { point: elevationProfilePoint, isMapFeature: true };

      //center on point if not in viewport?
      /*
      if (!this._isWayPointInViewPort(newElevationProfilePoint.getGeometry())) {
        //this._viewService.setCenter(newElevationProfilePoint.getGeometry().getCoordinates());
        this._mapService.getMap().getView().animate({
          center: newElevationProfilePoint.getGeometry().getCoordinates(),
          duration: 500,
          easing: olEasing.linear
        });
      }
      */
    }
  }

  private _removeElevationProfilePointFromMap() {
    if (this._elevationProfilePointOnMap && this._elevationProfilePointOnMap.isMapFeature) {
      const featuresToRemove: Feature[] = [];
      this._traceDrawLayerPoint.getSource().getFeatures().forEach((feature) => {
        ///there is normally only one highlighted point
        if (feature.get('elevationProfile')) {
          featuresToRemove.push(feature);
        }
      });
      featuresToRemove.forEach((feature) => {
        this._traceDrawLayerPoint.getSource().removeFeature(feature);
      });
    }
    this._elevationProfilePointOnMap = null;
  }

  private _getRouteHeading(cursorPosition: Coordinate) {
    //until zoomlevel 7 only show the distance on the map with a small dot (see method _getRouteHeadingStyle)
    //from zoomlevel 8 and higher we also show the direction of the route 
    const returnFirstResultOnly = this._viewService.getZoom() <= 7;
    /* */
    const lengthOfHeadingLine =  this._viewService.getZoom() < 10 ? 150 : this._viewService.getZoom() < 11 ? 100 : 50;//135
    const searchRadius = this._viewService.getZoom() <= 7 ? 40 : this._viewService.getZoom() <= 9 ? 20 : 5;

    const routesUntilPoint: LineString[] = TraceRouteData.getRouteUntilPoint(new Point(cursorPosition), this._traceRouteData.routes, this._viewService.getProjection().getCode(), returnFirstResultOnly, searchRadius);

    if (routesUntilPoint.length > 0) {

      let olLineString = new LineString(TraceRouteData.convertTaceRoutesToCoordinates(this._traceRouteData.routes, true));//.simplify();

      //test is normally not necessary, routesUntilPoint would be empty
      if (olLineString) { //&& olLineString.getCoordinates().length >= 2

        const geojsonLineString: GeoJsonGeometry = new GeoJSON().writeGeometryObject(olLineString, {
          featureProjection: this._viewService.getProjection().getCode(),
          dataProjection: 'EPSG:4326'
        });

        //when we use this "olLineString.getLength()" the results are not accurate
        const totalLength = turfLength({
          "type": "Feature",
          "geometry": geojsonLineString,
          "properties": {}
        }, { units: "meters" });

        const olFeatures: Feature[] = [];

        routesUntilPoint.forEach((routeUntilPoint: LineString, index: number) => {

          //when we use this "routeUntilPoint.getLength()" the results are not accurate
          const routeUntilPointLength = turfLength({
            "type": "Feature",
            "geometry": new GeoJSON().writeGeometryObject(routeUntilPoint, {
              featureProjection: this._viewService.getProjection().getCode(),
              dataProjection: 'EPSG:4326'
            }),
            "properties": {}
          }, { units: "meters" });

          //when there is more then one heading line, start the next line a bit further from the clicked point?
          //const extraSpacing = index > 0 ? 20 : 0;
          const extraSpacing = 0;
          const lengthToFirstPoint = totalLength > (routeUntilPointLength + extraSpacing) ? (routeUntilPointLength + extraSpacing) : (totalLength - 1);
          const lengthToLastPoint = totalLength > (lengthToFirstPoint + lengthOfHeadingLine) ? (lengthToFirstPoint + lengthOfHeadingLine) : totalLength;

          const slicedLine: GeoJsonFeature<GeoJsonLineString> = lineSliceAlong(
            <GeoJsonFeature<GeoJsonLineString>>{
              "type": "Feature",
              "geometry": geojsonLineString,
              "properties": {}
            },
            lengthToFirstPoint,
            lengthToLastPoint,
            { units: 'meters' });

          if (slicedLine) {
            const olFeature = <Feature>new GeoJSON().readFeature(slicedLine, {
              dataProjection: 'EPSG:4326',
              featureProjection: this._viewService.getProjection().getCode()
            });
            olFeature.set('isRouteHeading', true);
            olFeature.set('routeHeadingDistance', lengthToFirstPoint);
            olFeature.set('label', TraceRouteData.formatRouteLength(lengthToFirstPoint, false, '0 m'));

            olFeatures.push(olFeature)
          }
        });

        if (olFeatures.length > 0) {

          this._traceDrawLayerHeading.getSource().clear();

          /*
          olFeatures.forEach(feature => {
            this._traceDrawLayerHeading.getSource().addFeature(feature);
          });
          */
          this._traceDrawLayerHeading.getSource().addFeature(olFeatures[0]);
          //no intersect test because we didn't use the extraSpacing option above
          if (olFeatures.length > 1 /*&& !olExtent.intersects(olFeatures[0].getGeometry().getExtent(), olFeatures[olFeatures.length - 1].getGeometry().getExtent())*/) {
            this._traceDrawLayerHeading.getSource().addFeature(olFeatures[olFeatures.length - 1]);
          }

          window.setTimeout(() => {
            //when we use clear() together with this timeout, we possibly delete new features 
            this._traceDrawLayerHeading.getSource().removeFeature(olFeatures[0]);
            if (olFeatures.length > 1) {
              this._traceDrawLayerHeading.getSource().removeFeature(olFeatures[olFeatures.length - 1]);
            }
          }, 5000);
        }
      }
    }
  }

  private _informElevationProfileForDataUpdate() {
    if (this.hasRouteToCreateProfile()) {
      this._broadcastTraceRouteDataUpdate(TraceRouteDataUpdateType.NEW_DATA);
    }
  }

  updateElevationUpDownDist(upwardDistance: number, downwardDistance: number) {
    if (!this._isActive()) {
      //maybe the elevation profile was still processing when the user shut down trace
      return;
    }
    this._elevationUpwardDist = typeof upwardDistance === 'number' && upwardDistance >= -1 ? upwardDistance : -1;
    this._elevationDownwardDist = typeof downwardDistance === 'number' && downwardDistance <= 1 ? downwardDistance : 1;
    this._broadcastTraceRouteDataUpdate(TraceRouteDataUpdateType.UPDATED_ELEV_UPDOWN_DIST);

    if (this.hasPermalinkParam()) {
      this._updateMostRecentHistoryItem_upDownDist();
    }
  }

  getElevationUpwardDist(): number {
    return this._elevationUpwardDist;
  }

  getElevationDownwardDist(): number {
    return this._elevationDownwardDist;
  }
  // _confirmationDialog = null
  // startNewDrawing() {
  //   // this.openTraceTab_str('CREATE_ROUTE');
  //   // if (this._confirmationDialog !== null) {
  //   //   this._confirmationDialog.close();
  //   //   this._confirmationDialog = null;
  //   // }
  //   // this._confirmationDialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
  //   //   panelClass: 'small-dialog',
  //   //   data: {
  //   //     title: this._translateService.instant('trace.create-route.reset'),
  //   //     text: this._translateService.instant('trace.create-route.confirmdelete-all'),
  //   //     type: 'resetData'
  //   //   },
  //   // });

  //   // this._confirmationDialog.afterClosed().pipe(take(1)).subscribe(result => {
  //     // if (result && result.data && result.data.type === 'resetData') {
  //       if (this._browserSniffer.isPhone()) {
  //         this.closeViewDialog();
  //       }
  //       //setPermalinkParam(false) is normally already done by previous call to resetTraceRouteData(true)
  //       //this.traceService.setPermalinkParam(false);
  //       this.fullCleanupAndSaveForHistoryItems();
  //       //if you go to the editing tab after this action in view tab, you want it to start with a completely new drawing (and a new trace id in the url)
  //       this.addNewHistoryItemForEditing();
  //       this.setPermalinkParam(true);
  //       this.resetTraceRouteData(true);

  //     //  this.openTraceTab(TraceDrawerType.CREATE_ROUTE) //: this.onOpenCreateMobile();


  //     // }
  //     // this._confirmationDialog = null;
  //   // });

  // }

  /* ************************************************************************************************************************************************************************************
  * extra small screen utlities, phone
  *************************************************************************************************************************************************************************************/

  private _importDialog: MatDialogRef<TraceXsmallDialogImportComponent, any> = null;
  private _suggestionsDialog: MatDialogRef<TraceXsmallDialogSuggestionsComponent, any> = null;
  private _viewDialog: BottomPaneRef<TraceXsmallDialogViewComponent> = null;

  // private _createDialog: MatDialogRef<TraceXsmallDialogCreateComponent, any>; 
  // alertDialog(index:number,tab:MatTab,tabHeader:any,)
  // {
  //   const dialogRef = this._dialog.open(DialogOverviewExampleDialog, {
  //     data: {},
  //   });
  //   const res = dialogRef.afterClosed().subscribe((result) => {
  //     if (result) {
  //       tabHeader.focusIndex = index;

  //       if (!tab.disabled) {
  //         this.tabgroup.selectedIndex = index;
  //       }
  //     }
  //   });
  // }

  getDrawDisclaimerDialog(): MatDialogRef<DrawDisclaimerDialogComponent> {
    return this._dialog.open(DrawDisclaimerDialogComponent, {
      panelClass: 'small-dialog',
      disableClose: true,
      data: {
        title: this._translateService.instant('trace.create-route.draw-disclaimer-title'),
        text: this._translateService.instant('trace.create-route.draw-disclaimer-text') + '<br>',
      }
    });
  }

  openCreateMobile(keepTraceData: boolean) {
    setTimeout(() => {
      if (!keepTraceData) {
        //on mobile it can be confusing if no route is on the map and this message pops up because of the undo/redo list, which is still filled
        //the undo list will be removed without test now
        if (this.hasTraceRouteOrWaypointOnMap() || this.hasTraceRouteWithGeneralInfo() /*|| this.canUndoOrRedoEditing*/) {
          //new drawing alert
          const dialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
            panelClass: 'small-dialog',
            data: {
              title: this._translateService.instant('trace.route_new_drawing'),
              text: this._translateService.instant('trace.create-route.confirmstart-new-route'),
              type: 'resetData'
            },
          })
          dialog.afterClosed().pipe(take(1)).subscribe(result => {
            if (result && result.data && result.data.type === 'resetData') {
              this.setPermalinkParam(false);
              this.fullCleanupAndSaveForHistoryItems();
              this.addNewHistoryItemForEditing();
              this.setPermalinkParam(true);

              this.resetTraceRouteData(true);

              setTimeout(() => {
                //recursive because we need to check for drawdisclaimer
                this.openCreateMobile(true);
              }, 300);
            }

          });
        } else {
          //there was no route/poi/metadata , but maybe the undo list is not empty, by changing the permalink, the undo list is reset
          this.setPermalinkParam(false);
          this.fullCleanupAndSaveForHistoryItems();
          this.addNewHistoryItemForEditing();
          this.setPermalinkParam(true);

          this.resetTraceRouteData(true);

          //recursive because we need to check for drawdisclaimer
          this.openCreateMobile(true);
        }
      } else {
        if (sessionStorage.getItem('drawdisclaimer') !== 'shown') {
          this.getDrawDisclaimerDialog().afterClosed().subscribe(() => {
            sessionStorage.setItem('drawdisclaimer', 'shown');
            //case user was dragging window to phone size when disclaimer was open
            (this._browserSniffer.isPhone()) ? this.onOpenCreateMobile() : this.openTraceTab(TraceDrawerType.CREATE_ROUTE);
          });
        } else {
          this.onOpenCreateMobile();
        }
      }
    }, 0);
  }

  onOpenCreateMobile() {
    const wasActive = this._isActive();
    if (!wasActive) {
      this._startUp();
    }

    this.hideSuggestionsPreview();

    this.tabindex.set(TraceDrawerType.CREATE_ROUTE);

    //if it was already open and we start a new drawing
    //then we need to reopen it, otherwise it's under the bottomsheet
    this._toggleEventService.toggleEvent({
      forwardedFor: 'drawing-tools',
      eventName: ToggleEventNameType.CLOSE_COMMAND
    });

    this.openCreateBottomSheet();

    setTimeout(() => {
      this._toggleEventService.toggleEvent({
        forwardedFor: 'drawing-tools',
        eventName: ToggleEventNameType.OPEN_COMMAND
      });
    }, 300);
  }

  closeCreateMobile() {
    this.setInEditingMode(false);
    //case of resizing
    this.openTraceTab(TraceDrawerType.VIEW_ROUTE);
    this.closeCreateBottomSheet();
    this._toggleEventService.toggleEvent({
      forwardedFor: 'drawing-tools',
      eventName: ToggleEventNameType.CLOSE_COMMAND
    });

    if (this.canShutDownSafely()) {
      this.shutDown();
      console.log('traceservice shutdown after create dialog closure, no trace layers with route data');
      return;
    }
  }

  openCreateBottomSheet() {
    let sheetRef = this._bottomSheet.open(TraceCreateComponent, {
      data: {},
      hasBackdrop: false,
      panelClass: 'trace-create-bottomsheet',
      // disableClose: true
    });
    // hitting the back button does close the bottomsheet but not the drawingtools
    // but in responsive resize, the drawingtools have to be open
    sheetRef.afterDismissed().subscribe((data) => {
      if (this.isInEditingMode() && this._browserSniffer.isPhone()) {
        this.closeCreateMobile();
      }
    });
  }

  closeCreateBottomSheet() {//event
    this._bottomSheet.dismiss();
    // event.preventDefault();
  }

  openImportDialog(): void {
    //traceservice is usually not yet started up when this method is called
    //we need the history list
    if (!this._isActive()) {
      this._startUp();
    }

    this.hideSuggestionsPreview();

    this.tabindex.set(TraceDrawerType.IMPORT_ROUTE);

    this._importDialog = this._dialog.open(TraceXsmallDialogImportComponent, {
      closeOnNavigation: true,
      panelClass: 'trace-import-dialog',
      width: '100%',
      maxWidth: '100vw !important',
      height: '100%',
      maxHeight: '100vh !important',

    });
    this._importDialog.afterClosed().subscribe(result => {
      this._importDialog = null;
      //this.toggleVisibility(null);
      //this dialog is also closed when resizing the screen
      //timeout because a new route should be loaded into the model before isInViewMode becomes true
      window.setTimeout(() => {
        //in editing mode it's possible that the user didn't draw anything yet
        if (this.canShutDownSafely()) {
          this.shutDown();
          console.log('traceservice shutdown after import dialog closure, no trace layers with route data');
        }
      }, 1500);
    });

    this._backEventService.registerOpenedWindow({ windowName: 'TraceXsmallDialogImportComponent', isAutoCloseableWindow: true });
  }

  closeImportDialog(): void {
    this._importDialog?.close();
  }

  openSuggestionsDialog(suggestionsTabTitleI8nKey?: string): void {
    //traceservice is usually not yet started up when this method is called
    //we need the history list
    if (!this._isActive()) {
      this._startUp();
    }

    this.tabindex.set(TraceDrawerType.SUGGESTIONS_ROUTE);

    this._suggestionsDialog = this._dialog.open(TraceXsmallDialogSuggestionsComponent, {
      closeOnNavigation: true,
      panelClass: 'trace-import-dialog',
      width: '100%',
      maxWidth: '100vw !important',
      height: '100%',
      maxHeight: '100vh !important',
      data: {
        suggestionsTabTitleI8nKey: suggestionsTabTitleI8nKey
      }
    });
    this._suggestionsDialog.afterClosed().subscribe(result => {
      this._suggestionsDialog = null;
      //this.toggleVisibility(null);
      //this dialog is also closed when resizing the screen
      //timeout because a new route should be loaded into the model before isInViewMode becomes true
      window.setTimeout(() => {
        //in editing mode it's possible that the user didn't draw anything yet
        if (this.canShutDownSafely()) {
          this.shutDown();
          console.log('traceservice shutdown after suggestions dialog closure, no trace layers with route data');
        }
      }, 1500);
    });

    this._backEventService.registerOpenedWindow({ windowName: 'TraceXsmallDialogSuggestionsComponent', isAutoCloseableWindow: true });
  }

  closeSuggestionsDialog(): void {
    this._suggestionsDialog?.close();
  }

  openViewDialog(data?: object) {
    //traceservice is already started up when this method is called
    this.removeElevationProfileTooltip();

    if (this._bottomPaneService.list().length > 0) {
      this._bottomPaneService.closeAll();
    }

    this.hideSuggestionsPreview();

    this.tabindex.set(TraceDrawerType.VIEW_ROUTE);

    //backevent is handled in by dialog
    this._viewDialog = this._bottomPaneService.open({
      id: 'traceXsmallDialogViewComponent',
      component: TraceXsmallDialogViewComponent,
      data: data,
      zIndex: 9,
      hasBackdrop: true,
      backdropClass: ['tt-backdrop'],
      panelClass: ['about-bottom-pane'],
      width: 'unset',
      maxHeight: '86vh',
      height: '86vh',
      minHeight: '86vh',
      closeOnBackdropClick: true,
    });

    this._viewDialog.afterClosed$().subscribe((result) => {
      this._viewDialog = null;
      //console.log('_viewDialog.afterClosed()', result)
      //this dialog is also closed when resizing the screen
      if (this.canShutDownSafely()) {
        //user clicked delete
        this.shutDown();
        console.log('traceservice shutdown after view dialog closure, no trace layers with route data');
      }
    })
  }

  closeViewDialog(): void {
    this._viewDialog?.closeClick();
  }

  /* ************************************************************************************************************************************************************************************
  * route thumbnail
  *************************************************************************************************************************************************************************************/

  private _thumbnailElement: HTMLElement = null;
  private _thumbnailMap: Map = null;
  private _thumbnailMapLayer_poi: VectorLayer<any> = null;
  private _thumbnailMapLayer_route: VectorLayer<any> = null;
  private _thumbnailSize: number = 70;

  private _initThumbnailMap() {

    if (!this._thumbnailElement) {
      this._thumbnailElement = document.createElement("div");
      this._thumbnailElement.classList.add('trace-thumbnail-map');
      this._thumbnailElement.setAttribute('id', 'trace-thumbnail-map');
      this._thumbnailElement.setAttribute('style', 'width:' + this._thumbnailSize + 'px;height:' + this._thumbnailSize + 'px;padding:0px;margin:0px;background:transparent;position:fixed;right:-400px;bottom:-400px;')
      document.body.appendChild(this._thumbnailElement);
    }

    if (!this._thumbnailMap) {

      this._thumbnailMapLayer_route = new VectorLayer({
        source: new VectorSource(),
        style: new Style({
          stroke: new Stroke({
            color: 'rgba(225, 62, 1, 1.0)',
            width: 2,
          })
        }),
        zIndex: 0
      });
      this._thumbnailMapLayer_route.set("imageTime", 0);
      this._thumbnailMapLayer_route.set("dataRef", "");

      this._thumbnailMapLayer_poi = new VectorLayer({
        source: new VectorSource(),
        style: new Style({
          image: new Circle({
            radius: 1,
            fill: new Fill({
              color: 'rgba(6, 138, 155, 1.0)'
            })
          })
        }),
        zIndex: 1
      });

      this._thumbnailMap = new Map({
        target: this._thumbnailElement,
        view: new View({ projection: this._viewService.getProjection(), center: this._viewService.getCenter() }),
        layers: [this._thumbnailMapLayer_poi, this._thumbnailMapLayer_route]
      });

      this._thumbnailMap.on('postrender', (event) => {
        if (this._thumbnailMapLayer_route.getSource().getFeatures().length === 0 && this._thumbnailMapLayer_poi.getSource().getFeatures().length === 0) {
          return;
        }

        const mostRecentHistoryItem = this._traceRouteHistory.getMostRecentHistoryItem();
        const dataRef = this._thumbnailMapLayer_route.get("dataRef");
        const imageTime = this._thumbnailMapLayer_route.get("imageTime");
        if (!mostRecentHistoryItem || mostRecentHistoryItem.dataRef !== dataRef || imageTime === 0) {
          return;
        }

        let imageData = null;
        try {
          const mapCanvas = (this._thumbnailMap.getTarget() as HTMLElement).querySelectorAll('.ol-layers canvas')[0] as HTMLCanvasElement;
          imageData = mapCanvas ? mapCanvas.toDataURL('image/png') : null;
        } catch (error) {
          console.log('unable to create thumbnail for history list : ' + error);
        }

        //is dataref & imageTime still the same?
        if (
          mostRecentHistoryItem.dataRef === this._thumbnailMapLayer_route.get("dataRef") &&
          dataRef === this._thumbnailMapLayer_route.get("dataRef") &&
          imageTime === this._thumbnailMapLayer_route.get("imageTime")
        ) {
          if (
            imageData &&
            this._traceRouteHistory.updateMostRecentHistoryItem_thumb(imageData)
          ) {
            this._saveTraceRouteHistoryToLocalStorage();
          }

          this._thumbnailMapLayer_route.set("imageTime", 0);
          this._thumbnailMapLayer_route.set("dataRef", "");
          this._thumbnailMapLayer_route.getSource().clear();

          this._thumbnailMapLayer_poi.getSource().clear();
        }
      });
    }
  }
  private _disposeThumbnailMap() {
    if (this._thumbnailMap) {
      this._thumbnailMap.dispose();
      this._thumbnailMap = null;
    }
    if (this._thumbnailMapLayer_route) {
      this._thumbnailMapLayer_route.dispose();
      this._thumbnailMapLayer_route = null;
    }
    if (this._thumbnailMapLayer_poi) {
      this._thumbnailMapLayer_poi.dispose();
      this._thumbnailMapLayer_poi = null;
    }
    if (this._thumbnailElement) {
      this._thumbnailElement.innerHTML = '';
    }
  }
  getEmptyThumbnail(): string {
    const canvasElement: HTMLCanvasElement = document.createElement("canvas");
    canvasElement.width = this._thumbnailSize;
    canvasElement.height = this._thumbnailSize;
    canvasElement.style.position = "fixed";
    canvasElement.style.bottom = "-400px";
    canvasElement.style.right = "-400px";
    const context = canvasElement.getContext('2d');
    context.fillStyle = "rgba(0, 0, 0, 0.02)";
    context.fillRect(0, 0, canvasElement.width, canvasElement.height)
    let imageData = canvasElement.toDataURL('image/png');
    return imageData;
  }
  private _setEmptyThumbnail(dataRef: string): boolean {
    const imageData = this.getEmptyThumbnail();
    return imageData
      && this._traceRouteHistory.getMostRecentHistoryItem().dataRef === dataRef
      && this._traceRouteHistory.updateMostRecentHistoryItem_thumb(imageData);
  }
  private _setRouteThumbnail(dataRef: string, retrial?: number) {
    if (this._thumbnailMap && this._thumbnailMapLayer_route && this._thumbnailMapLayer_poi && this._traceDrawLayerLineString && this._traceDrawLayerPoint) {

      //normally this already done during a previous postrender event
      this._thumbnailMapLayer_route.set("imageTime", 0);
      this._thumbnailMapLayer_route.set("dataRef", "");

      this._thumbnailMapLayer_poi.getSource().clear();
      this._thumbnailMapLayer_route.getSource().clear();

      const hasRouteForThumbnail = this.hasRouteToCreateProfile();
      //if there is only a starting point for the route, then we will take the point with id 'startPoint' from layer this._traceDrawLayerPoint (layer this._traceDrawLayerLineString will still be empty)
      const hasRouteWithOnlyStartPoint = this.hasRouteOnMap() && !this.hasRouteToCreateProfile();
      const hasWayPointsForThumbnail = this.hasWayPointsOnMap();

      if (hasRouteForThumbnail || hasRouteWithOnlyStartPoint || hasWayPointsForThumbnail) {
        let extentOfFeatures: olExtent.Extent;
        if (hasRouteForThumbnail && hasWayPointsForThumbnail) {
          extentOfFeatures = olExtent.extend(this._traceDrawLayerLineString.getSource().getExtent(), this._traceDrawLayerPoint.getSource().getExtent());
        } else if (hasRouteForThumbnail) {
          extentOfFeatures = this._traceDrawLayerLineString.getSource().getExtent();
        } else {
          extentOfFeatures = this._traceDrawLayerPoint.getSource().getExtent();
        }

        this._thumbnailMap.getView().fit(olExtent.buffer(extentOfFeatures, 0), {
          size: this._thumbnailMap.getSize(),
          padding: [3, 3, 3, 3],
          duration: 0,
          maxZoom: this._maxAutoZoomForPrj,
        });

        window.setTimeout(() => {
          if (hasWayPointsForThumbnail || hasRouteWithOnlyStartPoint) {
            const featuresForThumbnail = []
            const tempFeatures = this._traceDrawLayerPoint.getSource().getFeatures();
            for (let i = 0; i < tempFeatures.length; i++) {
              //startPoint & endPoint of the route are also in this layer
              const featureId = '' + tempFeatures[i].getId();
              if ((hasWayPointsForThumbnail && featureId.indexOf('wayPoint_') === 0) || (hasRouteWithOnlyStartPoint && featureId === 'startPoint')) {
                featuresForThumbnail.push(tempFeatures[i]);
              }
            }
            this._thumbnailMapLayer_poi.getSource().addFeatures(featuresForThumbnail);
          }

          this._thumbnailMapLayer_route.set("imageTime", new Date().getTime());
          this._thumbnailMapLayer_route.set("dataRef", dataRef);

          if (hasRouteForThumbnail) {
            this._thumbnailMapLayer_route.getSource().addFeatures(this._traceDrawLayerLineString.getSource().getFeatures());
          }
        }, 100);
      }
    } else {
      //map container element is not yet available
      retrial = typeof retrial === "number" ? retrial++ : 1;
      if (retrial <= 10) {
        window.setTimeout(() => {
          this._setRouteThumbnail(dataRef, retrial);
        }, 300);
      }
    }
  }
}